import * as resourceKeys from 'src/localization/resouceKeys';
import { useTranslation } from 'react-i18next';
import { VehicleType } from 'src/utils/enums/financialTypes.enum';
import {
    AccountType,
    BankAccountType,
    PaymentProcessStatus,
    PaymentReason,
    PaymentTypeFilter,
    PurchaseType,
    SecurityDepositWaiverType,
} from 'src/utils/enums/payment.enum';
import {
    CreditApplicationStatus,
    CreditSearchStatusOptionsState,
} from 'src/services/models/credit.service';
import {
    FundingSearchStatusOptions,
    FundingSearchStatusOptionsState,
} from 'src/services/models/funding.service';
type ResourceKeys = keyof typeof resourceKeys;
type LocalizedKeyObject = {
    [key in ResourceKeys]: string;
};

export const useLocalization = () => {
    const { t } = useTranslation();
    const localizedKeys: LocalizedKeyObject = {} as LocalizedKeyObject;
    for (const [key, value] of Object.entries(resourceKeys)) {
        localizedKeys[key as ResourceKeys] = t(value as ResourceKeys);
    }

    const contactInfo = {
        HEADER_LABEL: localizedKeys.CONTACT_INFO_HEADER_LABEL,
        HEADER_LINK_LABEL: localizedKeys.CONTACT_INFO_HEADER_LINK_LABEL,
        ADMIN_LABEL: localizedKeys.CONTACT_INFO_ADMIN_LABEL,
        PHONE_LABEL: localizedKeys.CONTACT_INFO_PHONE_LABEL,
        EMAIL_LABEL: localizedKeys.CONTACT_INFO_EMAIL_LABEL,
        FAX_LABEL: localizedKeys.CONTACT_INFO_FAX_LABEL,
        DEALER_LINE_LABEL: localizedKeys.CONTACT_INFO_DEALER_LINE_LABEL,
        CUSTOMER_CARE_LABEL: localizedKeys.CONTACT_INFO_CUSTOMER_CARE_LABEL,
    };

    const departmentInfo = {
        DEPT_LABEL: localizedKeys.DEPARTMENT_LABEL_1,
        OPTION_LABEL: localizedKeys.DEPARTMENT_LABEL_2,
        CREDIT: localizedKeys.DEPARTMENT_CREDIT,
        FUNDING: localizedKeys.DEPARTMENT_FUNDING,
        LEASE: localizedKeys.DEPARTMENT_LEASE,
        CUSTOMER_SERVICE: localizedKeys.DEPARTMENT_CUSTOMER_SERVICE,
        DEALER_SUPPORT: localizedKeys.DEPARTMENT_DEALER_SUPPORT,
        DE_TECH_SUPPORT: localizedKeys.DEPARTMENT_DE_TECH_SUPPORT,
    };

    const labelText = {
        LABEL_TEXT_EMAIL: localizedKeys.LABEL_TEXT_EMAIL,
        LABEL_TEXT_PASSWORD: localizedKeys.LABEL_TEXT_PASSWORD,
        LABEL_TEXT_WELCOME: localizedKeys.LABEL_TEXT_WELCOME,
        LABEL_TEXT_LOGIN: localizedKeys.LABEL_TEXT_LOGIN,
        LABEL_TEXT_DOWNLOAD: localizedKeys.LABEL_TEXT_DOWNLOAD,
        LABEL_TEXT_PRINT: localizedKeys.LABEL_TEXT_PRINT,
        LABEL_TEXT_OR: localizedKeys.LABEL_TEXT_OR,
        LABEL_TEXT_FORGOT_PASSWORD: localizedKeys.LABEL_TEXT_FORGOT_PASSWORD,
        LABEL_TEXT_QR_STEP1: localizedKeys.LABEL_TEXT_QR_STEP1,
        LABEL_TEXT_QR_STEP2: localizedKeys.LABEL_TEXT_QR_STEP2,
        LABEL_TEXT_QR_STEP3: localizedKeys.LABEL_TEXT_QR_STEP3,
        LABEL_TEXT_QR_SUBMIT: localizedKeys.LABEL_TEXT_QR_SUBMIT,
        LABEL_TEXT_VERIFICATION_SUBMIT:
            localizedKeys.LABEL_TEXT_VERIFICATION_SUBMIT,
        LABEL_TEXT_VERIFICATION_SUBTITLE:
            localizedKeys.LABEL_TEXT_VERIFICATION_SUBTITLE,
        LABEL_TEXT_VERIFICATION_REMEMBER_ME:
            localizedKeys.LABEL_TEXT_VERIFICATION_REMEMBER_ME,
        LABEL_TEXT_ACCOUNT_LOCKED: localizedKeys.LABEL_TEXT_ACCOUNT_LOCKED,
        LABEL_TEXT_ACCOUNT_CONTACT: localizedKeys.LABEL_TEXT_ACCOUNT_CONTACT,
        LABEL_TEXT_ACCOUNT_BRAND: localizedKeys.LABEL_TEXT_ACCOUNT_BRAND,
        LABEL_TEXT_ACCOUNT_PHONE: localizedKeys.LABEL_TEXT_ACCOUNT_PHONE,
        LABEL_TEXT_ACCOUNT_HOURS: localizedKeys.LABEL_TEXT_ACCOUNT_HOURS,
        LABEL_TEXT_ACCOUNT_WEEKEND_HOURS:
            localizedKeys.LABEL_TEXT_ACCOUNT_HOURS_WEEKEND,
        LABEL_TEXT_ACCOUNT_LINK: localizedKeys.LABEL_TEXT_ACCOUNT_LINK,
        LABEL_TEXT_ERROR_ATTEMPT_TITLE:
            localizedKeys.LABEL_TEXT_ERROR_ATTEMPT_TITLE,
        LABEL_TEXT_ERROR_ATTEMPT_SUBTITLE:
            localizedKeys.LABEL_TEXT_ERROR_ATTEMPT_SUBTITLE,
        LABEL_TEXT_FUNDING_SEARCH_ERROR_NO_RESULTS:
            localizedKeys.LABEL_TEXT_FUNDING_SEARCH_ERROR_NO_RESULTS,
        LABEL_TEXT_FUNDING_SEARCH_ERROR_SUGGESTION:
            localizedKeys.LABEL_TEXT_FUNDING_SEARCH_ERROR_SUGGESTION,
        LABEL_TEXT_MENU_HOME: localizedKeys.LABEL_TEXT_MENU_HOME,
        LABEL_TEXT_MENU_CREDIT: localizedKeys.LABEL_TEXT_MENU_CREDIT,
        LABEL_TEXT_MENU_CREDIT_APP: localizedKeys.LABEL_TEXT_MENU_CREDIT_APP,
        LABEL_TEXT_MENU_CREDIT_STATUS:
            localizedKeys.LABEL_TEXT_MENU_CREDIT_STATUS,
        LABEL_TEXT_MENU_FUNDING: localizedKeys.LABEL_TEXT_MENU_FUNDING,
        LABEL_TEXT_MENU_FUNDING_APP: localizedKeys.LABEL_TEXT_MENU_FUNDING_APP,
        LABEL_TEXT_MENU_FUNDING_STATUS:
            localizedKeys.LABEL_TEXT_MENU_FUNDING_STATUS,
        LABEL_TEXT_MENU_FUNDING_DOCUMENTS:
            localizedKeys.LABEL_TEXT_MENU_FUNDING_DOCUMENTS,
        LABEL_TEXT_MENU_PAYOFF: localizedKeys.LABEL_TEXT_MENU_PAYOFF,
        LABEL_TEXT_MENU_PAYOFF_APP: localizedKeys.LABEL_TEXT_MENU_PAYOFF_APP,
        LABEL_TEXT_MENU_PAYOFF_STATUS:
            localizedKeys.LABEL_TEXT_MENU_PAYOFF_STATUS,
        LABEL_TEXT_MENU_PAYOFF_DOCUMENTS:
            localizedKeys.LABEL_TEXT_MENU_PAYOFF_DOCUMENTS,
        LABEL_TEXT_MENU_RESOURCES: localizedKeys.LABEL_TEXT_MENU_RESOURCES,
        LABEL_TEXT_MENU_RESOURCES_APP:
            localizedKeys.LABEL_TEXT_MENU_RESOURCES_APP,
        LABEL_TEXT_MENU_RESOURCES_STATUS:
            localizedKeys.LABEL_TEXT_MENU_RESOURCES_STATUS,
        LABEL_TEXT_MENU_RESOURCES_DOCUMENTS:
            localizedKeys.LABEL_TEXT_MENU_RESOURCES_DOCUMENTS,
        LABEL_TEXT_MENU_PAYMENTS: localizedKeys.LABEL_TEXT_MENU_PAYMENTS,
        LABEL_TEXT_MENU_SERVICE_LOANER:
            localizedKeys.LABEL_TEXT_MENU_SERVICE_LOANER,
        LABEL_TEXT_MENU_DEALER_RENTAL:
            localizedKeys.LABEL_TEXT_MENU_DEALER_RENTAL,
        LABEL_TEXT_MENU_MANAGE_USER: localizedKeys.LABEL_TEXT_MENU_MANAGE_USER,
        LABEL_TEXT_MENU_LANGUAGE: localizedKeys.LABEL_TEXT_MENU_LANGUAGE,
        LABEL_TEXT_MENU_PROFILE_LOGOUT:
            localizedKeys.LABEL_TEXT_MENU_PROFILE_LOGOUT,
        LABEL_TEXT_MENU_PROFILE_SETTINGS:
            localizedKeys.LABEL_TEXT_MENU_PROFILE_SETTINGS,
        LABEL_TEXT_MENU_PROFILE: localizedKeys.LABEL_TEXT_MENU_PROFILE,
        LABEL_TEXT_MENU_PROFILE_LANG_ENGLISH:
            localizedKeys.LABEL_TEXT_MENU_PROFILE_LANG_ENGLISH,
        LABEL_TEXT_MENU_PROFILE_LANG_FRENCH:
            localizedKeys.LABEL_TEXT_MENU_PROFILE_LANG_FRENCH,
        LABEL_TEXT_BRAND_AUDI_FINANCIAL_SERVICES:
            localizedKeys.LABEL_TEXT_BRAND_AUDI_FINANCIAL_SERVICES,
        LABEL_TEXT_BRAND_VOLKSWAGEN_FINANCIAL_SERVICES:
            localizedKeys.LABEL_TEXT_BRAND_VOLKSWAGEN_FINANCIAL_SERVICES,
        LABEL_TEXT_BRAND_DUCATI_FINANCIAL_SERVICES:
            localizedKeys.LABEL_TEXT_BRAND_DUCATI_FINANCIAL_SERVICES,
        LABEL_TEXT_SHOWING: localizedKeys.LABEL_TEXT_SHOWING,
        LABEL_TEXT_RESULT_PER_PAGE: localizedKeys.LABEL_TEXT_RESULT_PER_PAGE,
        LABEL_TEXT_RESULT_OF_TABLE: localizedKeys.LABEL_TEXT_RESULT_OF_TABLE,
        LABEL_TEXT_MAINTENANCE_MESSAGE: localizedKeys.LABEL_TEXT_MAINTENANCE,
        LABEL_TEXT_DASH_DISTRICT: localizedKeys.LABEL_TEXT_DASH_DISTRICT,
        LABEL_TEXT_DASH_SELECT: localizedKeys.LABEL_TEXT_DASH_SELECT,
        LABEL_TEXT_DASH_MAIN_ACC: localizedKeys.LABEL_TEXT_DASH_MAIN_ACC,
        LABEL_TEXT_DASH_SEC_ACC: localizedKeys.LABEL_TEXT_DASH_SEC_ACC,
        LABEL_TEXT_DASH_DEALERSHIP: localizedKeys.LABEL_TEXT_DASH_DEALERSHIP,
        LABEL_TEXT_DASH_NORESULTS: localizedKeys.LABEL_TEXT_DASH_NORESULTS,
        LABEL_TEXT_IN_VALID_DEALER_PORTAL:
            localizedKeys.LABEL_TEXT_IN_VALID_DEALER_PORTAL,
        LABEL_TEXT_IN_VALID_DEALER_PORTAL_US:
            localizedKeys.LABEL_TEXT_IN_VALID_DEALER_PORTAL_US,
        LABEL_TEXT_MENU_PAYMENTS_MAKE:
            localizedKeys.LABEL_TEXT_MENU_PAYMENTS_MAKE,
    };

    const labelTitle = {
        LABEL_TITLE_QR: localizedKeys.LABEL_TITLE_QR,
        LABEL_TITLE_VERIFICATION: localizedKeys.LABEL_TITLE_VERIFICATION,
        LABEL_TITLE_LOCKED_ACCOUNT: localizedKeys.LABEL_TITLE_LOCKED_ACCOUNT,
    };

    const labelField = {
        LABEL_FIELD_ERROR_INVALID_EMAIL:
            localizedKeys.LABEL_FIELD_ERROR_INVALID_EMAIL,
        LABEL_FIELD_ERROR_REQUIRED: localizedKeys.LABEL_FIELD_ERROR_REQUIRED,
        LABEL_FIELD_ERROR_REQUEST_SERVICE:
            localizedKeys.LABEL_FIELD_ERROR_REQUEST_SERVICE,
        LABEL_FIELD_ERROR_INVALID_OR_INCORRECT_EMAIL:
            localizedKeys.LABEL_FIELD_ERROR_INVALID_OR_INCORRECT_EMAIL,
        LABEL_FIELD_ERROR_INVALID_OR_INCORRECT_PASSWORD:
            localizedKeys.LABEL_FIELD_ERROR_INVALID_OR_INCORRECT_PASSWORD,
        LABEL_FIELD_PRYVACY_NOTICE: localizedKeys.LABEL_FIELD_PRYVACY_NOTICE,
        LABEL_FIELD_CONTACT_US: localizedKeys.LABEL_FIELD_CONTACT_US,
        LABEL_FIELD_USAGE_AGREEMENT: localizedKeys.LABEL_FIELD_USAGE_AGREEMENT,
        LABEL_FIELD_PRIVACY_POLICY: localizedKeys.LABEL_FIELD_PRIVACY_POLICY,
        LABEL_FIELD_ERROR_INVALID_OR_EXPIRED_CODE:
            localizedKeys.LABEL_FIELD_ERROR_INVALID_OR_EXPIRED_CODE,
        LABEL_FIELD_ERROR_INVALID_PHONE:
            localizedKeys.LABEL_FIELD_ERROR_INVALID_PHONE,
    };

    const statusSummary = {
        LABEL_STATUS_SUMMARY: localizedKeys.LABEL_STATUS_SUMMARY,
        LABEL_STATUS_SUMMARY_INFO: localizedKeys.LABEL_STATUS_SUMMARY_INFO,
        LABEL_STATUS_SUMMARY_CREDIT_BT:
            localizedKeys.LABEL_STATUS_SUMMARY_CREDIT_BT,
        LABEL_STATUS_SUMMARY_FUNDING_BT:
            localizedKeys.LABEL_STATUS_SUMMARY_FUNDING_BT,
        LABEL_STATUS_SUMMARY_FUNDING_PRODUCT:
            localizedKeys.LABEL_STATUS_SUMMARY_FUNDING_PRODUCT,
        LABEL_STATUS_SUMMARY_CREDIT_PRODUCT:
            localizedKeys.LABEL_STATUS_SUMMARY_CREDIT_PRODUCT,
        LABEL_STATUS_SUMMARY_ITEM_APPROVED:
            localizedKeys.LABEL_STATUS_SUMMARY_ITEM_APPROVED,
        LABEL_STATUS_SUMMARY_ITEM_UNABLETOFUND:
            localizedKeys.LABEL_STATUS_SUMMARY_ITEM_UNABLETOFUND,
        LABEL_STATUS_SUMMARY_ITEM_WITHDRAWN:
            localizedKeys.LABEL_STATUS_SUMMARY_ITEM_WITHDRAWN,
        LABEL_STATUS_SUMMARY_ITEM_RECEIVED:
            localizedKeys.LABEL_STATUS_SUMMARY_ITEM_RECEIVED,
        LABEL_STATUS_SUMMARY_ITEM_BOOKED:
            localizedKeys.LABEL_STATUS_SUMMARY_ITEM_BOOKED,
        LABEL_STATUS_SUMMARY_ITEM_PENDING:
            localizedKeys.LABEL_STATUS_SUMMARY_ITEM_PENDING,
        LABEL_STATUS_SUMMARY_ITEM_CONDITIONED:
            localizedKeys.LABEL_STATUS_SUMMARY_ITEM_CONDITIONED,
        LABEL_STATUS_SUMMARY_ITEM_DECLINED:
            localizedKeys.LABEL_STATUS_SUMMARY_ITEM_DECLINED,
    };
    const bulletins = {
        LABEL_BULLETINS_HEADER: localizedKeys.LABEL_BULLETINS_HEADER,
        LABEL_BULLETINS_NAME: localizedKeys.LABEL_BULLETINS_NAME,
        LABEL_BULLETINS_POSTED: localizedKeys.LABEL_BULLETINS_POSTED,
        LABEL_BULLETINS_EFFECTIVE_DATES:
            localizedKeys.LABEL_BULLETINS_EFFECTIVE_DATES,
        LABEL_BULLETINS_TITLE: localizedKeys.LABEL_BULLETINS_TITLE,
        LABEL_BULLETINS_ARCHIVE: localizedKeys.LABEL_BULLETINS_ARCHIVE,
        LABEL_BULLETINS_ALL: localizedKeys.LABEL_BULLETINS_ALL,
        LABEL_BULLETINS_FILTER_ALL: localizedKeys.LABEL_BULLETINS_FILTER_ALL,
        LABEL_BULLETINS_FILTER_ACTIVE:
            localizedKeys.LABEL_BULLETINS_FILTER_ACTIVE,
        LABEL_BULLETINS_FILTER_EXPIRED:
            localizedKeys.LABEL_BULLETINS_FILTER_EXPIRED,
        LABEL_BULLETINS_SEARCH: localizedKeys.LABEL_BULLETINS_SEARCH,
        LABEL_BULLETINS_SEARCH_DESCRIPTION:
            localizedKeys.LABEL_BULLETINS_SEARCH_DESCRIPTION,
        LABEL_BULLETINS_SEARCH_PLACEHOLDER:
            localizedKeys.LABEL_BULLETINS_SEARCH_PLACEHOLDER,
        LABEL_BULLETINS_SEARCH_BUTTON:
            localizedKeys.LABEL_BULLETINS_SEARCH_BUTTON,
        LABEL_BULLETINS_STATUS: localizedKeys.LABEL_BULLETINS_STATUS,
        LABEL_BULLETINS_TYPE: localizedKeys.LABEL_BULLETINS_TYPE,
        LABEL_BULLETINS_TYPE_PLACEHOLDER:
            localizedKeys.LABEL_BULLETINS_TYPE_PLACEHOLDER,
        LABEL_BULLETINS_DATE_RANGE: localizedKeys.LABEL_BULLETINS_DATE_RANGE,
        LABEL_BULLETINS_CLEAR_FILTERS:
            localizedKeys.LABEL_BULLETINS_CLEAR_FILTERS,
        LABEL_BULLETINS_CLEAR_SEARCH:
            localizedKeys.LABEL_BULLETINS_CLEAR_SEARCH,
        LABEL_BULLETINS_NO_RESULTS: localizedKeys.LABEL_BULLETINS_NO_RESULTS,
        LABEL_BULLETINS_TYPE_ALL: localizedKeys.LABEL_BULLETINS_TYPE_ALL,
        LABEL_BULLETINS_TYPE_LEASE: localizedKeys.LABEL_BULLETINS_TYPE_LEASE,
        LABEL_BULLETINS_TYPE_RETAIL: localizedKeys.LABEL_BULLETINS_TYPE_RETAIL,
        LABEL_BULLETINS_TYPE_BALLOON:
            localizedKeys.LABEL_BULLETINS_TYPE_BALLOON,
        LABEL_BULLETINS_TYPE_MISCELLANEOUS:
            localizedKeys.LABEL_BULLETINS_TYPE_MISCELLANEOUS,
        LABEL_BULLETINS_TYPE_INFORMATIONAL:
            localizedKeys.LABEL_BULLETINS_TYPE_INFORMATIONAL,
        LABEL_BULLETINS_TYPE_PROGRAMS:
            localizedKeys.LABEL_BULLETINS_TYPE_PROGRAMS,
        LABEL_BULLETINS_COLUMN_NUMBER:
            localizedKeys.LABEL_BULLETINS_COLUMN_NUMBER,
        LABEL_BULLETINS_COLUMN_TITLE:
            localizedKeys.LABEL_BULLETINS_COLUMN_TITLE,
        LABEL_BULLETINS_COLUMN_START_DATE:
            localizedKeys.LABEL_BULLETINS_COLUMN_START_DATE,
        LABEL_BULLETINS_COLUMN_END_DATE:
            localizedKeys.LABEL_BULLETINS_COLUMN_END_DATE,
        LABEL_BULLETINS_COLUMN_MODIFY_DATE:
            localizedKeys.LABEL_BULLETINS_COLUMN_MODIFY_DATE,
    };

    const handbook = {
        LABEL_HANDBOOK_LABEL_LINK: localizedKeys.LABEL_HANDBOOK_LABEL_LINK,
        LABEL_HANDBOOK_TITLE: localizedKeys.LABEL_HANDBOOK_TITLE,
        LABEL_HANDBOOK_TEXT_AUDI: localizedKeys.LABEL_HANDBOOK_TEXT_AUDI,
        LABEL_HANDBOOK_TEXT_VW: localizedKeys.LABEL_HANDBOOK_TEXT_VW,
        LABEL_HANDBOOK_TEXT_DUCATI: localizedKeys.LABEL_HANDBOOK_TEXT_DUCATI,
    };

    const modal = {
        LABEL_MODAL_INACTIVITY_TITLE:
            localizedKeys.LABEL_MODAL_INACTIVITY_TITLE,
        LABEL_MODAL_INACTIVITY_CONTENT:
            localizedKeys.LABEL_MODAL_INACTIVITY_CONTENT,
        LABEL_MODAL_INACTIVITY_BTN_LOGOUT:
            localizedKeys.LABEL_MODAL_INACTIVITY_BTN_LOGOUT,
        LABEL_MODAL_INACTIVITY_BTN_CONTINUE:
            localizedKeys.LABEL_MODAL_INACTIVITY_BTN_CONTINUE,
    };

    const data = {
        NO_DATA_AVAILABLE: localizedKeys.NO_DATA_AVAILABLE,
    };

    const fundingStatus = {
        BOOKED: statusSummary.LABEL_STATUS_SUMMARY_ITEM_BOOKED,
        RECEIVED: statusSummary.LABEL_STATUS_SUMMARY_ITEM_RECEIVED,
        UNABLETOFUND: statusSummary.LABEL_STATUS_SUMMARY_ITEM_UNABLETOFUND,
        WITHDRAWN: statusSummary.LABEL_STATUS_SUMMARY_ITEM_WITHDRAWN,
        APPROVED: statusSummary.LABEL_STATUS_SUMMARY_ITEM_APPROVED,
        HEADER_LABEL: localizedKeys.FUNDING_STATUS_HEADER_LABEL,
        TABLE_HEADER_NAME: localizedKeys.FUNDING_STATUS_TABLE_HEADER_NAME,
        TABLE_HEADER_APPLICANT_NAME:
            localizedKeys.FUNDING_STATUS_TABLE_HEADER_APPLICANT_NAME,
        TABLE_HEADER_ACCOUNT_NUMBER:
            localizedKeys.FUNDING_STATUS_TABLE_HEADER_ACCOUNT_NUMBER,
        TABLE_HEADER_STATUS: localizedKeys.FUNDING_STATUS_TABLE_HEADER_STATUS,
        TABLE_HEADER_COLLATERAL:
            localizedKeys.FUNDING_STATUS_TABLE_HEADER_COLLATERAL,
        TABLE_HEADER_VIN: localizedKeys.FUNDING_STATUS_TABLE_HEADER_VIN,
        TABLE_HEADER_DATE: localizedKeys.FUNDING_STATUS_TABLE_HEADER_DATE,
        TABLE_FUNCTION_RESET_FILTER:
            localizedKeys.FUNDING_STATUS_TABLE_FUNCTION_RESET_FILTER,
        TABLE_FUNCTION_RECEIVED_DATE:
            localizedKeys.FUNDING_STATUS_TABLE_FUNCTION_RECEIVED_DATE,
        TABLE_FUNCTION_ALL_STATUSES:
            localizedKeys.FUNDING_STATUS_TABLE_FUNCTION_ALL_STATUSES,
        TABLE_FUNCTION_SEARCH_BY:
            localizedKeys.FUNDING_STATUS_TABLE_FUNCTION_SEARCH_BY,
        TABLE_FUNCTION_SEARCH_BY_CA:
            localizedKeys.FUNDING_STATUS_TABLE_FUNCTION_SEARCH_BY_CA,
        TABLE_OPTION_DAYS: localizedKeys.FUNDING_STATUS_TABLE_OPTION_DAYS,
    };

    const fundingStatusDetails = {
        HEADER_LABEL: localizedKeys.FUNDING_STATUS_DETAILS_HEADER,
        APPLICANT_HEADER: localizedKeys.FUNDING_STATUS_DETAILS_APPLICANT_HEADER,
        APPLICANT_NAME: localizedKeys.FUNDING_STATUS_DETAILS_APPLICANT_NAME,
        APPLICANT_ADDRESS:
            localizedKeys.FUNDING_STATUS_DETAILS_APPLICANT_ADDRESS,
        APPLICANT_HOME: localizedKeys.FUNDING_STATUS_DETAILS_APPLICANT_HOME,
        APPLICANT_MOBILE: localizedKeys.FUNDING_STATUS_DETAILS_APPLICANT_MOBILE,
        APPLICANT_SSN: localizedKeys.FUNDING_STATUS_DETAILS_APPLICANT_SSN,
        LOAN_AMOUNT: localizedKeys.FUNDING_STATUS_DETAILS_LOAN_AMOUNT,
        LOAN_TYPE: localizedKeys.FUNDING_STATUS_DETAILS_LOAN_TYPE,
        LOAN_TERM: localizedKeys.FUNDING_STATUS_DETAILS_LOAN_TERM,
        LOAN_MONTHS: localizedKeys.FUNDING_STATUS_DETAILS_LOAN_MONTHS,
        LOAN_BULLETIN: localizedKeys.FUNDING_STATUS_DETAILS_LOAN_BULLETIN,
        LOAN_TOTAL: localizedKeys.FUNDING_STATUS_DETAILS_LOAN_TOTAL,
        PACKAGE_HEADER: localizedKeys.FUNDING_STATUS_DETAILS_PACKAGE_HEADER,
        PACKAGE_APPLICATION:
            localizedKeys.FUNDING_STATUS_DETAILS_PACKAGE_APPLICATION_NUMBER,
        PACKAGE_CONTRACTID:
            localizedKeys.FUNDING_STATUS_DETAILS_PACKAGE_CONTRACTID,
        PACKAGE_STATUS: localizedKeys.FUNDING_STATUS_DETAILS_PACKAGE_STATUS,
        HISTORY_HEADER: localizedKeys.FUNDING_STATUS_DETAILS_HISTORY_HEADER,
        HISTORY_AUDITOR: localizedKeys.FUNDING_STATUS_DETAILS_HISTORY_AUDITOR,
        HISTORY_NOTES: localizedKeys.FUNDING_STATUS_DETAILS_HISTORY_NOTES,
        HISTORY_MISSING: localizedKeys.FUNDING_STATUS_DETAILS_MISSING_ITEMS,
        DATE_AT: localizedKeys.FUNDING_STATUS_DETAILS_DATE_AT,
    };

    const statusFilterOptionToResponseMap = {
        [fundingStatus.BOOKED]: FundingSearchStatusOptions.BookedAndPaid,
        [fundingStatus.RECEIVED]: FundingSearchStatusOptions.ReceivedInProcess,
        [fundingStatus.UNABLETOFUND]: FundingSearchStatusOptions.UnableToFund,
        [fundingStatus.WITHDRAWN]: FundingSearchStatusOptions.Withdrawn,
        [fundingStatus.APPROVED]: FundingSearchStatusOptions.FundingApproved,
    };
    const statusFilterResponseToOptionMap = {
        [FundingSearchStatusOptions.BookedAndPaid]: fundingStatus.BOOKED,
        [FundingSearchStatusOptions.ReceivedInProcess]: fundingStatus.RECEIVED,
        [FundingSearchStatusOptions.UnableToFund]: fundingStatus.UNABLETOFUND,
        [FundingSearchStatusOptions.Withdrawn]: fundingStatus.WITHDRAWN,
        [FundingSearchStatusOptions.FundingApproved]: fundingStatus.APPROVED,
    };
    const statusFilterOptionMap = {
        [fundingStatus.BOOKED]: FundingSearchStatusOptionsState.BookedAndPaid,
        [fundingStatus.RECEIVED]:
            FundingSearchStatusOptionsState.ReceivedInProcess,
        [fundingStatus.UNABLETOFUND]:
            FundingSearchStatusOptionsState.UnableToFund,
        [fundingStatus.WITHDRAWN]: FundingSearchStatusOptionsState.Withdrawn,
        [fundingStatus.APPROVED]:
            FundingSearchStatusOptionsState.FundingApproved,
        [fundingStatus.TABLE_FUNCTION_ALL_STATUSES]:
            FundingSearchStatusOptionsState.FundingApproved,
    };

    const eft = {
        LABEL_EFT_HEADER: localizedKeys.LABEL_EFT_HEADER,
        EFT_DISCLAIMER_CONFIDENTIAL: localizedKeys.EFT_DISCLAIMER_CONFIDENTIAL,
        EFT_THANKS: localizedKeys.EFT_THANKS,
        EFT_VW: localizedKeys.EFT_VW,
        EFT_AUDI: localizedKeys.EFT_AUDI,
        EFT_DISCLAIMER_FUNDS: localizedKeys.EFT_DISCLAIMER_FUNDS,
        EFT_NO_RESPONSE: localizedKeys.EFT_NO_RESPONSE,
        EFT_SELECT_OTHER: localizedKeys.EFT_SELECT_OTHER,
        EFT_DAYS_WITH_NOTIFICATIONS: localizedKeys.EFT_DAYS_WITH_NOTIFICATIONS,
        EFT_DEPOSIT_PART1: localizedKeys.EFT_DEPOSIT_PART1,
        EFT_AT: localizedKeys.EFT_AT,
        EFT_DEPOSIT_PART2: localizedKeys.EFT_DEPOSIT_PART2,
        EFT_REMITTANCE_INFO: localizedKeys.EFT_REMITTANCE_INFO,
        EFT_RESERVE_AMOUNT: localizedKeys.EFT_RESERVE_AMOUNT,
        EFT_AMOUNT_PAID: localizedKeys.EFT_AMOUNT_PAID,
        EFT_TOTAL: localizedKeys.EFT_TOTAL,
    };

    const errorMessages = {
        API_FAILURE: localizedKeys.API_FAILURE,
        NOT_NUMBER: localizedKeys.NOT_NUMBER,
    };

    const labelsCommon = {
        EMPTY_FIELD: localizedKeys.EMPTY_FIELD,
        PRINT: localizedKeys.PRINT,
    };

    const serviceLoanerInventory = {
        LABEL_SL_HEADER: localizedKeys.LABEL_SL_HEADER,
    };
    const dealerRentalInventory = {
        LABEL_DR_HEADER: localizedKeys.LABEL_DR_HEADER,
    };
    const inventoryTable = {
        VIN: localizedKeys.INVENTORY_TABLE_VIN,
        VEHICLE: localizedKeys.INVENTORY_TABLE_VEHICLE,
        STATUS: localizedKeys.INVENTORY_TABLE_STATUS,
        MATURITY_DATE: localizedKeys.INVENTORY_TABLE_MATURITY_DATE,
        PURCHASE_PRICE: localizedKeys.INVENTORY_TABLE_PURCHASE_PRICE,
    };
    const dealerMgmt = {
        DEALER_MGMT_HEADER_LABEL: localizedKeys.DEALER_MGMT_HEADER_LABEL,
        DEALER_MGMT_ADD_BUTTON_LABEL:
            localizedKeys.DEALER_MGMT_ADD_BUTTON_LABEL,
        DEALER_MGMT_USERS_NUMBER_LABEL:
            localizedKeys.DEALER_MGMT_USERS_NUMBER_LABEL,
        DEALER_MGMT_TABLE_HEADER_NAME:
            localizedKeys.DEALER_MGMT_TABLE_HEADER_NAME,
        DEALER_MGMT_TABLE_HEADER_EMAIL:
            localizedKeys.DEALER_MGMT_TABLE_HEADER_EMAIL,
        DEALER_MGMT_TABLE_HEADER_ADMIN:
            localizedKeys.DEALER_MGMT_TABLE_HEADER_ADMIN,
        DEALER_MGMT_TABLE_HEADER_STATUS:
            localizedKeys.DEALER_MGMT_TABLE_HEADER_STATUS,
        DEALER_MGMT_TABLE_HEADER_DEALERS:
            localizedKeys.DEALER_MGMT_TABLE_HEADER_DEALERS,
        DEALER_MGMT_TABLE_MENU_EDIT: localizedKeys.DEALER_MGMT_TABLE_MENU_EDIT,
        DEALER_MGMT_TABLE_MENU_DEACTIVATE:
            localizedKeys.DEALER_MGMT_TABLE_MENU_DEACTIVATE,
        DEALER_MGMT_TABLE_MENU_ACTIVATE:
            localizedKeys.DEALER_MGMT_TABLE_MENU_ACTIVATE,
        DEALER_MGMT_TABLE_MENU_DELETE:
            localizedKeys.DEALER_MGMT_TABLE_MENU_DELETE,
        DEALER_MGMT_DEACTIVATE_MODAL_DESCRIPTION:
            localizedKeys.DEALER_MGMT_DEACTIVATE_MODAL_DESCRIPTION,
        DEALER_MGMT_ACTIVATE_MODAL_DESCRIPTION:
            localizedKeys.DEALER_MGMT_ACTIVATE_MODAL_DESCRIPTION,
        DEALER_MGMT_DELETE_MODAL_DESCRIPTION:
            localizedKeys.DEALER_MGMT_DELETE_MODAL_DESCRIPTION,
        DEALER_MGMT_DEACTIVATE_MODAL_CONFIRM_BUTTON:
            localizedKeys.DEALER_MGMT_DEACTIVATE_MODAL_CONFIRM_BUTTON,
        DEALER_MGMT_ACTIVATE_MODAL_CONFIRM_BUTTON:
            localizedKeys.DEALER_MGMT_ACTIVATE_MODAL_CONFIRM_BUTTON,
        DEALER_MGMT_DELETE_MODAL_CONFIRM_BUTTON:
            localizedKeys.DEALER_MGMT_DELETE_MODAL_CONFIRM_BUTTON,
        DEALER_MGMT_DELETE_MODAL_TITLE:
            localizedKeys.DEALER_MGMT_DELETE_MODAL_TITLE,
        DEALER_MGMT_DEACTIVATE_MODAL_TITLE:
            localizedKeys.DEALER_MGMT_DEACTIVATE_MODAL_TITLE,
        DEALER_MGMT_TABLE_USER_STATUS_ACTIVE:
            localizedKeys.DEALER_MGMT_TABLE_USER_STATUS_ACTIVE,
        DEALER_MGMT_TABLE_USER_STATUS_INACTIVE:
            localizedKeys.DEALER_MGMT_TABLE_USER_STATUS_INACTIVE,
        DEALER_MGMT_TABLE_MENU_DELETEMFA:
            localizedKeys.DEALER_MGMT_TABLE_MENU_DELETEMFA,
        DEALER_MGMT_DELETEMFA_MODAL_TITLE:
            localizedKeys.DEALER_MGMT_DELETEMFA_MODAL_TITLE,
        DEALER_MGMT_DELETEMFA_MODAL_DESCRIPTION:
            localizedKeys.DEALER_MGMT_DELETEMFA_MODAL_DESCRIPTION,
        DEALER_MGMT_DELETEMFA_MODAL_CONFIRM_BUTTON:
            localizedKeys.DEALER_MGMT_DELETEMFA_MODAL_CONFIRM_BUTTON,
        DEALER_MGMT_TABLE_USER_MESSAGES_SUCCESSMFA_DELETED:
            localizedKeys.DEALER_MGMT_TABLE_USER_MESSAGES_SUCCESSMFA_DELETED,
        DEALER_MGMT_RESETPASS_MODAL_TITLE:
            localizedKeys.DEALER_MGMT_RESETPASS_MODAL_TITLE,
        DEALER_MGMT_RESETPASS_MODAL_DESCRIPTION:
            localizedKeys.DEALER_MGMT_RESETPASS_MODAL_DESCRIPTION,
        DEALER_MGMT_RESETPASS_MODAL_CONFIRM_BUTTON:
            localizedKeys.DEALER_MGMT_RESETPASS_MODAL_CONFIRM_BUTTON,
        DEALER_MGMT_UNLOCK_MODAL_TITLE:
            localizedKeys.DEALER_MGMT_UNLOCK_MODAL_TITLE,
        DEALER_MGMT_UNLOCK_MODAL_DESCRIPTION:
            localizedKeys.DEALER_MGMT_UNLOCK_MODAL_DESCRIPTION,
        DEALER_MGMT_UNLOCK_MODAL_CONFIRM_BUTTON:
            localizedKeys.DEALER_MGMT_UNLOCK_MODAL_CONFIRM_BUTTON,
    };

    const registrationSuccess = {
        REG_SUCC_TITLE: localizedKeys.REGISTRATION_SUCCESS_LABEL_TITLE,
        REG_SUCC_SUBTITLE: localizedKeys.REGISTRATION_SUCCESS_LABEL_SUBTITLE,
        REG_SUCC_TEXT: localizedKeys.REGISTRATION_SUCCESS_LABEL_TEXT,
        REG_SUCC_BUTTON: localizedKeys.REGISTRATION_SUCCESS_LABEL_BUTTON,
    };

    const accountRegistration = {
        ACC_REG_TITLE: localizedKeys.ACCOUNT_REGISTRATION_TITLE,
        ACC_REG_DESCRIPTION: localizedKeys.ACCOUNT_REGISTRATION_DESCRIPTION,
        ACC_REG_LABEL_PASSWORD:
            localizedKeys.ACCOUNT_REGISTRATION_LABEL_PASSWORD,
        ACC_REG_LABEL_PASSWORD_CONFIRM:
            localizedKeys.ACCOUNT_REGISTRATION_LABEL_PASSWORD_CONFIRM,
        ACC_REG_LABEL_PASSWORD_NOTE_HEADING:
            localizedKeys.ACCOUNT_REGISTRATION_LABEL_PASSWORD_NOTE_HEADING,
        ACC_REG_LABEL_PASSWORD_NOTE1:
            localizedKeys.ACCOUNT_REGISTRATION_LABEL_PASSWORD_NOTE1,
        ACC_REG_LABEL_PASSWORD_NOTE2:
            localizedKeys.ACCOUNT_REGISTRATION_LABEL_PASSWORD_NOTE2,
        ACC_REG_LABEL_PASSWORD_NOTE3:
            localizedKeys.ACCOUNT_REGISTRATION_LABEL_PASSWORD_NOTE3,
        ACC_REG_LABEL_AGREEMENT:
            localizedKeys.ACCOUNT_REGISTRATION_LABEL_AGREEMENT,
        ACC_REG_LABEL_USAGE_AGREEMENT:
            localizedKeys.ACCOUNT_REGISTRATION_LABEL_USAGE_AGREEMENT,
        ACC_REG_LABEL_CONTINUE:
            localizedKeys.ACCOUNT_REGISTRATION_BUTTON_CONTINUE,
        ACC_REG_LABEL_FIELD_REQUIRED:
            localizedKeys.ACCOUNT_REGISTRATION_LABEL_FIELD_REQUIRED,
        ACC_REG_LABEL_NOTIFICATION_ERROR:
            localizedKeys.ACCOUNT_REGISTRATION_LABEL_NOTIFICATION_ERROR,
    };

    const commonLabels = {
        EMPTY_FIELD: localizedKeys.EMPTY_FIELD,
        PRINT: localizedKeys.PRINT,
    };

    const detailsView = {
        TITLE_DEALER_RENTAL: localizedKeys.TITLE_DEALER_RENTAL,
        TITLE_SERVICE_LOANER: localizedKeys.TITLE_SERVICE_LOANER,
        VIN: localizedKeys.VIN,
        MAKE: localizedKeys.MAKE,
        MODELCODE: localizedKeys.MODELCODE,
        COLOR: localizedKeys.COLOR,
        OPTIONS: localizedKeys.OPTIONS,
        STOCKNO: localizedKeys.STOCKNO,
        MILEAGE: localizedKeys.MILEAGE,
        MILEAGE_DATE: localizedKeys.MILEAGE_DATE,
        NOTES: localizedKeys.NOTES,
        ACCOUNT_INFO: localizedKeys.ACCOUNT_INFO,
        LEASE_CONTRACT_ID: localizedKeys.LEASE_CONTRACT_ID,
        IN_SERVICE_DATE: localizedKeys.IN_SERVICE_DATE,
        LEASE_TERM: localizedKeys.LEASE_TERM,
        MATURITY_DATE: localizedKeys.MATURITY_DATE,
        SPECIAL_PGM_CODE: localizedKeys.SPECIAL_PGM_CODE,
        LENGTH_OF_SERVICE: localizedKeys.LENGTH_OF_SERVICE,
        REMAINING_PAYMENTS: localizedKeys.REMAINING_PAYMENTS,
        STATUS: localizedKeys.STATUS,
        MSRP: localizedKeys.MSRP,
        MONTHLY_PAYMENT: localizedKeys.MONTHLY_PAYMENT,
        CURRENTPURCHAERPRICE: localizedKeys.CURRENTPURCHAERPRICE,
        REMAINING_PAYMENTS_ON_LEASE: localizedKeys.REMAINING_PAYMENTS_ON_LEASE,
        BUYOUT_AT_TERM: localizedKeys.BUYOUT_AT_TERM,
        ACTIVE: localizedKeys.ACTIVE,
        MATURITY: localizedKeys.MATURITY,
        BEYOND_TERM: localizedKeys.BEYOND_TERM,
        FORCE_GROUND: localizedKeys.FORCE_GROUND,
        MONTHS: localizedKeys.MONTHS,
    };

    const addNewUser = {
        HEADER_BUTTON_LABEL: localizedKeys.ADD_NEW_USER_HEADER_BACK,
        BUTTON_ADD_LABEL: localizedKeys.ADD_NEW_USER_BUTTON_ADD,
        BODY_HEADER: localizedKeys.ADD_NEW_USER_BODY_HEADER,
        BODY_ALLFIELDS: localizedKeys.ADD_NEW_USER_BODY_ALLFIELDS,
        FORM_LABEL_EMAIL: localizedKeys.ADD_NEW_USER_FORM_LABEL_EMAIL,
        FORM_LABEL_FIRSTNAME: localizedKeys.ADD_NEW_USER_FORM_LABEL_FIRSTNAME,
        FORM_LABEL_LASTNAME: localizedKeys.ADD_NEW_USER_FORM_LABEL_LASTNAME,
        FORM_LABEL_PHONE: localizedKeys.ADD_NEW_USER_FORM_LABEL_PHONE,
        DEALERSHIP_HEADER: localizedKeys.ADD_NEW_USER_DEALERSHIP_HEADER,
        DEALERSHIP_REQUIRED: localizedKeys.ADD_NEW_USER_DEALERSHIP_REQUIRED,
        DEALERSHIP_SELECT_PLACEHOLDER:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PLACEHOLDER,
        DEALERSHIP_SELECT_MODIFIER:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_MODIFIER,
        DEALERSHIP_SELECT_PERMISSION_ADMIN:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_ADMIN,
        DEALERSHIP_SELECT_PERMISSION_CREDITAPPLICATION:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_CREDITAPPLICATION,
        DEALERSHIP_SELECT_PERMISSION_APPLICATIONSTATUS:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_APPLICATIONSTATUS,
        DEALERSHIP_SELECT_PERMISSION_FUNDINGSTATUS:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_FUNDINGSTATUS,
        DEALERSHIP_SELECT_PERMISSION_PAYOFFQUOTES:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_PAYOFFQUOTES,
        DEALERSHIP_SELECT_PERMISSION_BULLETINS:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_BULLETINS,
        DEALERSHIP_SELECT_PERMISSION_DEALERRESERVEEFT:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_DEALERRESERVEEFT,
        DEALERSHIP_SELECT_PERMISSION_DEALERRENTAL:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_DEALERRENTAL,
        DEALERSHIP_SELECT_PERMISSION_SERVICELOANER:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_SERVICELOANER,
        DEALERSHIP_SELECT_PERMISSION_TITLE_MATURITY:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_TITLE_MATURITY,
        DEALERSHIP_SELECT_PERMISSION_MATURITY_ASSIGN:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_MATURITY_ASSIGN,
        DEALERSHIP_SELECT_PERMISSION_MATURITY_VIEW:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_MATURITY_VIEW,
        DEALERSHIP_SELECT_PERMISSION_TITLE_QUICKFUND:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_TITLE_QUICKFUND,
        DEALERSHIP_SELECT_PERMISSION_QUICKFUND_ASSIGN:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_QUICKFUND_ASSIGN,
        DEALERSHIP_SELECT_PERMISSION_QUICKFUND_VIEW:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_QUICKFUND_VIEW,
        DEALERSHIP_SELECT_PERMISSION_TITLE_ELECTRONIC_PAYMENTS:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_TITLE_ELECTRONIC_PAYMENTS,
        DEALERSHIP_SELECT_PERMISSION_SUBMIT_ELECTRONIC_PAYMENT:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_SUBMIT_ELECTRONIC_PAYMENT,
        DEALERSHIP_SELECT_PERMISSION_SUBMIT_APPROVED_ELECTRONIC_PAYMENT:
            localizedKeys.ADD_NEW_USER_DEALERSHIP_SELECT_PERMISSION_SUBMIT_APPROVED_ELECTRONIC_PAYMENT,
        NOTIFICATION_SUCCESS: localizedKeys.ADD_NEW_USER_NOTIFICATION_SUCCESS,
        NOTIFICATION_ERROR_REQUEST:
            localizedKeys.ADD_NEW_USER_NOTIFICATION_ERROR_REQUEST,
        MODAL_TITLE: localizedKeys.ADD_NEW_USER_MODAL_TITLE,
        MODAL_CONTENT: localizedKeys.ADD_NEW_USER_MODAL_CONTENT,
        MODAL_LABEL_BUTTON_CANCEL:
            localizedKeys.ADD_NEW_USER_MODAL_LABEL_BUTTON_CANCEL,
        MODAL_LABEL_BUTTON_CONFIRM:
            localizedKeys.ADD_NEW_USER_MODAL_LABEL_BUTTON_CONFIRM,
    };

    const ismAddNewUser = {
        HEADER_BACK: localizedKeys.ISM_ADD_NEW_USER_HEADER_BACK,
        BODY_HEADER: localizedKeys.ISM_ADD_NEW_USER_BODY_HEADER,
        BODY_ALLFIELDS: localizedKeys.ISM_ADD_NEW_USER_BODY_ALLFIELDS,
        FORM_LABEL_PHONE: localizedKeys.ISM_ADD_NEW_USER_FORM_LABEL_PHONE,
        FORM_LABEL_EMAIL: localizedKeys.ISM_ADD_NEW_USER_FORM_LABEL_EMAIL,
        FORM_LABEL_FIRSTNAME:
            localizedKeys.ISM_ADD_NEW_USER_FORM_LABEL_FIRSTNAME,
        FORM_LABEL_LASTNAME: localizedKeys.ISM_ADD_NEW_USER_FORM_LABEL_LASTNAME,
        DEALERSHIP_HEADER: localizedKeys.ISM_ADD_NEW_USER_DEALERSHIP_HEADER,
        DEALERSHIP_DESCRIPTION:
            localizedKeys.ISM_ADD_NEW_USER_DEALERSHIP_DESCRIPTION,
        DEALERSHIP_SELECT_PLACEHOLDER:
            localizedKeys.ISM_ADD_NEW_USER_DEALERSHIP_SELECT_PLACEHOLDER,
        DEALERSHIP_SELECT_MODIFIER:
            localizedKeys.ISM_ADD_NEW_USER_DEALERSHIP_SELECT_MODIFIER,
        PERMISSIONS_HEADER: localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_HEADER,
        PERMISSIONS_DESCRIPTION:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_DESCRIPTION,
        PERMISSIONS_INTERNAL_TOOLS:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_INTERNAL_TOOLS,
        PERMISSIONS_DEALER_TOOLS:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_DEALER_TOOLS,
        PERMISSIONS_CONTENT_TOOLS:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_CONTENT_TOOLS,
        PERMISSIONS_VEHICLE_MAINTENANCE:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_VEHICLE_MAINTENANCE,
        PERMISSIONS_INTERNAL_SECURITY:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_INTERNAL_SECURITY,
        PERMISSIONS_FIELD_USER_MGMT:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_FIELD_USER_MGMT,
        PERMISSIONS_DEALER_ACTIVATION:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_DEALER_ACTIVATION,
        PERMISSIONS_DEALER_SECURITY:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_DEALER_SECURITY,
        PERMISSIONS_CV_FORM: localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_CV_FORM,
        PERMISSIONS_EDOCS: localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_EDOCS,
        PERMISSIONS_NEWS_STORIES:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_NEWS_STORIES,
        PERMISSIONS_DISTRIBUTION_RULE:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_DISTRIBUTION_RULE,
        PERMISSIONS_ONLINE_BULLETINS:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_ONLINE_BULLETINS,
        PERMISSIONS_STATIC_CONTENT:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_STATIC_CONTENT,
        PERMISSIONS_VEHICLE_MAINTENANCE_PERM:
            localizedKeys.ISM_ADD_NEW_USER_PERMISSIONS_VEHICLE_MAINTENANCE_PERM,
        BUTTON_SUBMIT: localizedKeys.ISM_ADD_NEW_USER_BUTTON_SUBMIT,
        BUTTON_CANCEL: localizedKeys.ISM_ADD_NEW_USER_BUTTON_CANCEL,
        NOTIFICATION_SUCCESS:
            localizedKeys.ISM_ADD_NEW_USER_NOTIFICATION_SUCCESS,
        NOTIFICATION_ERROR: localizedKeys.ISM_ADD_NEW_USER_NOTIFICATION_ERROR,
        MODAL_TITLE: localizedKeys.ISM_ADD_NEW_USER_MODAL_TITLE,
        MODAL_CONTENT: localizedKeys.ISM_ADD_NEW_USER_MODAL_CONTENT,
        MODAL_CANCEL: localizedKeys.ISM_ADD_NEW_USER_MODAL_CANCEL,
        MODAL_CONFIRM: localizedKeys.ISM_ADD_NEW_USER_MODAL_CONFIRM,
    };

    const editUser = {
        SETTINGS_HEADER: localizedKeys.EDIT_USER_SETTINGS_HEADER,
        SETTINGS_PROFILE: localizedKeys.EDIT_USER_SETTINGS_PROFILE,
        SETTINGS_PASSWORD: localizedKeys.EDIT_USER_SETTINGS_PASSWORD,
        PROFILE_HEADER: localizedKeys.EDIT_USER_PROFILE_HEADER,
        PASSWORD_HEADER: localizedKeys.EDIT_USER_PASSWORD_HEADER,
        NOTIFICATION_ERROR_REQUEST:
            localizedKeys.EDIT_USER_NOTIFICATION_ERROR_REQUEST,
        NOTIFICATION_SUCCESS_REQUEST:
            localizedKeys.EDIT_USER_NOTIFICATION_SUCCESS_REQUEST,
        NOTIFICATION_SUCCESS_ACTIVATION:
            localizedKeys.EDIT_USER_NOTIFICATION_SUCCESS_ACTIVATION,
        NOTIFICATION_SUCCESS_DEACTIVATION:
            localizedKeys.EDIT_USER_NOTIFICATION_SUCCESS_DEACTIVATION,
        NOTIFICATION_SUCCESS_PASSWORDRESETLINK:
            localizedKeys.EDIT_USER_NOTIFICATION_SUCCESS_PASSWORDRESETLINK,
        NOTIFICATION_ERROR_PASSWORDRESETLINK_LOCKED:
            localizedKeys.EDIT_USER_NOTIFICATION_ERROR_PASSWORDRESETLINK_LOCKED,
        EDIT_USER_NOTIFICATION_WARNING_LOCKED:
            localizedKeys.EDIT_USER_NOTIFICATION_WARNING_LOCKED,
        MODAL_CONTENT: localizedKeys.EDIT_USER_MODAL_CONTENT,
        BUTTON_DISCARD_LABEL: localizedKeys.EDIT_USER_BUTTON_DISCARD_LABEL,
        BUTTON_SAVE_LABEL: localizedKeys.EDIT_USER_BUTTON_SAVE_LABEL,
        BUTTON_RESET_PWD_LABEL: localizedKeys.EDIT_USER_BUTTON_RESET_PWD_LABEL,
        BUTTON_UNLOCK_LABEL: localizedKeys.EDIT_USER_BUTTON_UNLOCK,
        NOTIFICATION_SUCCESS_UNLOCK:
            localizedKeys.EDIT_USER_NOTIFICATION_SUCCESS_UNLOCKED,
    };

    const ismUpdateUser = {
        HEADER_BACK: localizedKeys.ISM_UPDATE_USER_HEADER_BACK,
        PROFILE_HEADER: localizedKeys.ISM_UPDATE_USER_PROFILE_HEADER,
        PHONE_DESCRIPTION: localizedKeys.ISM_UPDATE_USER_PHONE_DESCRIPTION,
        BUTTON_CANCEL: localizedKeys.ISM_UPDATE_USER_BUTTON_CANCEL,
        MENU_SUSPEND: localizedKeys.ISM_UPDATE_USER_MENU_SUSPEND,
        MENU_REINSTATE: localizedKeys.ISM_UPDATE_USER_MENU_REINSTATE,
        MENU_DELETE: localizedKeys.ISM_UPDATE_USER_MENU_DELETE,
        NOTIFICATION_SUCCESS_SAVE:
            localizedKeys.ISM_UPDATE_USER_NOTIFICATION_SUCCESS_SAVE,
        NOTIFICATION_SUCCESS_REINSTATE:
            localizedKeys.ISM_UPDATE_USER_NOTIFICATION_SUCCESS_REINSTATE,
        NOTIFICATION_SUCCESS_SUSPEND:
            localizedKeys.ISM_UPDATE_USER_NOTIFICATION_SUCCESS_SUSPEND,
        NOTIFICATION_ERROR: localizedKeys.ISM_UPDATE_USER_NOTIFICATION_ERROR,
        MODAL_TITLE: localizedKeys.ISM_UPDATE_USER_MODAL_TITLE,
        MODAL_CONTENT: localizedKeys.ISM_UPDATE_USER_MODAL_CONTENT,
        MODAL_CANCEL: localizedKeys.ISM_UPDATE_USER_MODAL_CANCEL,
        MODAL_CONFIRM: localizedKeys.ISM_UPDATE_USER_MODAL_CONFIRM,
        ACTION_MODAL_TITLE_SUSPEND:
            localizedKeys.ISM_UPDATE_USER_ACTION_MODAL_TITLE_SUSPEND,
        ACTION_MODAL_TITLE_REINSTATE:
            localizedKeys.ISM_UPDATE_USER_ACTION_MODAL_TITLE_REINSTATE,
        ACTION_MODAL_TITLE_DELETE:
            localizedKeys.ISM_UPDATE_USER_ACTION_MODAL_TITLE_DELETE,
        ACTION_MODAL_BODY_SUSPEND:
            localizedKeys.ISM_UPDATE_USER_ACTION_MODAL_BODY_SUSPEND,
        ACTION_MODAL_BODY_REINSTATE:
            localizedKeys.ISM_UPDATE_USER_ACTION_MODAL_BODY_REINSTATE,
        ACTION_MODAL_BODY_DELETE:
            localizedKeys.ISM_UPDATE_USER_ACTION_MODAL_BODY_DELETE,
        ACTION_MODAL_CONFIRM:
            localizedKeys.ISM_UPDATE_USER_ACTION_MODAL_CONFIRM,
    };

    const deleteUser = {
        NOTIFICATION_SUCCESS_DELETION:
            localizedKeys.NOTIFICATION_SUCCESS_DELETION,
    };
    const creditAppPrompt = {
        TITLE: localizedKeys.CREDIT_APP_PROMPT_TITLE,
        DESCRIPTION: localizedKeys.CREDIT_APP_PROMPT_DESCRIPTION,
        CANCEL_BTN_TXT: localizedKeys.CREDIT_APP_PROMPT_CANCEL_BTN_TXT,
        PROCEED_BRN_TXT: localizedKeys.CREDIT_APP_PROMPT_PROCEED_BTN_TXT,
    };
    const vehicleInformation = {
        TITLE: localizedKeys.VEHICLEINFO_TITLE,
        TYPE_LABEL: localizedKeys.VEHICLEINFO_TYPE_LABEL,
        TYPE_NEW: localizedKeys.VEHICLEINFO_TYPE_NEW,
        TYPE_PRE: localizedKeys.VEHICLEINFO_TYPE_PRE,
        TYPE_CPO: localizedKeys.VEHICLEINFO_TYPE_CPO,
        TYPE_DEMO: localizedKeys.VEHICLEINFO_TYPE_DEMO,
        MAKE_LABEL: localizedKeys.VEHICLEINFO_MAKE_LABEL,
        MAKE_DEAFULT: localizedKeys.VEHICLEINFO_MAKE_DEAFULT,
        YEAR_LABEL: localizedKeys.VEHICLEINFO_YEAR_LABEL,
        YEAR_DEAFULT: localizedKeys.VEHICLEINFO_YEAR_DEAFULT,
        MODEL_LABEL: localizedKeys.VEHICLEINFO_MODEL_LABEL,
        MODEL_DEAFULT: localizedKeys.VEHICLEINFO_MODEL_DEAFULT,
        BODY_LABEL: localizedKeys.VEHICLEINFO_BODY_LABEL,
        BODY_DEAFULT: localizedKeys.VEHICLEINFO_BODY_DEAFULT,
        MILEAGE_LABEL: localizedKeys.VEHICLEINFO_MILEAGE_LABEL,
        MILEAGE_ERROR: localizedKeys.VEHICLEINFO_MILEAGE_ERROR,
        VIN_LABEL: localizedKeys.VEHICLEINFO_VIN_LABEL,
        VIN_ERROR: localizedKeys.VEHICLEINFO_VIN_ERROR,
        OPTION_AUCTION: localizedKeys.VEHICLEINFO_OPTION_AUCTION,
        REQUIRED_ERR_LABEL: localizedKeys.LABEL_FIELD_ERROR_REQUIRED,
    };

    const applicantInfo = {
        TITLE: localizedKeys.CREDIT_APPLICANT_TITLE,
        FIRSTNAME_LABEL: localizedKeys.CREDIT_APPLICANT_FIRSTNAME_LABEL,
        FIRSTNAME_ERROR: localizedKeys.CREDIT_APPLICANT_FIRSTNAME_ERROR,
        MI_LABEL: localizedKeys.CREDIT_APPLICANT_MI_LABEL,
        MI_ERROR: localizedKeys.CREDIT_APPLICANT_MI_ERROR,
        LASTNAME_LABEL: localizedKeys.CREDIT_APPLICANT_LASTNAME_LABEL,
        LASTNAME_ERROR: localizedKeys.CREDIT_APPLICANT_LASTNAME_ERROR,
        IDNUMBER_US_LABEL: localizedKeys.CREDIT_APPLICANT_IDNUMBER_US_LABEL,
        IDNUMBER_US_BUSINESS_LABEL:
            localizedKeys.CREDIT_APPLICANT_IDNUMBER_US_BUSINESS_LABEL,
        IDNUMBER_US_ERROR: localizedKeys.CREDIT_APPLICANT_IDNUMBER_US_ERROR,
        IDNUMBER_CA_LABEL: localizedKeys.CREDIT_APPLICANT_IDNUMBER_CA_LABEL,
        IDNUMBER_CA_ERROR: localizedKeys.CREDIT_APPLICANT_IDNUMBER_CA_ERROR,
        BIRTHDATE_LABEL: localizedKeys.CREDIT_APPLICANT_BIRTHDATE_LABEL,
        BIRTHDATE_ERROR: localizedKeys.CREDIT_APPLICANT_BIRTHDATE_ERROR,
        ADDRESS1_LABEL: localizedKeys.CREDIT_APPLICANT_ADDRESS1_LABEL,
        ADDRESS1_ERROR: localizedKeys.CREDIT_APPLICANT_ADDRESS1_ERROR,
        ADDRESS2_LABEL: localizedKeys.CREDIT_APPLICANT_ADDRESS2_LABEL,
        CITY_LABEL: localizedKeys.CREDIT_APPLICANT_CITY_LABEL,
        CITY_ERROR: localizedKeys.CREDIT_APPLICANT_CITY_ERROR,
        STATE_US_LABEL: localizedKeys.CREDIT_APPLICANT_STATE_US_LABEL,
        STATE_CA_LABEL: localizedKeys.CREDIT_APPLICANT_STATE_CA_LABEL,
        STATE_US_DEFAULT: localizedKeys.CREDIT_APPLICANT_STATE_US_DEFAULT,
        STATE_CA_DEFAULT: localizedKeys.CREDIT_APPLICANT_STATE_CA_DEFAULT,
        STATE_CA_AB: localizedKeys.CREDIT_APPLICANT_STATE_CA_AB,
        STATE_CA_BC: localizedKeys.CREDIT_APPLICANT_STATE_CA_BC,
        STATE_CA_MB: localizedKeys.CREDIT_APPLICANT_STATE_CA_MB,
        STATE_CA_NB: localizedKeys.CREDIT_APPLICANT_STATE_CA_NB,
        STATE_CA_NL: localizedKeys.CREDIT_APPLICANT_STATE_CA_NL,
        STATE_CA_NS: localizedKeys.CREDIT_APPLICANT_STATE_CA_NS,
        STATE_CA_NT: localizedKeys.CREDIT_APPLICANT_STATE_CA_NT,
        STATE_CA_NU: localizedKeys.CREDIT_APPLICANT_STATE_CA_NU,
        STATE_CA_ON: localizedKeys.CREDIT_APPLICANT_STATE_CA_ON,
        STATE_CA_PE: localizedKeys.CREDIT_APPLICANT_STATE_CA_PE,
        STATE_CA_QC: localizedKeys.CREDIT_APPLICANT_STATE_CA_QC,
        STATE_CA_SK: localizedKeys.CREDIT_APPLICANT_STATE_CA_SK,
        STATE_CA_YT: localizedKeys.CREDIT_APPLICANT_STATE_CA_YT,
        ZIP_US_LABEL: localizedKeys.CREDIT_APPLICANT_ZIP_US_LABEL,
        ZIP_US_ERROR: localizedKeys.CREDIT_APPLICANT_ZIP_US_ERROR,
        ZIP_CA_LABEL: localizedKeys.CREDIT_APPLICANT_ZIP_CA_LABEL,
        ZIP_CA_ERROR: localizedKeys.CREDIT_APPLICANT_ZIP_CA_ERROR,
        RESIDENCE_LABEL: localizedKeys.CREDIT_APPLICANT_RESIDENCE_LABEL,
        RESIDENCE_OPTIONOWN: localizedKeys.CREDIT_APPLICANT_RESIDENCE_OPTIONOWN,
        RESIDENCE_OPTIONRENT:
            localizedKeys.CREDIT_APPLICANT_RESIDENCE_OPTIONRENT,
        RESIDENCE_OPTIONPARENT:
            localizedKeys.CREDIT_APPLICANT_RESIDENCE_OPTIONPARENT,
        ADRESSYEAR_LABEL: localizedKeys.CREDIT_APPLICANT_ADRESSYEAR_LABEL,
        ADRESSYEAR_ERROR: localizedKeys.CREDIT_APPLICANT_ADRESSYEAR_ERROR,
        ADRESSMONTH_LABEL: localizedKeys.CREDIT_APPLICANT_ADRESSMONTH_LABEL,
        ADRESSMONTH_ERROR: localizedKeys.CREDIT_APPLICANT_ADRESSMONTH_ERROR,
        RENT_LABEL: localizedKeys.CREDIT_APPLICANT_RENT_LABEL,
        HOUSING_LABEL: localizedKeys.CREDIT_APPLICANT_HOUSING_LABEL,
        MORGAGE_LABEL: localizedKeys.CREDIT_APPLICANT_MORGAGE_LABEL,
        PHONE_NOTE_US: localizedKeys.CREDIT_APPLICANT_PHONE_NOTE_US,
        PHONE_NOTE_CA: localizedKeys.CREDIT_APPLICANT_PHONE_NOTE_CA,
        HOMEPHONE_LABEL: localizedKeys.CREDIT_APPLICANT_HOMEPHONE_LABEL,
        HOMEPHONE_ERROR: localizedKeys.CREDIT_APPLICANT_HOMEPHONE_ERROR,
        WORKPHONE_LABEL: localizedKeys.CREDIT_APPLICANT_WORKPHONE_LABEL,
        WORKPHONE_ERROR: localizedKeys.CREDIT_APPLICANT_WORKPHONE_ERROR,
        CELLPHONE_LABEL: localizedKeys.CREDIT_APPLICANT_CELLPHONE_LABEL,
        CELLPHONE_ERROR: localizedKeys.CREDIT_APPLICANT_CELLPHONE_ERROR,
        PHONE_ERROR: localizedKeys.CREDIT_APPLICANT_PHONE_ERROR,
        EMAIL_LABEL: localizedKeys.CREDIT_APPLICANT_EMAIL_LABEL,
        EMAIL_ERROR: localizedKeys.CREDIT_APPLICANT_EMAIL_ERROR,
        PREVACC_LABEL: localizedKeys.CREDIT_APPLICANT_PREVACC_LABEL,
        PREVACC_ERROR: localizedKeys.CREDIT_APPLICANT_PREVACC_ERROR,
        LABEL_FIELD_ERROR_REQUIRED: localizedKeys.LABEL_FIELD_ERROR_REQUIRED,
        REFERENCE_TITLE: localizedKeys.CREDIT_APP_REFERENCE_TITLE,
        REFERENCE_ADD: localizedKeys.CREDIT_APP_REFERENCE_ADD,
        REFERENCE_REMOVE: localizedKeys.CREDIT_APP_REFERENCE_REMOVE,
        REFERENCE_PHONE: localizedKeys.CREDIT_APP_REFERENCE_PHONE,
        REFERENCE_FRIEND: localizedKeys.CREDIT_APP_REFERENCE_FRIEND,
        REFERENCE_RELATIVE: localizedKeys.CREDIT_APP_REFERENCE_RELATIVE,
        REFERENCE_LANDLORD: localizedKeys.CREDIT_APP_REFERENCE_LANDLORD,
        REFERENCE_OTHER: localizedKeys.CREDIT_APP_REFERENCE_OTHER,
        REFERENCE_LABEL: localizedKeys.CREDIT_APP_REFERENCE_LABEL,
        PREVIOUS_ADDRESS_TITLE: localizedKeys.CREDIT_APP_PREVIOUS_ADDRESS_TITLE,
        PREVIOUS_ADDRESS_NOTE: localizedKeys.CREDIT_APP_PREVIOUS_ADDRESS_NOTE,
        COPY_ADDRESS_TITLE: localizedKeys.CREDIT_APPLICANT_COPY_ADDRESS,
        RELATIONSHIP_TITLE: localizedKeys.CREDIT_APPLICANT_RELATION_TITLE,
        RELATIONSHIP_NONE: localizedKeys.CREDIT_APPLICANT_RELATION_NONE,
        RELATIONSHIP_AUNT: localizedKeys.CREDIT_APPLICANT_RELATION_AUNT,
        RELATIONSHIP_BOYFRIEND:
            localizedKeys.CREDIT_APPLICANT_RELATION_BOYFRIEND,
        RELATIONSHIP_BROTHER: localizedKeys.CREDIT_APPLICANT_RELATION_BROTHER,
        RELATIONSHIP_FRIEND: localizedKeys.CREDIT_APPLICANT_RELATION_FRIEND,
        RELATIONSHIP_GRANDFATHER:
            localizedKeys.CREDIT_APPLICANT_RELATION_GRANDFATHER,
        RELATIONSHIP_GIRLFRIEND:
            localizedKeys.CREDIT_APPLICANT_RELATION_GIRLFRIEND,
        RELATIONSHIP_GRANDMOTHER:
            localizedKeys.CREDIT_APPLICANT_RELATION_GRANDMOTHER,
        RELATIONSHIP_OWNER: localizedKeys.CREDIT_APPLICANT_RELATION_OWNER,
        RELATIONSHIP_PRESIDENT:
            localizedKeys.CREDIT_APPLICANT_RELATION_PRESIDENT,
        RELATIONSHIP_SISTER: localizedKeys.CREDIT_APPLICANT_RELATION_SISTER,
        RELATIONSHIP_TREASURER:
            localizedKeys.CREDIT_APPLICANT_RELATION_TREASURER,
        RELATIONSHIP_UNCLE: localizedKeys.CREDIT_APPLICANT_RELATION_UNCLE,
        RELATIONSHIP_VICE_PRESIDENT:
            localizedKeys.CREDIT_APPLICANT_RELATION_VICE_PRESIDENT,
        RELATIONSHIP_PARENT: localizedKeys.CREDIT_APPLICANT_RELATION_PARENT,
        RELATIONSHIP_SPOUSE: localizedKeys.CREDIT_APPLICANT_RELATION_SPOUSE,
        RELATIONSHIP_OTHER: localizedKeys.CREDIT_APPLICANT_RELATION_OTHER,
        ERROR_REQUIRED_VALID: localizedKeys.LABEL_FIELD_ERROR_REQUIRED_VALID,
    };

    const creditApp = {
        TITLE: localizedKeys.CREDIT_APP_TITLE,
        UPDATE_TITLE: localizedKeys.CREDIT_APP_UPDATE_TITLE,
        SELECT_OPTION: localizedKeys.CREDIT_APP_SELECT_OPTION,
        TYPE_LABEL: localizedKeys.CREDIT_APP_TYPE_LABEL,
        TYPE_INDIVIDUAL: localizedKeys.CREDIT_APP_TYPE_INDIVIDUAL,
        TYPE_JOINT: localizedKeys.CREDIT_APP_TYPE_JOINT,
        TYPE_COAPP: localizedKeys.CREDIT_APP_TYPE_COAPP,
        TYPE_BUSINESS: localizedKeys.CREDIT_APP_TYPE_BUSINESS,
        TYPE_BUSINESSCOAPP: localizedKeys.CREDIT_APP_TYPE_BUSINESSCOAPP,
        TYPE_BUSINESSGUARANTOR: localizedKeys.CREDIT_APP_TYPE_BUSINESSGUARANTOR,
        TOOLTIP: localizedKeys.CREDIT_APP_TOOLTIP,
        SUBMIT_BUTTON: localizedKeys.CREDIT_APP_SUBMIT_BUTTON,
        SUBMIT_UPDATES_BUTTON: localizedKeys.CREDIT_APP_SUBMIT_UPDATES_BUTTON,
        EDIT_LABEL: localizedKeys.CREDIT_APP_EDIT_LABEL,
        SAVE_BUTTON: localizedKeys.CREDIT_APP_SAVE_BUTTON,
        PRINT_BUTTON: localizedKeys.CREDIT_APP_PRINT_BUTTON,
        MODAL_SUCCESS_TITLE: localizedKeys.CREDIT_APP_MODAL_SUCCESS_TITLE,
        MODAL_SUCCESS_SUBTITLE: localizedKeys.CREDIT_APP_MODAL_SUCCESS_SUBTITLE,
        MODAL_SUCCESS_CANCEL: localizedKeys.CREDIT_APP_MODAL_SUCCESS_CANCEL,
        MODAL_SUCCESS_NEXT: localizedKeys.CREDIT_APP_MODAL_SUCCESS_NEXT,
        MODAL_ERROR_TITLE: localizedKeys.CREDIT_APP_MODAL_ERROR_TITLE,
        MODAL_ERROR_SUBTITLE: localizedKeys.CREDIT_APP_MODAL_ERROR_SUBTITLE,
        MODAL_ERROR_NEXT: localizedKeys.CREDIT_APP_MODAL_ERROR_NEXT,
        MODAL_PROCESSING_TITLE: localizedKeys.CREDIT_APP_MODAL_PROCESSING_TITLE,
        MODAL_PROCESSING_SUBTITLE:
            localizedKeys.CREDIT_APP_MODAL_PROCESSING_SUBTITLE,
        MODAL_TYPE_TITLE: localizedKeys.CREDIT_APP_MODAL_TYPE_TITLE,
        MODAL_TYPE_SUBTITLE: localizedKeys.CREDIT_APP_MODAL_TYPE_SUBTITLE,
        MODAL_TYPE_TEXT: localizedKeys.CREDIT_APP_MODAL_TYPE_TEXT,
        MODAL_TYPE_BACK: localizedKeys.CREDIT_APP_MODAL_TYPE_BACK,
        MODAL_TYPE_NEW: localizedKeys.CREDIT_APP_MODAL_TYPE_NEW,
        MODAL_NAME_TITLE: localizedKeys.CREDIT_APP_MODAL_NAME_TITLE,
        MODAL_NAME_SUBTITLE: localizedKeys.CREDIT_APP_MODAL_NAME_SUBTITLE,
        MODAL_NAME_CANCEL: localizedKeys.CREDIT_APP_MODAL_NAME_CANCEL,
        MODAL_NAME_CHANGE: localizedKeys.CREDIT_APP_MODAL_NAME_CHANGE,
        MODAL_NAME_CORRECT: localizedKeys.CREDIT_APP_MODAL_NAME_CORRECT,
    };

    const creditAppOtherInformation = {
        TITLE: localizedKeys.CREDIT_APP_OTHER_INFORMATION_TITLE_LABEL,
        ACKNOWLEDGEMENT_TITLE:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_TITLE,
        ACKNOWLEDGEMENT_PRIMARY:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_PRIMARY_APPLICANT,
        ACKNOWLEDGEMENT_JOINT:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_JOINT_APPLICANT,
        ACKNOWLEDGEMENT_COAPP:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_CO_APPLICANT,
        ACKNOWLEDGEMENT_BUSINESS:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_BUSINESS_APPLICANT,
        ACKNOWLEDGEMENT_PG:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_PERSONAL_GUARANTOR,
        ACKNOWLEDGEMENT_INSTRUCTIONS:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_INSTRUCTION,
        ACKNOWLEDGEMENT_PIPEDA_STATEMENT:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_PIPEDA_STATEMENT,
        ACKNOWLEDGEMENT_PIPEDA_VW_CLIENT:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_PIPEDA_VW_CLIENT,
        ACKNOWLEDGEMENT_PIPEDA_AUDI_CLIENT:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_PIPEDA_AUDI_CLIENT,
        ACKNOWLEDGEMENT_PIPEDA_AUDI:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_PIPEDA_AUDI,
        ACKNOWLEDGEMENT_PIPEDA_VW:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_PIPEDA_VW,
        ACKNOWLEDGEMENT_CONTACT_MESSAGE:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_CONTACT_MESSAGE,
        ACKNOWLEDGEMENT_CONTACT_AUDI:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_CONTACT_AUDI,
        ACKNOWLEDGEMENT_CONTACT_VW:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_CONTACT_VW,
        ACKNOWLEDGEMENT_QUEBEC_CONTRACT:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_QUEBEC_CONTRACT,
        ACKNOWLEDGEMENT_QUEBEC_ASSESSMENT:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_ACKNOWLEDGEMENT_QUEBEC_ASSESSMENT,
        NOTES_LABEL: localizedKeys.CREDIT_APP_OTHER_INFORMATION_NOTES_LABEL,
        NOTES_INFO: localizedKeys.CREDIT_APP_OTHER_INFORMATION_NOTES_INFO,
        NOTES_TOOLTIP: localizedKeys.CREDIT_APP_OTHER_INFORMATION_NOTES_TOOLTIP,
        FOOTER_FIRST: localizedKeys.CREDIT_APP_OTHER_INFORMATION_FOOTER_FIRST,
        FOOTER_LINK: localizedKeys.CREDIT_APP_OTHER_INFORMATION_FOOTER_LINK,
        FOOTER_PRIVACY_LINK:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_FOOTER_PRIVACY_LINK,
        FOOTER_AND: localizedKeys.CREDIT_APP_OTHER_INFORMATION_FOOTER_AND,
        FOOTER_LAST: localizedKeys.CREDIT_APP_OTHER_INFORMATION_FOOTER_LAST,
    };

    const creditAppPrintInstructions = {
        FIRST_STEP_HEADER:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_PRINT_FIRST_STEP_HEADER,
        FIRST_STEP_DESCRIPTION:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_PRINT_FIRST_STEP_DESCRIPTION,
        NEXT_STEP_HEADER:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_PRINT_NEXT_STEP_HEADER,
        NEXT_STEP_DESCRIPTION:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_PRINT_NEXT_STEP_DESCRIPTION,
        FINAL_STEP_HEADER:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_PRINT_FINAL_STEP_HEADER,
        FINAL_STEP_DESCRIPTION:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_PRINT_FINAL_STEP_DESCRIPTION,
        FINAL_STEP_ACK_STATEMENT:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_PRINT_FINAL_STEP_ACK_STATEMENT,
        FINAL_STEP_SUB_HEADER:
            localizedKeys.CREDIT_APP_OTHER_INFORMATION_PRINT_FINAL_STEP_SUB_HEADER,
    };

    const occupationIndividualInformation = {
        TITLE: localizedKeys.OCCUPATION_TITLE_LABEL,
        INCOME: localizedKeys.OCCUPATION_INCOME_LABEL,
        EMPLOYMENT_TYPE: localizedKeys.OCCUPATION_TITLE_LABEL_TYPE,
        TYPE_OF_INCOME: localizedKeys.OCCUPATION_TYPE_INCOME_LABEL,
        EMPLOYED: localizedKeys.OCCUPATION_EMPLOYED_LABEL,
        OTHER_INCOME: localizedKeys.OCCUPATION_OTHER_INCOME_LABEL,
        FEQUENCY: localizedKeys.OCCUPATION_FREQUENCY_LABEL,
        INCOME_MONTHLY: localizedKeys.OCCUPATION_FREQUENCY_INCOME_MONTHLY_LABEL,
        INCOME_HOURLY: localizedKeys.OCCUPATION_FREQUENCY_INCOME_HOURLY_LABEL,
        INCOME_WEEKLY: localizedKeys.OCCUPATION_FREQUENCY_INCOME_WEEKLY_LABEL,
        INCOME_EVERY_2_WEEKS:
            localizedKeys.OCCUPATION_FREQUENCY_INCOME_EVERY_2_LABEL,
        INCOME_BI_MONTHLY:
            localizedKeys.OCCUPATION_FREQUENCY_INCOME_BI_MONTH_LABEL,
        INCOME_ANNUAL: localizedKeys.OCCUPATION_FREQUENCY_INCOME_ANNUAL_LABEL,
        GROSS_AMOUNT: localizedKeys.OCCUPATION_GROSS_LABEL,
        ADD_INCOME: localizedKeys.OCCUPATION_ADD_INCOME_LABEL,
        ADD_OTHER_INCOME: localizedKeys.OCCUPATION_ADD_OTHER_INCOME_LABEL,
        DEL_INCOME: localizedKeys.OCCUPATION_DELETE_INCOME_LABEL,
        DEL_OTHER_INCOME: localizedKeys.OCCUPATION_DELETE_OTHER_INCOME_LABEL,
        EMPLOY_INFO: localizedKeys.OCCUPATION_EMPLOY_INFO_LABEL,
        SELF_EMPLOYED: localizedKeys.OCCUPATION_SELF_EMPLOY_LABEL,
        RETIRED: localizedKeys.OCCUPATION_RETIRED_LABEL,
        PROVINCES_AB: localizedKeys.OCCUPATION_PROVINCES_AB_LABEL,
        PROVINCES_BC: localizedKeys.OCCUPATION_PROVINCES_BC_LABEL,
        PROVINCES_MB: localizedKeys.OCCUPATION_PROVINCES_MB_LABEL,
        PROVINCES_NB: localizedKeys.OCCUPATION_PROVINCES_NB_LABEL,
        PROVINCES_NL: localizedKeys.OCCUPATION_PROVINCES_NL_LABEL,
        PROVINCES_NS: localizedKeys.OCCUPATION_PROVINCES_NS_LABEL,
        PROVINCES_NT: localizedKeys.OCCUPATION_PROVINCES_NT_LABEL,
        PROVINCES_NU: localizedKeys.OCCUPATION_PROVINCES_NU_LABEL,
        PROVINCES_ON: localizedKeys.OCCUPATION_PROVINCES_ON_LABEL,
        PROVINCES_PE: localizedKeys.OCCUPATION_PROVINCES_PE_LABEL,
        PROVINCES_QC: localizedKeys.OCCUPATION_PROVINCES_QC_LABEL,
        PROVINCES_SK: localizedKeys.OCCUPATION_PROVINCES_SK_LABEL,
        PROVINCES_YT: localizedKeys.OCCUPATION_PROVINCES_YT_LABEL,
        CURRENT_COMPANY: localizedKeys.OCCUPATION_CURRENT_COMPANY_LABEL,
        CURRENT_EMPLOYER: localizedKeys.OCCUPATION_CURRENT_EMPLOYER_LABEL,
        PREVIOUS_EMPLOYER: localizedKeys.OCCUPATION_PREVIOUS_EMPLOYER_LABEL,
        PREVIOUS_EMPLOYER_DESC1:
            localizedKeys.OCCUPATION_PREVIOUS_EMPLOYER_DESC1,
        PREVIOUS_EMPLOYER_DESC2:
            localizedKeys.OCCUPATION_PREVIOUS_EMPLOYER_DESC2,
        COMPANY: localizedKeys.OCCUPATION_COMPANY_LABEL,
        EMPLOYER: localizedKeys.OCCUPATION_EMPLOYER_LABEL,
        ADDRESS: localizedKeys.OCCUPATION_ADDRESS_LABEL,
        ADDRESS_ERR: localizedKeys.OCCUPATION_ADDRESS_ERR_LABEL,
        CITY: localizedKeys.OCCUPATION_CITY_LABEL,
        CITY_ERR: localizedKeys.OCCUPATION_CITY_ERR_LABEL,
        ZIP: localizedKeys.OCCUPATION_ZIP_LABEL,
        ZIP_ERR: localizedKeys.OCCUPATION_ZIP_ERR_LABEL,
        POSTAL: localizedKeys.OCCUPATION_POSTAL_LABEL,
        POSTAL_ERR: localizedKeys.OCCUPATION_POSTAL_ERR_LABEL,
        OCCUPATION: localizedKeys.OCCUPATION_OCCUPATION_LABEL,
        OCCUPATION_ERR: localizedKeys.OCCUPATION_OCCUPATION_ERR_LABEL,
        YEARS: localizedKeys.OCCUPATION_YEARS_LABEL,
        YEARS_ERR: localizedKeys.OCCUPATION_YEARS_ERR_LABEL,
        MONTHS: localizedKeys.OCCUPATION_MONTHS_LABEL,
        MONTHS_ERR: localizedKeys.OCCUPATION_MONTHS_ERR_LABEL,
        PRIMARY_APP: localizedKeys.OCCUPATION_PRIMARY_APP_LABEL,
        CO_APP: localizedKeys.OCCUPATION_CO_APP_LABEL,
        JOINT_APP: localizedKeys.OCCUPATION_JOINT_APP_LABEL,
        VALID_AMOUNT_ERR: localizedKeys.OCCUPATION_VALID_AMOUNT_ERR_LABEL,
        VALID_REQUIRED_ERR: localizedKeys.OCCUPATION_VALID_REQUIRED_ERR_LABEL,
        COUNTRY: localizedKeys.OCCUPATION_COUNTRY_LABEL,
        STATE: localizedKeys.OCCUPATION_STATE_LABEL,
        PROVINCE: localizedKeys.OCCUPATION_PROVINCE_LABEL,
        OCCUPATION_INCOME_LABEL:
            localizedKeys.OCCUPATION_OCCUPATION_INCOME_LABEL,
    };

    const passwordReset = {
        TITLE: localizedKeys.PASSWORD_RESET_TITLE,
        DESCRIPTION: localizedKeys.PASSWORD_RESET_DESCRIPTION,
        LABEL_PASSWORD: localizedKeys.PASSWORD_RESET_LABEL_PASSWORD,
        LABEL_CONFIRM_PASSWORD:
            localizedKeys.PASSWORD_RESET_LABEL_CONFIRM_PASSWORD,
        LABEL_INVALID_PASSWORD:
            localizedKeys.PASSWORD_RESET_LABEL_INVALID_PASSWORD,
        LABEL_WRONG_PASSWORD: localizedKeys.PASSWORD_RESET_LABEL_WRONG_PASSWORD,
        LABEL_RULES_PASSWORD: localizedKeys.PASSWORD_RESET_LABEL_RULE_PASSWORD,
        LABEL_BACK_BUTTON: localizedKeys.PASSWORD_RESET_LABEL_BACK_LOGIN,
        SUCCESS_INFO: localizedKeys.PASSWORD_RESET_SUCCESS_INFO,
        TOKEN_ERROR: localizedKeys.PASSWORD_RESET_TOKEN_ERROR,
        TOKEN_LINK: localizedKeys.PASSWORD_RESET_TOKEN_ERROR_LINK,
        TOKEN_ERROR2: localizedKeys.PASSWORD_RESET_TOKEN_ERROR2,
    };

    const forgotPassword = {
        TITLE: localizedKeys.FORGOT_PASSWORD_TITLE,
        DESCRIPTION: localizedKeys.FORGOT_PASSWORD_DESCRIPTION,
        LABEL_EMAIL: localizedKeys.FORGOT_PASSWORD_LABEL_EMAIL,
        SUCCESS_INFO: localizedKeys.FORGOT_PASSWORD_SUCCESS_INFO,
        SUCCESS_INFO2: localizedKeys.FORGOT_PASSWORD_SUCCESS_INFO2,
    };

    const financeSection = {
        AMOUNT_MINIMUN_ERROR: localizedKeys.CREDIT_APP_AMOUNT_MINIMUN_ERROR,
        CASH_PRICE_ERROR: localizedKeys.CREDIT_APP_FINANCE_CASH_PRICE_ERROR,
        CASH_PRICE_LABEL: localizedKeys.CREDIT_APP_FINANCE_CASH_PRICE_LABEL,
        CASH_PRICE_TOOLTIP: localizedKeys.CREDIT_APP_FINANCE_CASH_PRICE_TOOLTIP,
        DOWN_PAYMENT_ERROR: localizedKeys.CREDIT_APP_FINANCE_DOWN_PAYMENT_ERROR,
        DOWN_PAYMENT_LABEL: localizedKeys.CREDIT_APP_FINANCE_DOWN_PAYMENT_LABEL,
        DOWN_PAYMENT_TOOLTIP:
            localizedKeys.CREDIT_APP_FINANCE_DOWN_PAYMENT_TOOLTIP,
        GROSS_CAP_ERROR: localizedKeys.CREDIT_APP_FINANCE_GROSS_CAP_ERROR,
        GROSS_CAP_LABEL: localizedKeys.CREDIT_APP_FINANCE_GROSS_CAP_LABEL,
        MONTHLY_PAY_LABEL: localizedKeys.CREDIT_APP_FINANCE_MONTHLY_PAY_LABEL,
        MONTHLY_PAY_TOOLTIP:
            localizedKeys.CREDIT_APP_FINANCE_MONTHLY_PAY_TOOLTIP,
        MSRP_ERROR: localizedKeys.CREDIT_APP_FINANCE_MSRP_ERROR,
        MSRP_LABEL: localizedKeys.CREDIT_APP_FINANCE_MSRP_LABEL,
        NET_CAP_ERROR: localizedKeys.CREDIT_APP_FINANCE_NET_CAP_ERROR,
        NET_CAP_PRICE_LABEL:
            localizedKeys.CREDIT_APP_FINANCE_NET_CAP_PRICE_LABEL,
        NET_CAP_TOOLTIP: localizedKeys.CREDIT_APP_FINANCE_NET_CAP_TOOLTIP,
        SPECIAL_PROGRAM_ITEM_BROKER:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_BROKER,
        SPECIAL_PROGRAM_ITEM_COLLEGE_GRAD:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_COLLEGE_GRAD,
        SPECIAL_PROGRAM_ITEM_DEF_PAYMENT:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_DEF_PAYMENT,
        SPECIAL_PROGRAM_ITEM_FIRST_TIME:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_FIRST_TIME,
        SPECIAL_PROGRAM_ITEM_FOREIGN_BUS:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_FOREIGN_BUS,
        SPECIAL_PROGRAM_ITEM_FOREIGN_STUDENT:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_FOREIGN_STUDENT,
        SPECIAL_PROGRAM_ITEM_LEASE_TRANSFER:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_LEASE_TRANSFER,
        SPECIAL_PROGRAM_ITEM_MULTI_SEC_DEPOSIT:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_MULTI_SEC_DEPOSIT,
        SPECIAL_PROGRAM_ITEM_PRE_PAY_LEASE:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_PRE_PAY_LEASE,
        SPECIAL_PROGRAM_ITEM_RATE_PROTECTION:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_RATE_PROTECTION,
        SPECIAL_PROGRAM_ITEM_VGCA_EMPLOYEE:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_VGCA_EMPLOYEE,
        SPECIAL_PROGRAM_ITEM_FOREIGN_WORKER:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_FOREIGN_WORKER,
        SPECIAL_PROGRAM_LABEL:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_LABEL,
        SPECIAL_PROGRAM_LABEL_2:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_LABEL_2,
        SPECIAL_PROGRAM_TOOLTIP:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_TOOLTIP,
        TERM_ERROR: localizedKeys.CREDIT_APP_FINANCE_TERM_ERROR,
        TERM_LABEL: localizedKeys.CREDIT_APP_FINANCE_TERM_LABEL,
        TERM_TOOLTIP: localizedKeys.CREDIT_APP_FINANCE_TERM_TOOLTIP,
        TITLE: localizedKeys.CREDIT_APP_FINANCE_TITLE,
        TYPE_LABEL: localizedKeys.CREDIT_APP_FINANCE_TYPE_LABEL,
        TYPE_TOOLTIP: localizedKeys.CREDIT_APP_FINANCE_TYPE_TOOLTIP,
        TRADE_IN_AMOUNT_PAYMENT_ERROR:
            localizedKeys.CREDIT_APP_TRADE_IN_AMOUNT_PAYMENT_ERROR,
        TRADE_IN_AMOUNT_PAYMENT_ERROR1:
            localizedKeys.CREDIT_APP_TRADE_IN_AMOUNT_PAYMENT_ERROR1,
        TRADE_IN_AMOUNT_PAYMENT_LABEL:
            localizedKeys.CREDIT_APP_TRADE_IN_AMOUNT_PAYMENT_LABEL,
        TRADE_IN_AMOUNT_PAYMENT_TOOLTIP:
            localizedKeys.CREDIT_APP_TRADE_IN_AMOUNT_PAYMENT_TOOLTIP,
        TRADE_IN_BODY_LABEL: localizedKeys.CREDIT_APP_TRADE_IN_BODY_LABEL,
        TRADE_IN_MAKE_LABEL: localizedKeys.CREDIT_APP_TRADE_IN_MAKE_LABEL,
        TRADE_IN_MODEL_LABEL: localizedKeys.CREDIT_APP_TRADE_IN_MODEL_LABEL,
        TRADE_IN_YEAR_ERROR: localizedKeys.CREDIT_APP_TRADE_IN_YEAR_ERROR,
        TRADE_IN_YEAR_LABEL: localizedKeys.CREDIT_APP_TRADE_IN_YEAR_LABEL,
        ERROR_REQUIRED: localizedKeys.LABEL_FIELD_ERROR_REQUIRED,
        OPTION_LEASE: localizedKeys.TYPE_OPTION_LEASE,
        OPTION_LOAN: localizedKeys.TYPE_OPTION_LOAN,
        OPTION_VWSELECT: localizedKeys.TYPE_OPTION_VWSELECT,
        OPTION_DRIVER: localizedKeys.TYPE_OPTION_DRIVER,
        SPECIAL_PROGRAM_ITEM_AMBASSADOR_PROGRAM:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_AMBASSADOR_PROGRAM,
        SPECIAL_PROGRAM_ITEM_PERMANENT_RESIDENT:
            localizedKeys.CREDIT_APP_FINANCE_SPECIAL_PROGRAM_ITEM_PERMANENT_RESIDENT,
    };

    const businessComercial = {
        TITLE: localizedKeys.CREDIT_APP_BUSINESS_TITLE,
        APPLICANT_TITLE: localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_TITLE,
        OPTION_CORPORATION:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_OPTION_CORPORATION,
        OPTION_PARTNERSHIP:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_OPTION_PARTNERSHIP,
        OPTION_PROPRIERTORSHIP:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_OPTION_PROPRIERTORSHIP,
        OPTION_GOVERMENT:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_OPTION_GOVERMENT,
        OPTION_MUNICIPALITY:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_OPTION_MUNICIPALITY,
        OPTION_NONPROFIT:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_OPTION_NONPROFIT,
        OPTION_LLC: localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_OPTION_LLC,
        OPTION_SCORPORATION:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_OPTION_SCORPORATION,
        OPTION_SOLEPROP:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_OPTION_SOLEPROP,
        OPTION_OTHER: localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_OPTION_OTHER,
        APPLICANT_INFO: localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_INFO,
        APPLICANT_INFOUSA: localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_INFO_USA,
        APPLICANT_NAME: localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_NAME,
        APPLICANT_TYPE: localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_TYPE,
        APPLICANT_INCORPORATION:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_INCORPORATION,
        APPLICANT_FEDERAL: localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_FEDERAL,
        APPLICANT_PROVINCE:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_PROVINCE,
        APPLICANT_CHOOSEPROVINCE:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_CHOOSEPROVINCE,
        APPLICANT_YEARS: localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_YEARS,
        APPLICANT_MONTHS: localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_MONTHS,
        APPLICANT_TELEPHONE:
            localizedKeys.CREDIT_APP_BUSINESS_APPLICANT_TELEPHONE,
        BUSINESS_TITLE: localizedKeys.CREDIT_APP_BUSINESS_BUSINESS_TITLE,
        BUSINESS_ADDRESS1: localizedKeys.CREDIT_APP_BUSINESS_BUSINESS_ADDRESS1,
        BUSINESS_ADDRESS2: localizedKeys.CREDIT_APP_BUSINESS_BUSINESS_ADDRESS2,
        BUSINESS_CITY: localizedKeys.CREDIT_APP_BUSINESS_BUSINESS_CITY,
        BUSINESS_PROVINCE: localizedKeys.CREDIT_APP_BUSINESS_BUSINESS_PROVINCE,
        BUSINESS_POSTALCODE:
            localizedKeys.CREDIT_APP_BUSINESS_BUSINESS_POSTALCODE,
        OWNERS_TITLE: localizedKeys.CREDIT_APP_BUSINESS_OWNERS_TITLE,
        OWNERS_NAME: localizedKeys.CREDIT_APP_BUSINESS_OWNERS_NAME,
        OWNERS_MI: localizedKeys.CREDIT_APP_BUSINESS_OWNERS_MI,
        OWNERS_LASTNAME: localizedKeys.CREDIT_APP_BUSINESS_OWNERS_LASTNAME,
        OWNERS_TITLELABEL: localizedKeys.CREDIT_APP_BUSINESS_OWNERS_TITLELABEL,
        OWNERS_CHOOSETITLE:
            localizedKeys.CREDIT_APP_BUSINESS_OWNERS_CHOOSETITLE,
        OWNERS_OWNERSHIP: localizedKeys.CREDIT_APP_BUSINESS_OWNERS_OWNERSHIP,
        RESPONSIBLE_TITLE: localizedKeys.CREDIT_APP_BUSINESS_RESPONSIBLE_TITLE,
        RESPONSIBLE_NAME: localizedKeys.CREDIT_APP_BUSINESS_RESPONSIBLE_NAME,
        RESPONSIBLE_MI: localizedKeys.CREDIT_APP_BUSINESS_RESPONSIBLE_MI,
        RESPONSIBLE_LASTNAME:
            localizedKeys.CREDIT_APP_BUSINESS_RESPONSIBLE_LASTNAME,
        RESPONSIBLE_TELEPHONE:
            localizedKeys.CREDIT_APP_BUSINESS_RESPONSIBLE_TELEPHONE,
        DRIVER_TITLE: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_TITLE,
        DRIVER_INFO: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_INFO,
        DRIVER_RELATIONSHIP:
            localizedKeys.CREDIT_APP_BUSINESS_DRIVER_RELATIONSHIP,
        DRIVER_OPTION_OWNER:
            localizedKeys.CREDIT_APP_BUSINESS_DRIVER_OPTION_OWNER,
        DRIVER_OPTION_PRESIDENT:
            localizedKeys.CREDIT_APP_BUSINESS_DRIVER_OPTION_PRESIDENT,
        DRIVER_OPTION_VICEPRESIDENT:
            localizedKeys.CREDIT_APP_BUSINESS_DRIVER_OPTION_VICEPRESIDENT,
        DRIVER_OPTION_TREASURER:
            localizedKeys.CREDIT_APP_BUSINESS_DRIVER_OPTION_TREASURER,
        DRIVER_OPTION_OTHER:
            localizedKeys.CREDIT_APP_BUSINESS_DRIVER_OPTION_OTHER,
        DRIVER_NAME: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_NAME,
        DRIVER_MI: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_MI,
        DRIVER_LASTNAME: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_LASTNAME,
        DRIVER_CITY: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_CITY,
        DRIVER_PROVINCE: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_PROVINCE,
        DRIVER_POSTALCODE: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_POSTALCODE,
        DRIVER_YEARS: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_YEARS,
        DRIVER_MONTHS: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_MONTHS,
        DRIVER_LICENSE: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_LICENSE,
        DRIVER_DATE: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_DATE,
        DRIVER_BIRTHDATE: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_BIRTHDATE,
        DRIVER_TELEPHONE: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_TELEPHONE,
        DRIVER_ADDRESS1: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_ADDRESS1,
        DRIVER_ADDRESS2: localizedKeys.CREDIT_APP_BUSINESS_DRIVER_ADDRESS2,
        REQUIRED_FIELD: localizedKeys.REQUIRED_FIELD,
        INVALID_FIELD: localizedKeys.INVALID_FIELD,
        REFERENCE_TITLE: localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_TITLE,
        REFERENCE_PREVIOUSCAR:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_PREVIOUSCAR,
        REFERENCE_ADDREFERENCE:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_ADDREFERENCE,
        REFERENCE_REMOVEREFERENCE:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_REMOVEREFERENCE,
        REFERENCE_BANKNAME:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_BANKNAME,
        REFERENCE_TYPE: localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_TYPE,
        REFERENCE_TYPES_CHECKING:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_TYPES_CHECKING,
        REFERENCE_TYPES_SAVINGS:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_TYPES_SAVINGS,
        REFERENCE_TYPES_LINE:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_TYPES_LINE,
        REFERENCE_BRANCH: localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_BRANCH,
        REFERENCE_ADDRESS1:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_ADDRESS1,
        REFERENCE_ADDRESS2:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_ADDRESS2,
        REFERENCE_CITY: localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_CITY,
        REFERENCE_PROVINCE:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_PROVINCE,
        REFERENCE_POSTAL: localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_POSTAL,
        REFERENCE_TELEPHONE:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_TELEPHONE,
        REFERENCE_ACCOUNTNO:
            localizedKeys.CREDIT_APP_BUSINESS_REFERENCE_ACCOUNTNO,
        REQUIRED_OR_INVALID:
            localizedKeys.CREDIT_APP_BUSINESS_REQUIRED_OR_INVALID,
        CO_APP_INFO: localizedKeys.CREDIT_APP_BUSINESS_CO_APP_INFO,
        GUARANTOR_APP_INFO:
            localizedKeys.CREDIT_APP_BUSINESS_GUARANTOR_APP_INFO,
        CO_APP_INCOME: localizedKeys.CREDIT_APP_BUSINESS_CO_APP_INCOME,
        GUARANTOR_APP_INCOME:
            localizedKeys.CREDIT_APP_BUSINESS_GUARANTOR_APP_INCOME,
        RENT_MORTAGE: localizedKeys.CREDIT_APP_BUSINESS_RENT_MORTAGE,
    };

    const creditSearch = {
        SEARCH_BAR_PLACEHOLDER: localizedKeys.CREDIT_SEARCH_PLACEHOLDER_INPUT,
        SEARCH_BAR_TOOLTIP: localizedKeys.CREDIT_SEARCH_TOOLTIP_INPUT,
        SEARCH_BAR_TOOLTIP_CA: localizedKeys.CREDIT_SEARCH_TOOLTIP_INPUT_CA,
        HEADER_LABEL: localizedKeys.CREDIT_SEARCH_HEADER_LABEL,
        TABLE_OPTION_MONTHS: localizedKeys.CREDIT_SEARCH_TABLE_OPTION_MONTHS,
        TABLE_OPTION_DATE_PLACEHOLDER:
            localizedKeys.CREDIT_SEARCH_TABLE_OPTION_DATE_PLACEHOLDER,
        TABLE_OPTION_DATE_PREFIX:
            localizedKeys.CREDIT_SEARCH_TABLE_OPTION_DATE_PREFIX,
        TABLE_OPTION_STATUS_ALL:
            localizedKeys.CREDIT_SEARCH_TABLE_OPTION_STATUS_ALL,
        TABLE_HEADER_APPLICANT_NAME:
            localizedKeys.CREDIT_SEARCH_TABLE_HEADER_APPLICANT_NAME,
        TABLE_HEADER_APPLICATION_NUMBER:
            localizedKeys.CREDIT_SEARCH_TABLE_HEADER_APPLICATION_NUMBER,
        TABLE_HEADER_STATUS: localizedKeys.CREDIT_SEARCH_TABLE_HEADER_STATUS,
        TABLE_HEADER_COLLATERAL:
            localizedKeys.CREDIT_SEARCH_TABLE_HEADER_COLLATERAL,
        TABLE_HEADER_SUBMITTED_BY:
            localizedKeys.CREDIT_SEARCH_TABLE_HEADER_SUBMITTED_DATE,
        TABLE_HEADER_SUBMITTED_DATE:
            localizedKeys.CREDIT_SEARCH_TABLE_HEADER_SUBMITTED,
        APPROVED: statusSummary.LABEL_STATUS_SUMMARY_ITEM_APPROVED,
        PENDING: statusSummary.LABEL_STATUS_SUMMARY_ITEM_PENDING,
        CONDITIONED: statusSummary.LABEL_STATUS_SUMMARY_ITEM_CONDITIONED,
        DECLINED: statusSummary.LABEL_STATUS_SUMMARY_ITEM_DECLINED,
        WITHDRAWN: statusSummary.LABEL_STATUS_SUMMARY_ITEM_WITHDRAWN,
    };

    const creditStatusFilterOptionMap = {
        [creditSearch.PENDING]: CreditSearchStatusOptionsState.Pending,
        [creditSearch.CONDITIONED]: CreditSearchStatusOptionsState.Conditioned,
        [creditSearch.DECLINED]: CreditSearchStatusOptionsState.Declined,
        [creditSearch.WITHDRAWN]: CreditSearchStatusOptionsState.Withdrawn,
        [creditSearch.APPROVED]: CreditSearchStatusOptionsState.Approved,
        [creditSearch.TABLE_OPTION_STATUS_ALL]:
            CreditSearchStatusOptionsState.AllStatutes,
    };

    const creditStatusFilterOptionToResponseMap = {
        [creditSearch.PENDING]: CreditApplicationStatus.PENDING,
        [creditSearch.CONDITIONED]: CreditApplicationStatus.CONDITIONED,
        [creditSearch.DECLINED]: CreditApplicationStatus.DECLINED,
        [creditSearch.WITHDRAWN]: CreditApplicationStatus.WITHDRAWN,
        [creditSearch.APPROVED]: CreditApplicationStatus.APPROVED,
    };

    const creditStatusFilterResponseToOptionMap = {
        [CreditApplicationStatus.CONDITIONED]: creditSearch.CONDITIONED,
        [CreditApplicationStatus.PENDING]: creditSearch.PENDING,
        [CreditApplicationStatus.DECLINED]: creditSearch.DECLINED,
        [CreditApplicationStatus.WITHDRAWN]: creditSearch.WITHDRAWN,
        [CreditApplicationStatus.APPROVED]: creditSearch.APPROVED,
        [CreditApplicationStatus.BOOKED]: creditSearch.PENDING,
        [CreditApplicationStatus.OTHERTYPE]: creditSearch.DECLINED,
    };

    const creditStatusDetails = {
        HEADER_LABEL: localizedKeys.CREDIT_DETAILS_HEADER_LABEL,
        APPLICANT_TITLE: localizedKeys.CREDIT_DETAILS_APPLICANT_TITLE,
        JOINT_APPLICANT_TITLE:
            localizedKeys.CREDIT_DETAILS_APPLICANT_TITLE_JOINT,
        CO_APPLICANT_TITLE: localizedKeys.CREDIT_DETAILS_APPLICANT_TITLE_COAPP,
        GUARANTOR_APPLICANT_TITLE:
            localizedKeys.CREDIT_DETAILS_APPLICANT_TITLE_GUARANTOR,
        APPLICANT_LABEL_NAME: localizedKeys.CREDIT_DETAILS_APPLICANT_LABEL_NAME,
        APPLICANT_LABEL_ADDRESS:
            localizedKeys.CREDIT_DETAILS_APPLICANT_LABEL_ADDRESS,
        APPLICANT_LABEL_HOME: localizedKeys.CREDIT_DETAILS_APPLICANT_LABEL_HOME,
        APPLICANT_LABEL_MOBILE:
            localizedKeys.CREDIT_DETAILS_APPLICANT_LABEL_MOBILE,
        APPLICANT_LABEL_SSN: localizedKeys.CREDIT_DETAILS_APPLICANT_LABEL_SSN,
        APPLICANT_LABEL_APPLICATION_NUMBER:
            localizedKeys.CREDIT_DETAILS_APPLICANT_LABEL_APPLICATION_NUMBER,
        COLLATERAL_LABEL_TYPE:
            localizedKeys.CREDIT_DETAILS_COLLATERAL_LABEL_TYPE,
        COLLATERAL_LABEL_VEHICLE_TYPE:
            localizedKeys.CREDIT_DETAILS_COLLATERAL_LABEL_VEHICLE_TYPE,
        COLLATERAL_LABEL_AMOUNT_FINANCED:
            localizedKeys.CREDIT_DETAILS_COLLATERAL_LABEL_AMOUNT_FINANCED,
        COLLATERAL_LABEL_ADJUSTED_CAP_COST:
            localizedKeys.CREDIT_DETAILS_COLLATERAL_LABEL_ADJUSTED_CAP_COST,
        COLLATERAL_LABEL_TERM:
            localizedKeys.CREDIT_DETAILS_COLLATERAL_LABEL_TERM,
        COLLATERAL_LABEL_TIER:
            localizedKeys.CREDIT_DETAILS_COLLATERAL_LABEL_TIER,
        COLLATERAL_LABEL_ESTIMATED:
            localizedKeys.CREDIT_DETAILS_COLLATERAL_LABEL_ESTIMATED,
        COLLATERAL_LABEL_LTV: localizedKeys.CREDIT_DETAILS_COLLATERAL_LABEL_LTV,
        COLLATERAL_LABEL_VALUE:
            localizedKeys.CREDIT_DETAILS_COLLATERAL_LABEL_VALUE,
        DETAIL_TITLE: localizedKeys.CREDIT_DETAILS_DETAIL_TITLE,
        DETAIL_LABEL_DEALER: localizedKeys.CREDIT_DETAILS_DETAIL_LABEL_DEALER,
        DETAIL_LABEL_PHONE: localizedKeys.CREDIT_DETAILS_DETAIL_LABEL_PHONE,
        DETAIL_LABEL_CREDIT_AUTHORIZER:
            localizedKeys.CREDIT_DETAILS_DETAIL_LABEL_CREDIT_AUTHORIZER,
        DETAIL_LABEL_CURRENT_STATUS:
            localizedKeys.CREDIT_DETAILS_DETAIL_LABEL_CURRENT_STATUS,
        DETAIL_LABEL_COMMENTS:
            localizedKeys.CREDIT_DETAILS_DETAIL_LABEL_COMMENTS,
        DETAIL_LABEL_DECISIONED:
            localizedKeys.CREDIT_DETAILS_DETAIL_LABEL_DECISIONED,
        DETAIL_LABEL_SUBMITTED:
            localizedKeys.CREDIT_DETAILS_DETAIL_LABEL_SUBMITTED,
        DETAIL_LABEL_SUBMITTED_BY:
            localizedKeys.CREDIT_DETAILS_DETAIL_LABEL_SUBMITTED_BY,
        DETAIL_LTV_DISCLAIMER:
            localizedKeys.CREDIT_DETAILS_DETAIL_LTV_DISCLAIMER,
        DETAILS_BUTTONS_EDIT: localizedKeys.CREDIT_DETAILS_BUTTONS_EDIT,
        DETAILS_TOOLTIP: localizedKeys.CREDIT_DETAILS_TOOLTIP,
    };

    const portfolioManagerDetails = {
        HEADER_LABEL: localizedKeys.PORTFOLIO_MANAGER_HEADER_LABEL,
        ERROR: localizedKeys.PORTFOLIO_MANAGER_ERROR,

        CUSTOMER_INFORMATION_HEADER_LABEL:
            localizedKeys.PORTFOLIO_MANAGER_CUSTOMER_INFORMATION_HEADER_LABEL,
        CUSTOMER_INFORMATION_BUYER:
            localizedKeys.PORTFOLIO_MANAGER_CUSTOMER_INFORMATION_BUYER,
        CUSTOMER_INFORMATION_CO_BUYER:
            localizedKeys.PORTFOLIO_MANAGER_CUSTOMER_INFORMATION_CO_BUYER,
        CUSTOMER_INFORMATION_CURRENT_MATURITY_DATE:
            localizedKeys.PORTFOLIO_MANAGER_CUSTOMER_INFORMATION_CURRENT_MATURITY_DATE,
        CUSTOMER_INFORMATION_ORIGINAL_MATURITY_DATE:
            localizedKeys.PORTFOLIO_MANAGER_CUSTOMER_INFORMATION_ORIGINAL_MATURITY_DATE,
        CUSTOMER_INFORMATION_PRIVACY:
            localizedKeys.PORTFOLIO_MANAGER_CUSTOMER_INFORMATION_PRIVACY,
        CUSTOMER_INFORMATION_VIN:
            localizedKeys.PORTFOLIO_MANAGER_CUSTOMER_INFORMATION_VIN,

        CONTACT_INFORMATION_HEADER_LABEL:
            localizedKeys.PORTFOLIO_MANAGER_CONTACT_INFORMATION_HEADER_LABEL,
        CONTACT_INFORMATION_GARAGING_ADDRESS:
            localizedKeys.PORTFOLIO_MANAGER_CONTACT_INFORMATION_GARAGING_ADDRESS,
        CONTACT_INFORMATION_HOME_PHONE:
            localizedKeys.PORTFOLIO_MANAGER_CONTACT_INFORMATION_HOME_PHONE,
        CONTACT_INFORMATION_WORK_PHONE:
            localizedKeys.PORTFOLIO_MANAGER_CONTACT_INFORMATION_WORK_PHONE,
        CONTACT_INFORMATION_CELL_PHONE:
            localizedKeys.PORTFOLIO_MANAGER_CONTACT_INFORMATION_CELL_PHONE,
        CONTACT_INFORMATION_EMAIL:
            localizedKeys.PORTFOLIO_MANAGER_CONTACT_INFORMATION_EMAIL,
        CONTACT_INFORMATION_CO_BUYER_ADDRESS:
            localizedKeys.PORTFOLIO_MANAGER_CONTACT_INFORMATION_CO_BUYER_ADDRESS,
        CONTACT_INFORMATION_CO_BUYER_PHONE:
            localizedKeys.PORTFOLIO_MANAGER_CONTACT_INFORMATION_CO_BUYER_PHONE,
        CONTACT_INFORMATION_CO_BUYER_WORK_PHONE:
            localizedKeys.PORTFOLIO_MANAGER_CONTACT_INFORMATION_CO_BUYER_WORK_PHONE,
        CONTACT_INFORMATION_CO_BUYER_CELL_PHONE:
            localizedKeys.PORTFOLIO_MANAGER_CONTACT_INFORMATION_CO_BUYER_CELL_PHONE,
        CONTACT_INFORMATION_CO_BUYER_EMAIL:
            localizedKeys.PORTFOLIO_MANAGER_CONTACT_INFORMATION_CO_BUYER_EMAIL,

        VEHICLE_INFORMATION_HEADER_LABEL:
            localizedKeys.PORTFOLIO_MANAGER_VEHICLE_INFORMATION_HEADER_LABEL,
        VEHICLE_INFORMATION_YEAR:
            localizedKeys.PORTFOLIO_MANAGER_VEHICLE_INFORMATION_YEAR,
        VEHICLE_INFORMATION_MAKE:
            localizedKeys.PORTFOLIO_MANAGER_VEHICLE_INFORMATION_MAKE,
        VEHICLE_INFORMATION_MODEL:
            localizedKeys.PORTFOLIO_MANAGER_VEHICLE_INFORMATION_MODEL,
        VEHICLE_INFORMATION_MODEL_CODE:
            localizedKeys.PORTFOLIO_MANAGER_VEHICLE_INFORMATION_MODEL_CODE,
        VEHICLE_INFORMATION_TERM:
            localizedKeys.PORTFOLIO_MANAGER_VEHICLE_INFORMATION_TERM,
        VEHICLE_INFORMATION_COLOR:
            localizedKeys.PORTFOLIO_MANAGER_VEHICLE_INFORMATION_COLOR,
        VEHICLE_INFORMATION_DESCRIPTION:
            localizedKeys.PORTFOLIO_MANAGER_VEHICLE_INFORMATION_DESCRIPTION,
        VEHICLE_INFORMATION_OPTIONS:
            localizedKeys.PORTFOLIO_MANAGER_VEHICLE_INFORMATION_OPTIONS,

        ACCOUNT_INFORMATION_HEADER_LABEL:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_HEADER_LABEL,
        ACCOUNT_INFORMATION_ACCOUNT_NUMBER:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_ACCOUNT_NUMBER,
        ACCOUNT_INFORMATION_CONTRACT_DATE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_CONTRACT_DATE,
        ACCOUNT_INFORMATION_BRAND_SP_CODE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_BRAND_SP_CODE,
        ACCOUNT_INFORMATION_ACCOUNT_TYPE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_ACCOUNT_TYPE,
        ACCOUNT_INFORMATION_TOTAL_ACCOUNTS:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_TOTAL_ACCOUNTS,
        ACCOUNT_INFORMATION_BOUGHT_AS:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_BOUGHT_AS,
        ACCOUNT_INFORMATION_ORIGINAL_ODOMETER:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_ORIGINAL_ODOMETER,
        ACCOUNT_INFORMATION_ALLOWABLE_MILEAGE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_ALLOWABLE_MILEAGE,
        ACCOUNT_INFORMATION_CURRENT_RESIDUAL:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_CURRENT_RESIDUAL,
        ACCOUNT_INFORMATION_SECURITY_DEPOSIT:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_SECURITY_DEPOSIT,
        ACCOUNT_INFORMATION_MONEY_FACTOR:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_MONEY_FACTOR,
        ACCOUNT_INFORMATION_BASE_PAYMENT:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_BASE_PAYMENT,
        ACCOUNT_INFORMATION_PAYMENT_FREQUENCY:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_PAYMENT_FREQUENCY,
        ACCOUNT_INFORMATION_APR:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_APR,
        ACCOUNT_INFORMATION_DOWN_PAYMENT:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_DOWN_PAYMENT,
        ACCOUNT_INFORMATION_OUTSTANDING_BALANCE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_OUTSTANDING_BALANCE,
        ACCOUNT_INFORMATION_PURCHASE_OPTION_FEE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_PURCHASE_OPTION_FEE,
        ACCOUNT_INFORMATION_PROGRAM_ELIGIBILITY:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_PROGRAM_ELIGIBILITY,
        ACCOUNT_INFORMATION_ESTIMATED_BB_VALUE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_ESTIMATED_BB_VALUE,
        ACCOUNT_INFORMATION_ESTIMATED_EQUITY:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_ESTIMATED_EQUITY,
        ACCOUNT_INFORMATION_VEHICLE_TURN_IN_DATE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_VEHICLE_TURN_IN_DATE,
        ACCOUNT_INFORMATION_PAYOFF_DATE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_PAYOFF_DATE,
        ACCOUNT_INFORMATION_LEASE_TRANSFER:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_LEASE_TRANSFER,
        ACCOUNT_INFORMATION_QUOTE_TYPE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_QUOTE_TYPE,
        ACCOUNT_INFORMATION_LAST_QUOTE_GENERATED:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_LAST_QUOTE_GENERATED,
        ACCOUNT_INFORMATION_AUDI_CARE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_AUDI_CARE,
        ACCOUNT_INFORMATION_TURN_IN_FEE:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_TURN_IN_FEE,
        ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_MONTHLY:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_MONTHLY,
        ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_BIWEEKLY:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_BIWEEKLY,
        ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_SEMI_MONTHLY:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_SEMI_MONTHLY,
        ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_SINGLE_PAY:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_SINGLE_PAY,
        ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_WEEKLY:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_WEEKLY,
        ACCOUNT_INFORMATION_QUOTE_TYPE_VALUES_CUSTOMER:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_QUOTE_TYPE_VALUES_CUSTOMER,
        ACCOUNT_INFORMATION_QUOTE_TYPE_VALUES_DEALER:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_QUOTE_TYPE_VALUES_DEALER,
        ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_NEW:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_NEW,
        ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_PREOWNED:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_PREOWNED,
        ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_CPO:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_CPO,
        ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_DEMO:
            localizedKeys.PORTFOLIO_MANAGER_ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_DEMO,
    };

    const vehicleTypeFilterResponseToOptionMap = {
        [VehicleType.NEW]: vehicleInformation.TYPE_NEW,
        [VehicleType.CPO]: vehicleInformation.TYPE_CPO,
        [VehicleType.DEMO]: vehicleInformation.TYPE_DEMO,
        [VehicleType.PRE_OWNED]: vehicleInformation.TYPE_PRE,
    };

    const payments = {
        PAYMENT_MAKE_TITLE: localizedKeys.PAYMENT_MAKE_TITLE,
        PAYMENT_MAKE_DESCRIPTION: localizedKeys.PAYMENT_MAKE_DESCRIPTION,
        PAYMENT_MAKE_TITLEFORM: localizedKeys.PAYMENT_MAKE_TITLEFORM,
        PAYMENT_MAKE_PAYMENTBTN: localizedKeys.PAYMENT_MAKE_PAYMENTBTN,
        PAYMENT_MAKE_PAYOFFBTN: localizedKeys.PAYMENT_MAKE_PAYOFFBTN,
        PAYMENT_MAKE_ACCOUNTLABEL: localizedKeys.PAYMENT_MAKE_ACCOUNTLABEL,
        PAYMENT_MAKE_ACCOUNTERROR: localizedKeys.PAYMENT_MAKE_ACCOUNTERROR,
        PAYMENT_MAKE_ACCOUNTTIP: localizedKeys.PAYMENT_MAKE_ACCOUNTTIP,
        PAYMENT_MAKE_VINLABEL: localizedKeys.PAYMENT_MAKE_VINLABEL,
        PAYMENT_MAKE_VINERROR: localizedKeys.PAYMENT_MAKE_VINERROR,
        PAYMENT_MAKE_VINTIP: localizedKeys.PAYMENT_MAKE_VINTIP,
        PAYMENT_MAKE_SUBMIT: localizedKeys.PAYMENT_MAKE_SUBMIT,
        PAYMENT_INFORMATION_TITLE: localizedKeys.PAYMENT_INFORMATION_TITLE,
        PAYMENT_EDIT_INFORMATION_TITLE:
            localizedKeys.PAYMENT_EDIT_INFORMATION_TITLE,
        PAYMENT_INFORMATION_AMOUNTLABEL:
            localizedKeys.PAYMENT_INFORMATION_AMOUNTLABEL,
        PAYMENT_INFORMATION_AMOUNTERR:
            localizedKeys.PAYMENT_INFORMATION_AMOUNTERR,
        PAYMENT_INFORMATION_AMOUNTERRPAYOFF:
            localizedKeys.PAYMENT_INFORMATION_AMOUNTERRPAYOFF,
        PAYMENT_INFORMATION_REASONLABEL:
            localizedKeys.PAYMENT_INFORMATION_REASONLABEL,
        PAYMENT_INFORMATION_REASONERR:
            localizedKeys.PAYMENT_INFORMATION_REASONERR,
        PAYMENT_INFORMATION_TYPELABEL:
            localizedKeys.PAYMENT_INFORMATION_TYPELABEL,
        PAYMENT_INFORMATION_CHECKINGLABEL:
            localizedKeys.PAYMENT_INFORMATION_CHECKINGLABEL,
        PAYMENT_INFORMATION_SAVINGSLABEL:
            localizedKeys.PAYMENT_INFORMATION_SAVINGSLABEL,
        PAYMENT_INFORMATION_DEALERREFLABEL:
            localizedKeys.PAYMENT_INFORMATION_DEALERREFLABEL,
        PAYMENT_INFORMATION_DEALERREFERR:
            localizedKeys.PAYMENT_INFORMATION_DEALERREFERR,
        PAYMENT_INFORMATION_COMMENTLABEL:
            localizedKeys.PAYMENT_INFORMATION_COMMENTLABEL,
        PAYMENT_INFORMATION_COMMENTIP:
            localizedKeys.PAYMENT_INFORMATION_COMMENTIP,
        PAYMENT_INFORMATION_COMMENT1ERR:
            localizedKeys.PAYMENT_INFORMATION_COMMENT1ERR,
        PAYMENT_INFORMATION_COMMENT2ERR:
            localizedKeys.PAYMENT_INFORMATION_COMMENT2ERR,
        PAYMENT_INFORMATION_CANCELBTN:
            localizedKeys.PAYMENT_INFORMATION_CANCELBTN,
        PAYMENT_INFORMATION_SUBMITBTN:
            localizedKeys.PAYMENT_INFORMATION_SUBMITBTN,
        PAYMENT_INFORMATION_SUBMITAPPBTN:
            localizedKeys.PAYMENT_INFORMATION_SUBMITAPPBTN,
        PAYMENT_INFORMATION_CONFIRMBTN:
            localizedKeys.PAYMENT_INFORMATION_CONFIRMBTN,
        PAYMENT_INFORMATION_ACCOUNTINFOLABEL:
            localizedKeys.PAYMENT_INFORMATION_ACCOUNTINFOLABEL,
        PAYMENT_INFORMATION_ACCOUNTINFOTIP:
            localizedKeys.PAYMENT_INFORMATION_ACCOUNTINFOTIP,
        PAYMENT_INFORMATION_ACCOUNTNUMBERLABEL:
            localizedKeys.PAYMENT_INFORMATION_ACCOUNTNUMBERLABEL,
        PAYMENT_INFORMATION_ACCOUNTTYPELABEL:
            localizedKeys.PAYMENT_INFORMATION_ACCOUNTTYPELABEL,
        PAYMENT_INFORMATION_VEHICLEIDLABEL:
            localizedKeys.PAYMENT_INFORMATION_VEHICLEIDLABEL,
        PAYMENT_INFORMATION_VEHICLEDESCLABEL:
            localizedKeys.PAYMENT_INFORMATION_VEHICLEDESCLABEL,
        PAYMENT_INFORMATION_VEHICLEMAKEL:
            localizedKeys.PAYMENT_INFORMATION_VEHICLEMAKEL,
        PAYMENT_INFORMATION_VEHICLEMODEL:
            localizedKeys.PAYMENT_INFORMATION_VEHICLEMODEL,
        PAYMENT_INFORMATION_CUSTOMERNAMELABEL:
            localizedKeys.PAYMENT_INFORMATION_CUSTOMERNAMELABEL,
        PAYMENT_INFORMATION_ADDRESSLABEL:
            localizedKeys.PAYMENT_INFORMATION_ADDRESSLABEL,
        PAYMENT_INFORMATION_LOCATIONLABEL:
            localizedKeys.PAYMENT_INFORMATION_LOCATIONLABEL,
        PAYMENT_INFORMATION_PROVINCELABEL:
            localizedKeys.PAYMENT_INFORMATION_PROVINCELABEL,
        PAYMENT_INFORMATION_DEALERNUMLABEL:
            localizedKeys.PAYMENT_INFORMATION_DEALERNUMLABEL,
        PAYMENT_INFORMATION_OPTIONS_DEFAULT:
            localizedKeys.PAYMENT_INFORMATION_OPTIONS_DEFAULT,
        PAYMENT_INFORMATION_OPTIONS_CURT:
            localizedKeys.PAYMENT_INFORMATION_OPTIONS_CURT,
        PAYMENT_INFORMATION_OPTIONS_REGULAR:
            localizedKeys.PAYMENT_INFORMATION_OPTIONS_REGULAR,
        PAYMENT_INFORMATION_OPTIONS_LEASE:
            localizedKeys.PAYMENT_INFORMATION_OPTIONS_LEASE,
        PAYMENT_INFORMATION_OPTIONS_FLAT:
            localizedKeys.PAYMENT_INFORMATION_OPTIONS_FLAT,
        PAYMENT_INFORMATION_OPTIONS_INVOICE:
            localizedKeys.PAYMENT_INFORMATION_OPTIONS_INVOICE,
        PAYMENT_INFORMATION_OPTIONS_OTHER:
            localizedKeys.PAYMENT_INFORMATION_OPTIONS_OTHER,
        PAYMENT_INFORMATION_OPTIONS_CHARGEBACK:
            localizedKeys.PAYMENT_INFORMATION_OPTIONS_CHARGEBACK,
        PAYMENT_INFORMATION_TRANSACTION_ID:
            localizedKeys.PAYMENT_INFORMATION_TRANSACTION_ID,
        PAYMENT_INFORMATION_WITHDRAWAL_DATE:
            localizedKeys.PAYMENT_INFORMATION_WITHDRAWAL_DATE,
        PAYMENT_INFORMATION_RETURN_LABEL:
            localizedKeys.PAYMENT_INFORMATION_RETURN_LABEL,
        PAYMENT_INFORMATION_EDIT_LABEL:
            localizedKeys.PAYMENT_INFORMATION_EDIT_LABEL,
        PAYMENT_INFORMATION_TABLE_TITLE:
            localizedKeys.PAYMENT_INFORMATION_TABLE_TITLE,
        PAYMENT_INFORMATION_TABLE_TAB_TITLE:
            localizedKeys.PAYMENT_INFORMATION_TABLE_TAB_TITLE,
        PAYMENT_INFORMATION_TABLE_TAB_TITLE1:
            localizedKeys.PAYMENT_INFORMATION_TABLE_TAB_TITLE1,
        PAYMENT_INFORMATION_TABLE_DISCLAIMER:
            localizedKeys.PAYMENT_INFORMATION_TABLE_DISCLAIMER,
        PAYMENT_INFORMATION_TABLE_DISCLAIMER_CONTENT:
            localizedKeys.PAYMENT_INFORMATION_TABLE_DISCLAIMER_CONTENT,
        PAYMENT_INFORMATION_TABLE_HEADERS_TRANSACTION:
            localizedKeys.PAYMENT_INFORMATION_TABLE_HEADERS_TRANSACTION,
        PAYMENT_INFORMATION_TABLE_HEADERS_TYPE:
            localizedKeys.PAYMENT_INFORMATION_TABLE_HEADERS_TYPE,
        PAYMENT_INFORMATION_TABLE_HEADERS_ACCOUNT:
            localizedKeys.PAYMENT_INFORMATION_TABLE_HEADERS_ACCOUNT,
        PAYMENT_INFORMATION_TABLE_HEADERS_VIN:
            localizedKeys.PAYMENT_INFORMATION_TABLE_HEADERS_VIN,
        PAYMENT_INFORMATION_TABLE_HEADERS_AMOUNT:
            localizedKeys.PAYMENT_INFORMATION_TABLE_HEADERS_AMOUNT,
        PAYMENT_INFORMATION_TABLE_HEADERS_METHOD:
            localizedKeys.PAYMENT_INFORMATION_TABLE_HEADERS_METHOD,
        PAYMENT_INFORMATION_TABLE_HEADERS_STATUS:
            localizedKeys.PAYMENT_INFORMATION_TABLE_HEADERS_STATUS,
        PAYMENT_INFORMATION_TABLE_HEADERS_DATE:
            localizedKeys.PAYMENT_INFORMATION_TABLE_HEADERS_DATE,
        PAYMENT_INFORMATION_TAB_PAYMENT:
            localizedKeys.PAYMENT_INFORMATION_TAB_PAYMENT,
        PAYMENT_INFORMATION_TAB_PAYOFF:
            localizedKeys.PAYMENT_INFORMATION_TAB_PAYOFF,
        PAYMENTS_MAKE_LABEL: localizedKeys.PAYMENTS_MAKE_LABEL,
        PAYMENTS_MODEL_LABEL: localizedKeys.PAYMENTS_MODEL_LABEL,
        PAYMENT_INFORMATION_PAIDFROM:
            localizedKeys.PAYMENT_INFORMATION_PAIDFROM,
        PAYMENT_INFORMATION_CHECKING:
            localizedKeys.PAYMENT_INFORMATION_CHECKING,
        PAYMENT_INFORMATION_SAVINGS: localizedKeys.PAYMENT_INFORMATION_SAVINGS,
        PAYMENT_INFORMATION_LINE: localizedKeys.PAYMENT_INFORMATION_LINE,
        PAYMENT_INFORMATION_DETAILS_PEDINGTITLE:
            localizedKeys.PAYMENT_INFORMATION_DETAILS_PEDINGTITLE,
        PAYMENT_INFORMATION_DETAILS_ACCOUNTINFO:
            localizedKeys.PAYMENT_INFORMATION_DETAILS_ACCOUNTINFO,
        PAYMENT_INFORMATION_DETAILS_PAYMENTINFO:
            localizedKeys.PAYMENT_INFORMATION_DETAILS_PAYMENTINFO,
        PAYMENT_INFORMATION_DETAILS_BUTTONS_CLOSE:
            localizedKeys.PAYMENT_INFORMATION_DETAILS_BUTTONS_CLOSE,
        PAYMENT_INFORMATION_DETAILS_BUTTONS_EDIT:
            localizedKeys.PAYMENT_INFORMATION_DETAILS_BUTTONS_EDIT,
        PAYMENT_INFORMATION_DETAILS_BUTTONS_DELETE:
            localizedKeys.PAYMENT_INFORMATION_DETAILS_BUTTONS_DELETE,
        PAYMENT_INFORMATION_DETAILS_BUTTONS_APPROVE:
            localizedKeys.PAYMENT_INFORMATION_DETAILS_BUTTONS_APPROVE,
        PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_TITLE:
            localizedKeys.PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_TITLE,
        PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_CANCEL:
            localizedKeys.PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_CANCEL,
        PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_PROCEED:
            localizedKeys.PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_PROCEED,
        PAYMENT_INFORMATION_COMMENT: localizedKeys.PAYMENT_INFORMATION_COMMENT,
        PAYMENT_INFORMATION_DELETE_CONFIRMATION_TITLE:
            localizedKeys.PAYMENT_INFORMATION_DELETE_CONFIRMATION_TITLE,
        PAYMENT_INFORMATION_CREATE_SUCCESS_MESSAGGE:
            localizedKeys.PAYMENT_INFORMATION_CREATE_SUCCESS_MESSAGGE,
        PAYMENT_INFORMATION_PAYOFF_SUCCESS_MESSAGGE:
            localizedKeys.PAYMENT_INFORMATION_PAYOFF_SUCCESS_MESSAGGE,
        PAYMENT_INFORMATION_CREATE_ERROR_MESSAGE:
            localizedKeys.PAYMENT_INFORMATION_CREATE_ERROR_MESSAGE,
        PAYMENT_INFORMATION_DELETE_SUCCESS_MESSAGGE:
            localizedKeys.PAYMENT_INFORMATION_DELETE_SUCCESS_MESSAGGE,
        PAYMENT_INFORMATION_DELETE_ERROR_MESSAGE:
            localizedKeys.PAYMENT_INFORMATION_DELETE_ERROR_MESSAGE,
        PAYMENT_INFORMATION_APPROVE_CONFIRMATION_TITLE:
            localizedKeys.PAYMENT_INFORMATION_APPROVE_CONFIRMATION_TITLE,
        PAYMENT_INFORMATION_APPROVE_SUCCESS_MESSAGGE:
            localizedKeys.PAYMENT_INFORMATION_APPROVE_SUCCESS_MESSAGGE,
        PAYMENT_INFORMATION_APPROVE_ERROR_MESSAGE:
            localizedKeys.PAYMENT_INFORMATION_APPROVE_ERROR_MESSAGE,
        PAYMENT_DETAILS_FUNDS_ERROR_MESSAGE:
            localizedKeys.PAYMENT_DETAILS_FUNDS_ERROR_MESSAGE,
        PAYMENT_DETAILS_FUNDS_WARNING_MESSAGE:
            localizedKeys.PAYMENT_DETAILS_FUNDS_WARNING_MESSAGE,
        PAYMENT_INFORMATION_AMOUNT_ERR_LIMIT:
            localizedKeys.PAYMENT_INFORMATION_AMOUNT_ERR_LIMIT,
        PAYMENT_INFORMATION_NOTE: localizedKeys.PAYMENT_INFORMATION_NOTE,
    };

    const paymentHistory = {
        TITLE: localizedKeys.PAYMENT_HISTORY_TITLE,
        SEARCH_PANDP: localizedKeys.PAYMENT_HISTORY_SEARCH_PANDP,
        SEARCH_PAYMENT: localizedKeys.PAYMENT_HISTORY_SEARCH_PAYMENT,
        SEARCH_PAYOFF: localizedKeys.PAYMENT_HISTORY_SEARCH_PAYOFF,
        SEARCH_MONTH: localizedKeys.PAYMENT_HISTORY_SEARCH_MONTH,
        SEARCH_2MONTH: localizedKeys.PAYMENT_HISTORY_SEARCH_2MONTH,
        SEARCH_3MONTH: localizedKeys.PAYMENT_HISTORY_SEARCH_3MONTH,
        SEARCH_ALL: localizedKeys.PAYMENT_HISTORY_SEARCH_ALL,
        SEARCH_DELETED: localizedKeys.PAYMENT_HISTORY_SEARCH_DELETED,
        SEARCH_EXPIRED: localizedKeys.PAYMENT_HISTORY_SEARCH_EXPIRED,
        SEARCH_SUBMITTED: localizedKeys.PAYMENT_HISTORY_SEARCH_SUBMITTED,
        SEARCH_RESET: localizedKeys.PAYMENT_HISTORY_SEARCH_RESET,
        TABLE_DATE: localizedKeys.PAYMENT_HISTORY_TABLE_DATE,
        TABLE_TYPE: localizedKeys.PAYMENT_HISTORY_TABLE_TYPE,
        TYPE_LEASE: localizedKeys.PAYMENT_HISTORY_TABLE_TYPE_LEASE,
        TYPE_LOAN: localizedKeys.PAYMENT_HISTORY_TABLE_TYPE_LOAN,
        TYPE_BALLOON: localizedKeys.PAYMENT_HISTORY_TABLE_TYPE_BALLOON,
        TABLE_STATUS: localizedKeys.PAYMENT_HISTORY_TABLE_STATUS,
        STATUS_0: localizedKeys.PAYMENT_HISTORY_TABLE_STATUS_0,
        STATUS_1: localizedKeys.PAYMENT_HISTORY_TABLE_STATUS_1,
        STATUS_2: localizedKeys.PAYMENT_HISTORY_TABLE_STATUS_2,
        STATUS_3: localizedKeys.PAYMENT_HISTORY_TABLE_STATUS_3,
        STATUS_4: localizedKeys.PAYMENT_HISTORY_TABLE_STATUS_4,
        TABLE_ACCOUNT: localizedKeys.PAYMENT_HISTORY_TABLE_ACCOUNT,
        TABLE_AMOUNT: localizedKeys.PAYMENT_HISTORY_TABLE_AMOUNT,
        TABLE_REASON: localizedKeys.PAYMENT_HISTORY_TABLE_REASON,
        REASON_REGULAR: localizedKeys.PAYMENT_HISTORY_TABLE_REASON_REGULAR,
        REASON_CURTAILMENT:
            localizedKeys.PAYMENT_HISTORY_TABLE_REASON_CURTAILMENT,
        REASON_CANCEL: localizedKeys.PAYMENT_HISTORY_TABLE_REASON_CANCEL,
        REASON_OTHER: localizedKeys.PAYMENT_HISTORY_TABLE_REASON_OTHER,
        REASON_CHARGEBACK:
            localizedKeys.PAYMENT_HISTORY_TABLE_REASON_CHARGEBACK,
        REASON_END: localizedKeys.PAYMENT_HISTORY_TABLE_REASON_END,
        REASON_LEASE: localizedKeys.PAYMENT_HISTORY_TABLE_REASON_LEASE,
        TABLE_COMMENT: localizedKeys.PAYMENT_HISTORY_TABLE_COMMENT,
        TABLE_USER: localizedKeys.PAYMENT_HISTORY_TABLE_USER,
    };

    const paymentTypeFilterEnumToUi = {
        [PaymentTypeFilter.PaymentPayOff]: paymentHistory.SEARCH_PANDP,
        [PaymentTypeFilter.Payment]: paymentHistory.SEARCH_PAYMENT,
        [PaymentTypeFilter.PayOff]: paymentHistory.SEARCH_PAYOFF,
    };

    const paymentTypeFilterUiToEnum = {
        [paymentHistory.SEARCH_PANDP]: PaymentTypeFilter.PaymentPayOff,
        [paymentHistory.SEARCH_PAYMENT]: PaymentTypeFilter.Payment,
        [paymentHistory.SEARCH_PAYOFF]: PaymentTypeFilter.PayOff,
    };

    const paymentDateFilterEnumToUi = {
        ['1']: paymentHistory.SEARCH_MONTH,
        ['2']: paymentHistory.SEARCH_2MONTH,
        ['3']: paymentHistory.SEARCH_3MONTH,
    };

    const paymentDateFilterUiToEnum = {
        [paymentHistory.SEARCH_MONTH]: '1',
        [paymentHistory.SEARCH_2MONTH]: '2',
        [paymentHistory.SEARCH_3MONTH]: '3',
    };

    const paymentAccountEnumToUi = {
        [AccountType.OperatingLease]: paymentHistory.TYPE_LEASE,
        [AccountType.RetailBalloon]: paymentHistory.TYPE_BALLOON,
        [AccountType.RetailLoan]: paymentHistory.TYPE_LOAN,
        [AccountType.Unknown]: '',
    };

    const paymentStatusEnumToUi = {
        [`${PaymentProcessStatus.PendingApproval}`]: paymentHistory.STATUS_0,
        [`${PaymentProcessStatus.Pending}`]: paymentHistory.STATUS_1,
        [`${PaymentProcessStatus.Deleted}`]: paymentHistory.STATUS_2,
        [`${PaymentProcessStatus.Submitted}`]: paymentHistory.STATUS_3,
        [`${PaymentProcessStatus.BatchDeleted}`]: paymentHistory.STATUS_4,
    };

    const paymentStatusUiToEnum = {
        [paymentHistory.STATUS_0]: `${PaymentProcessStatus.PendingApproval}`,
        [paymentHistory.STATUS_1]: `${PaymentProcessStatus.Pending}`,
        [paymentHistory.STATUS_2]: `${PaymentProcessStatus.Deleted}`,
        [paymentHistory.STATUS_3]: `${PaymentProcessStatus.Submitted}`,
        [paymentHistory.STATUS_4]: `${PaymentProcessStatus.BatchDeleted}`,
    };

    const paymentReasonEnumToUi = {
        [PaymentReason.Curtailment]: paymentHistory.REASON_CURTAILMENT,
        [PaymentReason.EndOfTermInvoice]: paymentHistory.REASON_END,
        [PaymentReason.FlatCancel]: paymentHistory.REASON_CANCEL,
        [PaymentReason.LeaseTransferFee]: paymentHistory.REASON_LEASE,
        [PaymentReason.Other]: paymentHistory.REASON_OTHER,
        [PaymentReason.ChargeBack]: paymentHistory.REASON_CHARGEBACK,
        [PaymentReason.RegularPayment]: paymentHistory.REASON_REGULAR,
    };

    const paymentBankAccountTypeToUi = {
        [BankAccountType.Checking]: payments.PAYMENT_INFORMATION_CHECKING,
        [BankAccountType.Saving]: payments.PAYMENT_INFORMATION_SAVINGS,
        [BankAccountType.CreditLine]: payments.PAYMENT_INFORMATION_LINE,
    };

    const payoffQuotes = {
        TITLE: localizedKeys.PAYMENT_QUOTE_TITLE,
        ADVISORY: localizedKeys.PAYMENT_QUOTE_ADVISORY,
        ERROR: localizedKeys.PAYMENT_QUOTE_ERROR,
        LINK: localizedKeys.PAYMENT_QUOTE_LINK,
        LINK2: localizedKeys.PAYMENT_QUOTE_LINK2,
        ALERT: localizedKeys.PAYMENT_QUOTE_ALERT,
        SEARCH: localizedKeys.PAYMENT_QUOTE_SEARCH,
    };

    const payoffQuotesDetail = {
        TITLE: localizedKeys.PAYOFF_QUOTE_DETAIL_TITLE,
        PRIVATE: localizedKeys.PAYOFF_QUOTE_DETAIL_PRIVATE,
        WARNING_MESSAGE: localizedKeys.PAYOFF_QUOTE_DETAIL_WARNING_MESSAGE,
        BUTTON: localizedKeys.PAYOFF_QUOTE_DETAIL_BUTTON,
    };

    const payoffQuotesDetailInfo = {
        TITLE_LEASE: localizedKeys.PAYOFF_QUOTE_INFO_TITLE_LEASE,
        TITLE_RETAIL: localizedKeys.PAYOFF_QUOTE_INFO_TITLE_RETAIL,
        AMOUNT: localizedKeys.PAYOFF_QUOTE_INFO_AMOUNT,
        THROUGH_DATE: localizedKeys.PAYOFF_QUOTE_INFO_THROUGH,
        DATE: localizedKeys.PAYOFF_QUOTE_INFO_DATE,
        ADVISORY: localizedKeys.PAYOFF_QUOTE_INFO_ADVISORY,
        NOTE_US_VW: localizedKeys.PAYOFF_QUOTE_INFO_NOTE_US_VW,
        NOTE_US_AU: localizedKeys.PAYOFF_QUOTE_INFO_NOTE_US_AU,
        NOTE_CA_VW: localizedKeys.PAYOFF_QUOTE_INFO_NOTE_CA_VW,
        NOTE_CA_AU: localizedKeys.PAYOFF_QUOTE_INFO_NOTE_CA_AU,
        ATTENTION: localizedKeys.PAYOFF_QUOTE_INFO_NOTE_ATTENTION,
        ADVICE: localizedKeys.PAYOFF_QUOTE_INFO_NOTE_ADVICE,
        PAYMENT: localizedKeys.PAYOFF_QUOTE_INFO_NOTE_PAYMENT,
        LABEL: localizedKeys.PAYOFF_QUOTE_INFO_NOTE_LABEL,
    };

    const payoffQuotesQuoteDetails = {
        TITLE: localizedKeys.PAYOFF_QUOTE_DETAILS_TITLE,
        RETAIL_BALANCE: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_BALANCE,
        RETAIL_INTEREST: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_INTEREST,
        RETAIL_FEE: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_FEE,
        RETAIL_BAILIFF: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_BAILIFF,
        RETAIL_IMPOUND: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_IMPOUND,
        RETAIL_LEGAL: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_LEGAL,
        RETAIL_MISCELLANEOUS:
            localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_MISCELLANEOUS,
        RETAIL_REPOSESSION:
            localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_REPOSESSION,
        RETAIL_STORAAGE: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_STORAAGE,
        RETAIL_EXTENSION: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_EXTENSION,
        RETAIL_TRANSFER: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_TRANSFER,
        RETAIL_VIOLATION: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_VIOLATION,
        RETAIL_ADMIN: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_ADMIN,
        RETAIL_TRACER: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_TRACER,
        RETAIL_OWNERSHIP: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_OWNERSHIP,
        RETAIL_LIEN: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_LIEN,
        RETAIL_REGISTRATION:
            localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_REGISTRATION,
        RETAIL_PURCHASE: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_PURCHASE,
        RETAIL_MATURITY: localizedKeys.PAYOFF_QUOTE_DETAILS_RETAIL_MATURITY,
        LEASE_VALUE: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_VALUE,
        LEASE_PAYOUT: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_PAYOUT,
        LEASE_DUE: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_DUE,
        LEASE_TAXDUE: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_TAXDUE,
        LEASE_PAYMENT: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_PAYMENT,
        LEASE_TAXPAYMENT: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_TAXPAYMENT,
        LEASE_CHARGES: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_CHARGES,
        LEASE_FEE_TITLE: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_TITLE,
        LEASE_FEE_TAX: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_TAX,
        LEASE_FEE_CHARGE: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_CHARGE,
        LEASE_FEE_FEX: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FEX,
        LEASE_FEE_FTE: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FTE,
        LEASE_FEE_FTK: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FTK,
        LEASE_FEE_FDM: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FDM,
        LEASE_FEE_FCB: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FCB,
        LEASE_FEE_FLG: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FLG,
        LEASE_FEE_FRS: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FRS,
        LEASE_FEE_FRI: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FRI,
        LEASE_FEE_FCS: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FCS,
        LEASE_FEE_FCO: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FCO,
        LEASE_FEE_FRR: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FRR,
        LEASE_FEE_FRM: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FRM,
        LEASE_FEE_FMS: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FMS,
        LEASE_FEE_FTT: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FTT,
        LEASE_FEE_FEU: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FEU,
        LEASE_FEE_FEP: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FEP,
        LEASE_FEE_FNF: localizedKeys.PAYOFF_QUOTE_DETAILS_LEASE_FEE_FNF,
        PAYMENT_TITLE: localizedKeys.PAYOFF_QUOTE_DETAILS_PAYMENT_TITLE,
        PAYMENT_CHECKS_DIGITAL:
            localizedKeys.PAYOFF_QUOTE_DETAILS_PAYMENT_CHECKS_DIGITAL,
        PAYMENT_CHECKS_BRASS:
            localizedKeys.PAYOFF_QUOTE_DETAILS_PAYMENT_CHECKS_BRASS,
        PAYMENT_NOTE_LABEL:
            localizedKeys.PAYOFF_QUOTE_DETAILS_PAYMENT_NOTE_LABEL,
        PAYMENT_NOTE: localizedKeys.PAYOFF_QUOTE_DETAILS_PAYMENT_NOTE,
        AUTHORIZATION_NOTE:
            localizedKeys.PAYOFF_QUOTE_DETAILS_PAYMENT_NOTE_AUTHORIZATION,
        ONLINE_PAYMENT_TITLE:
            localizedKeys.PAYOFF_QUOTE_DETAILS_ONLINE_PAYMENT_TITLE,
        ONLINE_PAYMENT_INFO:
            localizedKeys.PAYOFF_QUOTE_DETAILS_ONLINE_PAYMENT_INFO,
        ONLINE_PAYMENT_INFO2:
            localizedKeys.PAYOFF_QUOTE_DETAILS_ONLINE_PAYMENT_INFO2,
        ACCOUNT_INFO_RETAIL:
            localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_INFO_RETAIL,
        ACCOUNT_INFO_LEASE:
            localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_INFO_LEASE,
        ACCOUNT_INFO_MATURITY:
            localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_INFO_MATURITY,
        ACCOUNT_INFO_TOTAL_PAYOUT:
            localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_INFO_TOTAL,
    };

    const payoffQuotesNote = {
        TITLE: localizedKeys.PAYOFF_QUOTE_DETAILS_NOTE_TITLE,
        US: localizedKeys.PAYOFF_QUOTE_DETAILS_NOTE_US,
        CA: localizedKeys.PAYOFF_QUOTE_DETAILS_NOTE_CA,
    };

    const payoffQuotesAccount = {
        TITLE: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_TITLE,
        TERM: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_TERM,
        FINALDATE: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_FINALDATE,
        SCHPAYMENT: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_SCHPAYMENT,
        REMPAYMENT: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_REMPAYMENT,
        NEXTDATE: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_NEXTDATE,
        LASTPAYMENT: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_LASTPAYMENT,
        LASTDATE: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_LASTDATE,
        BALANCE: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_BALANCE,
        MATURITYDATE: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_MATURITYDATE,
        AMTBEFORETAX: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_AMTBEFORETAX,
        OTHER_AMOUNT: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_OTHERAMOUNT,
        GST_HST: localizedKeys.PAYOFF_QUOTE_DETAILS_ACCOUNT_GSTHST,
    };

    const payoffQuotesDetailUS = {
        TITLE: localizedKeys.PAYOFF_QUOTE_DETAILS_US_TITLE,
        NEW_SEARCH: localizedKeys.PAYOFF_QUOTE_DETAILS_US_NEW_SEARCH,
        ACCOUNT_INFO_TITLE:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_ACCOUNT_INFO_TITLE,
        VIN_LABEL: localizedKeys.PAYOFF_QUOTE_DETAILS_US_VIN_LABEL,
        ORIGINAL_CONTRACT_DATE:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_ORIGINAL_CONTRACT_DATE,
        PAYMENT_DETAILS_TITLE:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_PAYMENT_DETAILS_TITLE,
        MONTHLY_PAYMENT_EXCLUDES_TAX:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_MONTHLY_PAYMENT_EXCLUDES_TAX,
        REMAINING_PAYMENTS:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_REMAINING_PAYMENTS,
        MONTHLY_PAYMENT_INCLUDES_TAX:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_MONTHLY_PAYMENT_INCLUDES_TAX,
        LAST_PAYMENT_DATE:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_LAST_PAYMENT_DATE,
        TOTAL_REMAINING_PAYMENTS:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_TOTAL_REMAINING_PAYMENTS,
        OTHER_INVOICED_CHARGES:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_OTHER_INVOICED_CHARGES,
        OUTSTANDING_BALANCE:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_OUTSTANDING_BALANCE,
        TOTAL_OUTSTANDING_BALANCE:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_TOTAL_OUTSTANDING_BALANCE,
        FOOTNOTE_PROJECTED:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_FOOTNOTE_PROJECTED,
        FOOTNOTE_DISPOSITION:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_FOOTNOTE_DISPOSITION,
        CANCEL_BUTTON: localizedKeys.PAYOFF_QUOTE_DETAILS_US_CANCEL_BUTTON,
        GET_PAYOFF_QUOTE_BUTTON:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_GET_PAYOFF_QUOTE_BUTTON,
        OPERATING_LEASE: localizedKeys.PAYOFF_QUOTE_DETAILS_US_OPERATING_LEASE,
        SINGLE_PAY_LEASE:
            localizedKeys.PAYOFF_QUOTE_DETAILS_US_SINGLE_PAY_LEASE,
        RETAIL_LOAN: localizedKeys.PAYOFF_QUOTE_DETAILS_US_RETAIL_LOAN,
        RETAIL_BALLOON: localizedKeys.PAYOFF_QUOTE_DETAILS_US_RETAIL_BALLOON,
    };

    const payoffQuoteInformation = {
        TITLE: localizedKeys.PAYOFF_QUOTE_INFORMATION_TITLE,
        AMOUNT_BEFORE_TAX:
            localizedKeys.PAYOFF_QUOTE_INFORMATION_AMOUNT_BEFORE_TAX,
        GOOD_THROUGH_DATE:
            localizedKeys.PAYOFF_QUOTE_INFORMATION_GOOD_THROUGH_DATE,
        TAX_AMOUNT: localizedKeys.PAYOFF_QUOTE_INFORMATION_TAX_AMOUNT,
        QUOTE_DATE: localizedKeys.PAYOFF_QUOTE_INFORMATION_QUOTE_DATE,
        TOTAL_QUOTE_AMOUNT:
            localizedKeys.PAYOFF_QUOTE_INFORMATION_TOTAL_QUOTE_AMOUNT,
        QUOTE_AMOUNT: localizedKeys.PAYOFF_QUOTE_INFORMATION_QUOTE_AMOUNT,
    };

    const payoffPaymentInformation = {
        ODOMETER_READING:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_ODOMETER_READING,
        SECURITY_DEPOSIT:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_SECURITY_DEPOSIT,
        PURCHASE_TYPE_LABEL:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_PURCHASE_TYPE_LABEL,
        PURCHASE_TYPE_DEALER:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_PURCHASE_TYPE_DEALER,
        PURCHASE_TYPE_CUSTOMER:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_PURCHASE_TYPE_CUSTOMER,
        ASSIGN_SECURITY_DEPOSIT_LABEL:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_ASSIGN_SECURITY_DEPOSIT_LABEL,
        ASSIGN_SECURITY_DEPOSIT_CUSTOMER:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_ASSIGN_SECURITY_DEPOSIT_CUSTOMER,
        ASSIGN_SECURITY_DEPOSIT_DEALER:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_ASSIGN_SECURITY_DEPOSIT_DEALER,
        ASSIGN_SECURITY_DEPOSIT_DEDUCT:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_ASSIGN_SECURITY_DEPOSIT_DEDUCT,
        PAYMENT_TYPE_LABEL:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_PAYMENT_TYPE_LABEL,
        PAYMENT_TYPE_CHEQUE:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_PAYMENT_TYPE_CHEQUE,
        PAYMENT_TYPE_SAVINGS:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_PAYMENT_TYPE_SAVINGS,
        PAYMENT_TYPE_BRASS:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_PAYMENT_TYPE_BRASS,
        LOYALTY_VIN: localizedKeys.PAYOFF_PAYMENT_INFORMATION_LOYALTY_VIN,
        DISCLAIMER_TITLE:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_DISCLAIMER_TITLE,
        INFORMATION_DISCLAIMER:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_DISCLAIMER,
        TOTAL: localizedKeys.PAYOFF_PAYMENT_INFORMATION_TOTAL,
        LPA_LABEL: localizedKeys.PAYOFF_PAYMENT_INFORMATION_LPA_LABEL,
        LPA_AUDI_TOOLTIP:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_LPA_AUDI_TOOLTIP,
        LPA_VW_TOOLTIP: localizedKeys.PAYOFF_PAYMENT_INFORMATION_LPA_VW_TOOLTIP,
        ASSIGN_SECURITY_DEPOSIT_WARNING:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_ASSIGN_SECURITY_DEPOSIT_WARNING,
        LOYALTY_VIN_ERROR:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_LOYALTY_VIN_ERROR,
        PURCHASE_TYPE_ERROR:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_PURCHASE_TYPE_ERROR,
        LPA_ERROR: localizedKeys.PAYOFF_PAYMENT_INFORMATION_LPA_ERROR,
        ODOMETER_READING_ERROR:
            localizedKeys.PAYOFF_PAYMENT_INFORMATION_ODOMETER_READING_ERROR,
    };

    const assignSecurityDepositToUi = {
        [SecurityDepositWaiverType.AssignToCustomer]:
            payoffPaymentInformation.ASSIGN_SECURITY_DEPOSIT_CUSTOMER,
        [SecurityDepositWaiverType.AssignToDealer]:
            payoffPaymentInformation.ASSIGN_SECURITY_DEPOSIT_DEALER,
        [SecurityDepositWaiverType.Deduct]:
            payoffPaymentInformation.ASSIGN_SECURITY_DEPOSIT_DEDUCT,
    };

    const purchaseTypeToUi = {
        [PurchaseType.CustomerBuyBack]:
            payoffPaymentInformation.PURCHASE_TYPE_CUSTOMER,
        [PurchaseType.DealerInventory]:
            payoffPaymentInformation.PURCHASE_TYPE_DEALER,
    };

    const warningMessages = {
        CLOSED_ACCOUNT: localizedKeys.PAYMENT_WARNING_MESSAGE_CLOSED_ACCOUNT,
        NOT_IN_PORTFOLIO:
            localizedKeys.PAYMENT_WARNING_MESSAGE_NOT_IN_PORTFOLIO,
        REDIRECT_CA: localizedKeys.PAYMENT_WARNING_MESSAGE_REDIRECT_CA,
        REDIRECT_US_VW: localizedKeys.PAYMENT_WARNING_MESSAGE_REDIRECT_US_VW,
        REDIRECT_US_AUDI:
            localizedKeys.PAYMENT_WARNING_MESSAGE_REDIRECT_US_AUDI,
        REDIRECT_US_DUCATI:
            localizedKeys.PAYMENT_WARNING_MESSAGE_REDIRECT_US_DUCATI,
        QUOTE_EXCEPTION_US:
            localizedKeys.PAYMENT_WARNING_MESSAGE_QUOTE_EXCEPTION_US,
        QUOTE_EXCEPTION_CA:
            localizedKeys.PAYMENT_WARNING_MESSAGE_QUOTE_EXCEPTION_CA,
        QUOTE_CANNOT_BE_RETURNED_US:
            localizedKeys.PAYMENT_WARNING_MESSAGE_QUOTE_CANNOT_BE_RETURNED_US,
        QUOTE_CANNOT_BE_RETURNED_CA:
            localizedKeys.PAYMENT_WARNING_MESSAGE_QUOTE_CANNOT_BE_RETURNED_CA,
        UNKNOWN_EXCEPTION_VW_CA:
            localizedKeys.PAYMENT_WARNING_MESSAGE_UNKNOWN_EXCEPTION_VW_CA,
        UNKNOWN_EXCEPTION_AUDI_CA:
            localizedKeys.PAYMENT_WARNING_MESSAGE_UNKNOWN_EXCEPTION_AUDI_CA,
        UNKNOWN_EXCEPTION_DUCATI_CA:
            localizedKeys.PAYMENT_WARNING_MESSAGE_UNKNOWN_EXCEPTION_DUCATI_CA,
        UNKNOWN_EXCEPTION_VW_US:
            localizedKeys.PAYMENT_WARNING_MESSAGE_UNKNOWN_EXCEPTION_VW_US,
        UNKNOWN_EXCEPTION_AUDI_US:
            localizedKeys.PAYMENT_WARNING_MESSAGE_UNKNOWN_EXCEPTION_AUDI_US,
        UNKNOWN_EXCEPTION_DUCATI_US:
            localizedKeys.PAYMENT_WARNING_MESSAGE_UNKNOWN_EXCEPTION_DUCATI_US,
        PAYOFF_PAYMENT_EXCLUDED_VW:
            localizedKeys.PAYMENT_WARNING_MESSAGE_PAYOFF_PAYMENT_EXCLUDED_VW,
        PAYOFF_PAYMENT_EXCLUDED_AUDI:
            localizedKeys.PAYMENT_WARNING_MESSAGE_PAYOFF_PAYMENT_EXCLUDED_AUDI,
        CHARGED_OFF_US_AUDI:
            localizedKeys.PAYMENT_WARMNING_MESSAGE_CHARGED_OFF_AUDI,
        CHARGED_OFF_US_VW:
            localizedKeys.PAYMENT_WARMNING_MESSAGE_CHARGED_OFF_VW,
        CHARGED_OFF_US_DUCATI:
            localizedKeys.PAYMENT_WARMNING_MESSAGE_CHARGED_OFF_DUCATI,
        CONTACT_REP_AUDI:
            localizedKeys.PAYMENT_WARNING_MESSAGE_CONTACT_REP_AUDI,
        CONTACT_REP_VW: localizedKeys.PAYMENT_WARNING_MESSAGE_CONTACT_REP_VW,
        CONTACT_REP_DUCATI:
            localizedKeys.PAYMENT_WARNING_MESSAGE_CONTACT_REP_DUCATI,
    };

    const portfolioManager = {
        PORTFOLIO_TITLE_LABEL: localizedKeys.PORTFOLIO_TITLE_LABEL,
        PORTFOLIO_CUSTOMER_LABEL: localizedKeys.PORTFOLIO_CUSTOMER_LABEL,
        PORTFOLIO_ACCOUNT_LABEL: localizedKeys.PORTFOLIO_ACCOUNT_LABEL,
        PORTFOLIO_BASE_PAYMENT_LABEL:
            localizedKeys.PORTFOLIO_BASE_PAYMENT_LABEL,
        PORTFOLIO_EST_EQUITY_LABEL: localizedKeys.PORTFOLIO_EST_EQUITY_LABEL,
        PORTFOLIO_CURRT_MATURITY_LABEL:
            localizedKeys.PORTFOLIO_CURRT_MATURITY_LABEL,
        PORTFOLIO_PAY_DATE_LABEL: localizedKeys.PORTFOLIO_PAY_DATE_LABEL,
        PORTFOLIO_MODEL_LABEL: localizedKeys.PORTFOLIO_MODEL_LABEL,
        PORTFOLIO_ACCT_TYPE_LABEL: localizedKeys.PORTFOLIO_ACCT_TYPE_LABEL,
        PORTFOLIO_PROGRAM_LABEL: localizedKeys.PORTFOLIO_PROGRAM_LABEL,
        PORTFOLIO_NONE_LABEL: localizedKeys.PORTFOLIO_NONE_LABEL,
        PORTFOLIO_MESSAGE_LABEL: localizedKeys.PORTFOLIO_MESSAGE_LABEL,
        PORTFOLIO_SEARCH_TOOLTIP: localizedKeys.PORTFOLIO_SEARCH_TOOLTIP,
        PORTFOLIO_SEARCH_BY_NAME_TOOLTIP:
            localizedKeys.PORTFOLIO_SEARCH_BY_NAME_TOOLTIP,
        PORTFOLIO_SEARCH_BY_SPECIAL_TOOLTIP:
            localizedKeys.PORTFOLIO_SEARCH_BY_SPECIAL_TOOLTIP,
        PORTFOLIO_SEARCH_MONTHS_3: localizedKeys.PORTFOLIO_SEARCH_MONTHS_3,
        PORTFOLIO_SEARCH_MONTHS_6: localizedKeys.PORTFOLIO_SEARCH_MONTHS_6,
        PORTFOLIO_SEARCH_MONTHS_9: localizedKeys.PORTFOLIO_SEARCH_MONTHS_9,
        PORTFOLIO_SEARCH_MONTHS_12: localizedKeys.PORTFOLIO_SEARCH_MONTHS_12,
        PORTFOLIO_SEARCH_LABEL: localizedKeys.PORTFOLIO_SEARCH_LABEL,
        PORTFOLIO_SEARCH_PROGRAMS_TITLE:
            localizedKeys.PORTFOLIO_SEARCH_PROGRAMS_TITLE,
        PORTFOLIO_SEARCH_DATE_TITLE: localizedKeys.PORTFOLIO_SEARCH_DATE_TITLE,
        PORTFOLIO_ACTIVE_CONTRACTS: localizedKeys.PORTFOLIO_ACTIVE_CONTRACTS,
        PORTFOLIO_TERMINATED_CONTRACTS:
            localizedKeys.PORTFOLIO_TERMINATED_CONTRACTS,
        PORTFOLIO_LOAN: localizedKeys.PORTFOLIO_LOAN,
        PORTFOLIO_LEASE: localizedKeys.PORTFOLIO_LEASE,
        PORTFOLIO_VW_SELECT: localizedKeys.PORTFOLIO_VW_SELECT,
        PORTFOLIO_AUDI_SELECT: localizedKeys.PORTFOLIO_AUDI_SELECT,
    };

    const reserves = {
        TITLE: localizedKeys.RESERVES_TITLE,
        SEARCH_BAR: localizedKeys.RESERVES_SEARCH_BAR,
        NO_RESUlTS: localizedKeys.RESERVES_NO_RESULTS,
        FILTER_JANUARY: localizedKeys.RESERVES_FILTER_JANUARY,
        FILTER_FEBRUARY: localizedKeys.RESERVES_FILTER_FEBRUARY,
        FILTER_MARCH: localizedKeys.RESERVES_FILTER_MARCH,
        FILTER_APRIL: localizedKeys.RESERVES_FILTER_APRIL,
        FILTER_MAY: localizedKeys.RESERVES_FILTER_MAY,
        FILTER_JUNE: localizedKeys.RESERVES_FILTER_JUNE,
        FILTER_JULY: localizedKeys.RESERVES_FILTER_JULY,
        FILTER_AUGUST: localizedKeys.RESERVES_FILTER_AUGUST,
        FILTER_SEPTEMBER: localizedKeys.RESERVES_FILTER_SEPTEMBER,
        FILTER_OCTOBER: localizedKeys.RESERVES_FILTER_OCTOBER,
        FILTER_NOVEMBER: localizedKeys.RESERVES_FILTER_NOVEMBER,
        FILTER_DECEMBER: localizedKeys.RESERVES_FILTER_DECEMBER,
        FILTER_RESET: localizedKeys.RESERVES_FILTER_RESET,
        TABS_MONTHLY: localizedKeys.RESERVES_TABS_MONTHLY,
        TABS_ACTIVITY: localizedKeys.RESERVES_TABS_ACTIVITY,
        MONTHLY_HEADER: localizedKeys.RESERVES_MONTHLY_HEADER,
        MONTHLY_FOR: localizedKeys.RESERVES_MONTHLY_FOR,
        ACTIVITY_HEADER: localizedKeys.RESERVES_ACTIVITY_HEADER,
        TABLE_ACCOUNT: localizedKeys.RESERVES_TABLE_ACCOUNT,
        TABLE_CUSTOMER: localizedKeys.RESERVES_TABLE_CUSTOMER,
        TABLE_VIN: localizedKeys.RESERVES_TABLE_VIN,
        TABLE_TRANSACTION: localizedKeys.RESERVES_TABLE_TRANSACTION,
        TABLE_FINANCE: localizedKeys.RESERVES_TABLE_FINANCE,
        TABLE_PAYMENT: localizedKeys.RESERVES_TABLE_PAYMENT,
        TABLE_EOM: localizedKeys.RESERVES_TABLE_EOM,
        MONTHLY_TOTALS_HEADER: localizedKeys.RESERVES_MONTHLY_TOTALS_HEADER,
        MONTHLY_TOTALS_CREDITS: localizedKeys.RESERVES_MONTHLY_TOTALS_CREDITS,
        MONTHLY_TOTALS_STARTBALANCE:
            localizedKeys.RESERVES_MONTHLY_TOTALS_STARTBALANCE,
        MONTHLY_TOTALS_EARNINGS: localizedKeys.RESERVES_MONTHLY_TOTALS_EARNINGS,
        MONTHLY_TOTALS_ENDBALANCE:
            localizedKeys.RESERVES_MONTHLY_TOTALS_ENDBALANCE,
        MONTHLY_TOTALS_BACKAMOUNT:
            localizedKeys.RESERVES_MONTHLY_TOTALS_BACKAMOUNT,
        MONTHLY_TOTALS_ADJUSTMENT_US:
            localizedKeys.RESERVES_MONTHLY_TOTALS_ADJUSTMENT_US,
        MONTHLY_TOTALS_ADJUSTMENT_CA:
            localizedKeys.RESERVES_MONTHLY_TOTALS_ADJUSTMENT_CA,
        MONTHLY_TOTALS_TRANSFER: localizedKeys.RESERVES_MONTHLY_TOTALS_TRANSFER,
        MONTHLY_TOTALS_REPOSSESSION:
            localizedKeys.RESERVES_MONTHLY_TOTALS_REPOSSESSION,
        MONTHLY_TOTALS_CHARGEOFF:
            localizedKeys.RESERVES_MONTHLY_TOTALS_CHARGEOFF,
        MONTHLY_TOTALS_PAYOFF_US:
            localizedKeys.RESERVES_MONTHLY_TOTALS_PAYOFF_US,
        MONTHLY_TOTALS_PAYOFF_CA:
            localizedKeys.RESERVES_MONTHLY_TOTALS_PAYOFF_CA,
        MONTHLY_TOTALS_OUTSTANDING:
            localizedKeys.RESERVES_MONTHLY_TOTALS_OUTSTANDING,
        MONTHLY_TOTALS_RETENTION:
            localizedKeys.RESERVES_MONTHLY_TOTALS_RETENTION,
        MONTHLY_TOTALS_DISBURSEMENT:
            localizedKeys.RESERVES_MONTHLY_TOTALS_DISBURSEMENT,
        MONTHLY_TOTALS_DISBURSEMENTAMT:
            localizedKeys.RESERVES_MONTHLY_TOTALS_DISBURSEMENTAMT,
        MONTHLY_YEARLY_HEADER: localizedKeys.RESERVES_MONTHLY_YEARLY_HEADER,
        MONTHLY_YEARLY_CREDITS: localizedKeys.RESERVES_MONTHLY_YEARLY_CREDITS,
        MONTHLY_YEARLY_EARNINGS: localizedKeys.RESERVES_MONTHLY_YEARLY_EARNINGS,
        MONTHLY_YEARLY_CHARGEBACK:
            localizedKeys.RESERVES_MONTHLY_YEARLY_CHARGEBACK,
        MONTHLY_YEARLY_ADJUSTMENT:
            localizedKeys.RESERVES_MONTHLY_YEARLY_ADJUSTMENT,
        MONTHLY_YEARLY_TRANSFER: localizedKeys.RESERVES_MONTHLY_YEARLY_TRANSFER,
        MONTHLY_YEARLY_DISBURSMENTS:
            localizedKeys.RESERVES_MONTHLY_YEARLY_DISBURSMENTS,
        MONTHLY_YEARLY_DESCRIPTION:
            localizedKeys.RESERVES_MONTHLY_YEARLY_DESCRIPTION,
        MONTHLY_TOTALS_DESCRIPTION:
            localizedKeys.RESERVES_MONTHLY_TOTALS_DESCRIPTION,
        MONTHLY_YEARLY_NOTE: localizedKeys.RESERVES_MONTHLY_YEARLY_NOTE,
        MONTHLY_YEARLY_MESSAGE: localizedKeys.RESERVES_MONTHLY_YEARLY_MESSAGE,
    };

    const documents = {
        DOCUMENTS_VIEW_TITLE: localizedKeys.DOCUMENTS_VIEW_TITLE,
        DOCUMENTS_VIEW_ERROR: localizedKeys.DOCUMENTS_VIEW_ERROR,
        DOCUMENTS_VIEW_TABS_GENERAL: localizedKeys.DOCUMENTS_VIEW_TABS_GENERAL,
        DOCUMENTS_VIEW_TABS_POLICIES:
            localizedKeys.DOCUMENTS_VIEW_TABS_POLICIES,
        DOCUMENTS_VIEW_TABS_FUNDING: localizedKeys.DOCUMENTS_VIEW_TABS_FUNDING,
        DOCUMENTS_VIEW_TABS_CREDIT: localizedKeys.DOCUMENTS_VIEW_TABS_CREDIT,
        DOCUMENTS_VIEW_TABS_TRANSFER:
            localizedKeys.DOCUMENTS_VIEW_TABS_TRANSFER,
        DOCUMENTS_VIEW_TABS_END: localizedKeys.DOCUMENTS_VIEW_TABS_END,
        DOCUMENTS_VIEW_NAMES_FINANCING:
            localizedKeys.DOCUMENTS_VIEW_NAMES_FINANCING,
        DOCUMENTS_VIEW_NAMES_CERT: localizedKeys.DOCUMENTS_VIEW_NAMES_CERT,
        DOCUMENTS_VIEW_NAMES_EN: localizedKeys.DOCUMENTS_VIEW_NAMES_EN,
        DOCUMENTS_VIEW_NAMES_FR: localizedKeys.DOCUMENTS_VIEW_NAMES_FR,
        DOCUMENTS_VIEW_NAMES_CCA: localizedKeys.DOCUMENTS_VIEW_NAMES_CCA,
        DOCUMENTS_VIEW_NAMES_CCAQ: localizedKeys.DOCUMENTS_VIEW_NAMES_CCAQ,
        DOCUMENTS_VIEW_NAMES_CCAR: localizedKeys.DOCUMENTS_VIEW_NAMES_CCAR,
        DOCUMENTS_VIEW_NAMES_CCAQC: localizedKeys.DOCUMENTS_VIEW_NAMES_CCAQC,
        DOCUMENTS_VIEW_NAMES_DOCUMENTCHECK:
            localizedKeys.DOCUMENTS_VIEW_NAMES_DOCUMENTCHECK,
        DOCUMENTS_VIEW_NAMES_LEASE: localizedKeys.DOCUMENTS_VIEW_NAMES_LEASE,
        DOCUMENTS_VIEW_NAMES_RETAIL: localizedKeys.DOCUMENTS_VIEW_NAMES_RETAIL,
        DOCUMENTS_VIEW_NAMES_WORKSHEETS:
            localizedKeys.DOCUMENTS_VIEW_NAMES_WORKSHEETS,
        DOCUMENTS_VIEW_NAMES_ONEPAY: localizedKeys.DOCUMENTS_VIEW_NAMES_ONEPAY,
        DOCUMENTS_VIEW_NAMES_VWSELECT:
            localizedKeys.DOCUMENTS_VIEW_NAMES_VWSELECT,
        DOCUMENTS_VIEW_NAMES_AUDISELECT:
            localizedKeys.DOCUMENTS_VIEW_NAMES_AUDISELECT,
        DOCUMENTS_VIEW_NAMES_INSURANCE:
            localizedKeys.DOCUMENTS_VIEW_NAMES_INSURANCE,
        DOCUMENTS_VIEW_NAMES_PREFORM:
            localizedKeys.DOCUMENTS_VIEW_NAMES_PREFORM,
        DOCUMENTS_VIEW_NAMES_CORPORATE:
            localizedKeys.DOCUMENTS_VIEW_NAMES_CORPORATE,
        DOCUMENTS_VIEW_NAMES_LPA: localizedKeys.DOCUMENTS_VIEW_NAMES_LPA,
        DOCUMENTS_VIEW_NAMES_REFUNDABLE:
            localizedKeys.DOCUMENTS_VIEW_NAMES_REFUNDABLE,
        DOCUMENTS_VIEW_NAMES_LEASETERM:
            localizedKeys.DOCUMENTS_VIEW_NAMES_LEASETERM,
        DOCUMENTS_VIEW_NAMES_LEASEWORK:
            localizedKeys.DOCUMENTS_VIEW_NAMES_LEASEWORK,
        DOCUMENTS_VIEW_NAMES_LEASECHECK:
            localizedKeys.DOCUMENTS_VIEW_NAMES_LEASECHECK,
        DOCUMENTS_VIEW_NAMES_CUSTOMER:
            localizedKeys.DOCUMENTS_VIEW_NAMES_CUSTOMER,
        DOCUMENTS_VIEW_TABS_PAYOFF: localizedKeys.DOCUMENTS_VIEW_TABS_PAYOFF,
        DOCUMENTS_VIEW_TABS_MISCELLANEOUS:
            localizedKeys.DOCUMENTS_VIEW_TABS_MISCELLANEOUS,
        DOCUMENTS_VIEW_NAMES_BCA: localizedKeys.DOCUMENTS_VIEW_NAMES_BCA,
        DOCUMENTS_VIEW_NAMES_CONSUMER_CREDIT_US:
            localizedKeys.DOCUMENTS_VIEW_NAMES_CONSUMER_CREDIT_US,
        DOCUMENTS_VIEW_NAMES_EMPLOYMENT_VERIFICATION:
            localizedKeys.DOCUMENTS_VIEW_NAMES_EMPLOYMENT_VERIFICATION,
        DOCUMENTS_VIEW_NAMES_FOREIGN_BUSINESS:
            localizedKeys.DOCUMENTS_VIEW_NAMES_FOREIGN_BUSINESS,
        DOCUMENTS_VIEW_NAMES_PERSONAL_REF:
            localizedKeys.DOCUMENTS_VIEW_NAMES_PERSONAL_REF,
        DOCUMENTS_VIEW_NAMES_ORDER_SUPPLIES:
            localizedKeys.DOCUMENTS_VIEW_NAMES_ORDER_SUPPLIES,
        DOCUMENTS_VIEW_NAMES_SPEEDY_FAX:
            localizedKeys.DOCUMENTS_VIEW_NAMES_SPEEDY_FAX,
        DOCUMENTS_VIEW_NAMES_CONSUMER_AUTO_PAYMENT:
            localizedKeys.DOCUMENTS_VIEW_NAMES_CONSUMER_AUTO_PAYMENT,
        DOCUMENTS_VIEW_NAMES_AUTOPAYMENT_CANCEL:
            localizedKeys.DOCUMENTS_VIEW_NAMES_AUTOPAYMENT_CANCEL,
        DOCUMENTS_VIEW_NAMES_SC_TAX_CREDIT:
            localizedKeys.DOCUMENTS_VIEW_NAMES_SC_TAX_CREDIT,
        DOCUMENTS_VIEW_NAMES_WI_TAX_CREDIT:
            localizedKeys.DOCUMENTS_VIEW_NAMES_WI_TAX_CREDIT,
        DOCUMENTS_VIEW_NAMES_IL_INTERPRETER:
            localizedKeys.DOCUMENTS_VIEW_NAMES_IL_INTERPRETER,
        DOCUMENTS_VIEW_NAMES_CT_TRADEIN:
            localizedKeys.DOCUMENTS_VIEW_NAMES_CT_TRADEIN,
        DOCUMENTS_VIEW_NAMES_ID_VERIFICATION:
            localizedKeys.DOCUMENTS_VIEW_NAMES_ID_VERIFICATION,
        DOCUMENTS_VIEW_NAMES_NOTICE_COSIGNER_RETAIL:
            localizedKeys.DOCUMENTS_VIEW_NAMES_NOTICE_COSIGNER_RETAIL,
        DOCUMENTS_VIEW_NAMES_NOTICE_COSIGNER_LEASE:
            localizedKeys.DOCUMENTS_VIEW_NAMES_NOTICE_COSIGNER_LEASE,
        DOCUMENTS_VIEW_NAMES_NOTICE_COSIGNER_FORMS:
            localizedKeys.DOCUMENTS_VIEW_NAMES_NOTICE_COSIGNER_FORMS,
        DOCUMENTS_VIEW_NAMES_EV_LEASE_ADDENDUM:
            localizedKeys.DOCUMENTS_VIEW_NAMES_EV_LEASE_ADDENDUM,
        DOCUMENTS_VIEW_NAMES_EV_DSL_ADDENDUM:
            localizedKeys.DOCUMENTS_VIEW_NAMES_EV_DSL_ADDENDUM,
        DOCUMENTS_VIEW_NAMES_FUNDING_OTHER:
            localizedKeys.DOCUMENTS_VIEW_NAMES_FUNDING_OTHER,
        DOCUMENTS_VIEW_NAMES_EV_BUNDLE_ACK:
            localizedKeys.DOCUMENTS_VIEW_NAMES_EV_BUNDLE_ACK,
        DOCUMENTS_VIEW_NAMES_FUNDING_CHECKLIST:
            localizedKeys.DOCUMENTS_VIEW_NAMES_FUNDING_CHECKLIST,
        DOCUMENTS_VIEW_NAMES_DEALER_RESERVE:
            localizedKeys.DOCUMENTS_VIEW_NAMES_DEALER_RESERVE,
        DOCUMENTS_VIEW_NAMES_BONUS_RESEARCH:
            localizedKeys.DOCUMENTS_VIEW_NAMES_BONUS_RESEARCH,
        DOCUMENTS_VIEW_NAMES_LAW_PRINTING_ORDER:
            localizedKeys.DOCUMENTS_VIEW_NAMES_LAW_PRINTING_ORDER,
        DOCUMENTS_VIEW_NAMES_INSURANCE_FORM_US:
            localizedKeys.DOCUMENTS_VIEW_NAMES_INSURANCE_FORM_US,
        DOCUMENTS_VIEW_NAMES_ODOMETER:
            localizedKeys.DOCUMENTS_VIEW_NAMES_ODOMETER,
        DOCUMENTS_VIEW_NAMES_NEW_MEXICO_INSURANCE:
            localizedKeys.DOCUMENTS_VIEW_NAMES_NEW_MEXICO_INSURANCE,
        DOCUMENTS_VIEW_NAMES_AUDI_CARE_FORMS:
            localizedKeys.DOCUMENTS_VIEW_NAMES_AUDI_CARE_FORMS,
        DOCUMENTS_VIEW_NAMES_NM_INSURANCE_ALL_RETAILS:
            localizedKeys.DOCUMENTS_VIEW_NAMES_NM_INSURANCE_ALL_RETAILS,
        DOCUMENTS_VIEW_NAMES_NM_INSURANCE_DEALER_AUTH:
            localizedKeys.DOCUMENTS_VIEW_NAMES_NM_INSURANCE_DEALER_AUTH,
        DOCUMENTS_VIEW_NAMES_AUDI_CARE:
            localizedKeys.DOCUMENTS_VIEW_NAMES_AUDI_CARE,
        DOCUMENTS_VIEW_NAMES_AUDI_CARE_CA:
            localizedKeys.DOCUMENTS_VIEW_NAMES_AUDI_CARE_CA,
    };

    const systemAdmin = {
        TITLE: localizedKeys.SYSTEM_ADMIN_TITLE,
        TABS_INTERNAL: localizedKeys.SYSTEM_ADMIN_TABS_INTERNAL,
        TABS_CONTENT: localizedKeys.SYSTEM_ADMIN_TABS_CONTENT,
        DEALER_MANAGEMENT: localizedKeys.SYSTEM_ADMIN_DEALER_MANAGEMENT,
        DEALER_ACTIVATION: localizedKeys.SYSTEM_ADMIN_DEALER_ACTIVATION,
        DEALER_SECURITY_MAINTENANCE:
            localizedKeys.SYSTEM_ADMIN_DEALER_SECURITY_MAINTENANCE,
        DEALER_GROUP_MAINTENANCE:
            localizedKeys.SYSTEM_ADMIN_DEALER_GROUP_MAINTENANCE,
        DOCUMENTS: localizedKeys.SYSTEM_ADMIN_DOCUMENTS_HEADING,
        STATIC_PDF_MAINTENANCE:
            localizedKeys.SYSTEM_ADMIN_STATIC_PDF_MAINTENANCE,
        DISTRIBUTION_RULE_MANAGEMENT:
            localizedKeys.SYSTEM_ADMIN_DISTRIBUTION_RULE_MANAGEMENT,
        CREATE_NEW_RULE: localizedKeys.SYSTEM_ADMIN_CREATE_NEW_RULE,
        VIEW_EDIT_EXISTING_RULE:
            localizedKeys.SYSTEM_ADMIN_VIEW_EDIT_EXISTING_RULE,
        CREATE_RULE_PAGE_RULE_NAME_LABEL:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_RULE_NAME_LABEL,
        CREATE_RULE_PAGE_TITLE:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_TITLE,
        EDIT_RULE_PAGE_TITLE: localizedKeys.SYSTEM_ADMIN_EDIT_RULE_PAGE_TITLE,
        CREATE_RULE_PAGE_DESCRIPTION:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_DESCRIPTION,
        CREATE_RULE_PAGE_RULE_NAME_PLACEHOLDER:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_RULE_NAME_PLACEHOLDER,
        CREATE_RULE_PAGE_SCOPE_LABEL:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_SCOPE_LABEL,
        CREATE_RULE_PAGE_SCOPE_REGION_DISTRICT:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_SCOPE_REGION_DISTRICT,
        CREATE_RULE_PAGE_SCOPE_STATE_PROVINCE:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_SCOPE_STATE_PROVINCE,
        CREATE_RULE_PAGE_SCOPE_DEALERSHIP:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_SCOPE_DEALERSHIP,
        CREATE_RULE_PAGE_REGION_LABEL:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_REGION_LABEL,
        CREATE_RULE_PAGE_COUNTRY_PLACEHOLDER:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_COUNTRY_PLACEHOLDER,
        CREATE_RULE_PAGE_BRAND_PLACEHOLDER:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_BRAND_PLACEHOLDER,
        CREATE_RULE_PAGE_COUNTRY_OPTION_USA:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_COUNTRY_OPTION_USA,
        CREATE_RULE_PAGE_COUNTRY_OPTION_CANADA:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_COUNTRY_OPTION_CANADA,
        CREATE_RULE_PAGE_BRAND_OPTION_VW:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_BRAND_OPTION_VW,
        CREATE_RULE_PAGE_BRAND_OPTION_VW_AND_AUDI:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_BRAND_OPTION_VW_AND_AUDI,
        CREATE_RULE_PAGE_BRAND_OPTION_ANY:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_BRAND_OPTION_ANY,
        CREATE_RULE_PAGE_BRAND_OPTION_AUDI:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_BRAND_OPTION_AUDI,
        CREATE_RULE_PAGE_BRAND_OPTION_DUCATI:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_BRAND_OPTION_DUCATI,
        CREATE_RULE_PAGE_REGION_PLACEHOLDER:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_REGION_PLACEHOLDER,
        CREATE_RULE_PAGE_DISTRICT_LABEL:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_DISTRICT_LABEL,
        CREATE_RULE_PAGE_DISTRICT_PLACEHOLDER:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_DISTRICT_PLACEHOLDER,
        CREATE_RULE_PAGE_STATE_PROVINCE_LABEL:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_STATE_PROVINCE_LABEL,
        CREATE_RULE_PAGE_STATE_PROVINCE_PLACEHOLDER:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_STATE_PROVINCE_PLACEHOLDER,
        CREATE_RULE_PAGE_STATE_PROVINCE_HELP_TEXT:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_STATE_PROVINCE_HELP_TEXT,
        CREATE_RULE_PAGE_DEALERSHIP_LABEL:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_DEALERSHIP_LABEL,
        CREATE_RULE_PAGE_DEALERSHIP_PLACEHOLDER:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_DEALERSHIP_PLACEHOLDER,
        CREATE_RULE_PAGE_RULE_TYPE_TITLE:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_RULE_TYPE_TITLE,
        CREATE_RULE_PAGE_RULE_TYPE_DESCRIPTION:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_RULE_TYPE_DESCRIPTION,
        CREATE_RULE_PAGE_REGION_HELP_TEXT:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_REGION_HELP_TEXT,
        CREATE_RULE_PAGE_DISTRICT_HELP_TEXT:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_DISTRICT_HELP_TEXT,
        CREATE_RULE_PAGE_BACK_BUTTON:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_BACK_BUTTON,
        CREATE_RULE_PAGE_SAVE_BUTTON:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_SAVE_BUTTON,
        CREATE_RULE_PAGE_CANCEL_BUTTON:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_CANCEL_BUTTON,
        CREATE_RULE_PAGE_LOAD_REGION_ERROR:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_LOAD_REGION_ERROR,
        CREATE_RULE_PAGE_LOAD_DISTRICT_ERROR:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_LOAD_DISTRICT_ERROR,
        CREATE_RULE_PAGE_SUCCESS_MESSAGE:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_SUCCESS_MESSAGE,
        CREATE_RULE_PAGE_UPDATE_SUCCESS_MESSAGE:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_UPDATE_SUCCESS_MESSAGE,
        CREATE_RULE_PAGE_LOAD_RULE_DETAILS_ERROR:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_LOAD_RULE_DETAILS_ERROR,
        CREATE_RULE_PAGE_MULTIPLE_VALUES_SELECTED:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_MULTIPLE_VALUES_SELECTED,
        CREATE_RULE_PAGE_SELECTED_COUNT_SUFFIX:
            localizedKeys.SYSTEM_ADMIN_CREATE_RULE_PAGE_SELECTED_COUNT_SUFFIX,
        ACCESS_SECURITY: localizedKeys.SYSTEM_ADMIN_ACCESS_SECURITY,
        INTERNAL_TOOLS: localizedKeys.SYSTEM_ADMIN_INTERNAL_TOOLS,
        FIELD_USER_MANAGEMENT: localizedKeys.SYSTEM_ADMIN_FIELD_USER_MANAGEMENT,
        INTERNAL_SECURITY_MAINTENANCE:
            localizedKeys.SYSTEM_ADMIN_INTERNAL_SECURITY_MAINTENANCE,
        DATE_AND_TERM_CHANGE: localizedKeys.SYSTEM_ADMIN_DATE_AND_TERM_CHANGE,
        PAGE_ACTIVATE_DEALER_TITLE:
            localizedKeys.SYSTEM_ADMIN_PAGE_ACTIVATE_DEALER_TITLE,
        PAGE_ACTIVATE_DEALER_TITLE_BUTTON:
            localizedKeys.SYSTEM_ADMIN_PAGE_ACTIVATE_DEALER_TITLE_BUTTON,
        PAGE_ACTIVATE_DEALER_TITLE_LABEL:
            localizedKeys.SYSTEM_ADMIN_PAGE_ACTIVATE_DEALER_TITLE_LABEL,
        PAGE_ACTIVATE_DEALER_DESC:
            localizedKeys.SYSTEM_ADMIN_PAGE_ACTIVATE_DEALER_DESC,
        PAGE_ACTIVATE_DEALER_MSG_SUCC:
            localizedKeys.SYSTEM_ADMIN_PAGE_ACTIVATE_DEALER_MSG_SUCC,
        PAGE_ACTIVATE_DEALER_MSG_ERR1:
            localizedKeys.SYSTEM_ADMIN_PAGE_ACTIVATE_DEALER_MSG_ERR1,
        PAGE_ACTIVATE_DEALER_MSG_ERR2:
            localizedKeys.SYSTEM_ADMIN_PAGE_ACTIVATE_DEALER_MGS_ERR2,
        PAGE_STATIC_PDF_MAINTENANCE_INFO:
            localizedKeys.SYSTEM_ADMIN_PAGE_STATIC_PDF_INFO,
        PAGE_STATIC_PDF_MAINTENANCE_INFO2:
            localizedKeys.SYSTEM_ADMIN_PAGE_STATIC_PDF_INFO2,
        SYSTEM_ADMIN_PAGE_UPLOAD_BTN:
            localizedKeys.SYSTEM_ADMIN_PAGE_UPLOAD_BTN,
        SYSTEM_ADMIN_PAGE_ADD_BTN: localizedKeys.SYSTEM_ADMIN_PAGE_ADD_BTN,
        SYSTEM_ADMIN_PAGE_DESCRIPTION_LABEL:
            localizedKeys.SYSTEM_ADMIN_PAGE_DESCRIPTION_LABEL,
        SYSTEM_ADMIN_PAGE_NO_ALLOWED:
            localizedKeys.SYSTEM_ADMIN_PAGE_NO_ALLOWED,
        SYSTEM_ADMIN_PAGE_SUCC_MSG: localizedKeys.SYSTEM_ADMIN_PAGE_SUCC_MSG,
        SYSTEM_ADMIN_PAGE_ERR_MSG: localizedKeys.SYSTEM_ADMIN_PAGE_ERR_MSG,
        SYSTEM_ADMIN_PAGE_PROCESS_LABEL:
            localizedKeys.SYSTEM_ADMIN_PAGE_PROCESS_LABEL,
        SYSTEM_ADMIN_PAGE_VIEW_BTN: localizedKeys.SYSTEM_ADMIN_PAGE_VIEW_BTN,
        SYSTEM_ADMIN_PAGE_UPLOAD_DOC_BTN:
            localizedKeys.SYSTEM_ADMIN_PAGE_UPLOAD_DOC_BTN,
        SYSTEM_ADMIN_PAGE_BRAND_LABEL:
            localizedKeys.SYSTEM_ADMIN_PAGE_BRAND_LABEL,
        SYSTEM_ADMIN_PAGE_COUNTRY_LABEL:
            localizedKeys.SYSTEM_ADMIN_PAGE_COUNTRY_LABEL,
        SYSTEM_ADMIN_PAGE_BY_LABEL: localizedKeys.SYSTEM_ADMIN_PAGE_BY_LABEL,
        SYSTEM_ADMIN_PAGE_DATE_LABEL:
            localizedKeys.SYSTEM_ADMIN_PAGE_DATE_LABEL,
        DATE_TERM_PAGE_TITLE: localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_TITLE,
        DATE_TERM_PAGE_DESCRIPTION:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_DESCRIPTION,
        DATE_TERM_PAGE_COUNTRY_LABEL:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_COUNTRY_LABEL,
        DATE_TERM_PAGE_COUNTRY_USA:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_COUNTRY_USA,
        DATE_TERM_PAGE_COUNTRY_CANADA:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_COUNTRY_CANADA,
        DATE_TERM_PAGE_CURRENTLY_SET_TEXT:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_CURRENTLY_SET_TEXT,
        DATE_TERM_PAGE_DATE_LABEL:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_DATE_LABEL,
        DATE_TERM_PAGE_CANCEL_BUTTON:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_CANCEL_BUTTON,
        DATE_TERM_PAGE_SUBMIT_BUTTON:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_SUBMIT_BUTTON,
        DATE_TERM_PAGE_REQUIRED_ERROR:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_REQUIRED_ERROR,
        DATE_TERM_PAGE_INVALID_DATE_ERROR:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_INVALID_DATE_ERROR,
        DATE_TERM_PAGE_SUCCESS_MESSAGE:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_SUCCESS_MESSAGE,
        DATE_TERM_PAGE_SYSTEM_ERROR_MESSAGE:
            localizedKeys.SYSTEM_ADMIN_DATE_TERM_PAGE_SYSTEM_ERROR_MESSAGE,
        DIST_RULE_PAGE_TITLE: localizedKeys.SYSTEM_ADMIN_DIST_RULE_PAGE_TITLE,
        DIST_RULE_PAGE_DESCRIPTION:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_PAGE_DESCRIPTION,
        DIST_RULE_SEARCH_PLACEHOLDER:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_SEARCH_PLACEHOLDER,
        DIST_RULE_SEARCH_BTN: localizedKeys.SYSTEM_ADMIN_DIST_RULE_SEARCH_BTN,
        DIST_RULE_CREATE_BTN: localizedKeys.SYSTEM_ADMIN_DIST_RULE_CREATE_BTN,
        DIST_RULE_CLOSE_BTN: localizedKeys.SYSTEM_ADMIN_DIST_RULE_CLOSE_BTN,
        DIST_RULE_COL_RULE_NAME:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_COL_RULE_NAME,
        DIST_RULE_COL_CREATED_BY:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_COL_CREATED_BY,
        DIST_RULE_COL_CREATED_DATE:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_COL_CREATED_DATE,
        DIST_RULE_COL_MODIFIED_DATE:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_COL_MODIFIED_DATE,
        DIST_RULE_MENU_VIEW_EDIT:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_MENU_VIEW_EDIT,
        DIST_RULE_MENU_DELETE: localizedKeys.SYSTEM_ADMIN_DIST_RULE_MENU_DELETE,
        DIST_RULE_DELETE_MODAL_TITLE:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_DELETE_MODAL_TITLE,
        DIST_RULE_DELETE_MODAL_BODY:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_DELETE_MODAL_BODY,
        DIST_RULE_DELETE_MODAL_CANCEL:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_DELETE_MODAL_CANCEL,
        DIST_RULE_DELETE_MODAL_CONFIRM:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_DELETE_MODAL_CONFIRM,
        DIST_RULE_DELETE_SUCCESS:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_DELETE_SUCCESS,
        DIST_RULE_DELETE_IN_USE:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_DELETE_IN_USE,
        DIST_RULE_DELETE_ERROR:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_DELETE_ERROR,
        DIST_RULE_NO_RESULTS: localizedKeys.SYSTEM_ADMIN_DIST_RULE_NO_RESULTS,
        DIST_RULE_ERROR: localizedKeys.SYSTEM_ADMIN_DIST_RULE_ERROR,
        DIST_RULE_SELECT_MODE_BANNER:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_SELECT_MODE_BANNER,
        DIST_RULE_ADD_SELECTED_RULE_BTN:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_ADD_SELECTED_RULE_BTN,
        DIST_RULE_SELECT_CANCEL_BTN:
            localizedKeys.SYSTEM_ADMIN_DIST_RULE_SELECT_CANCEL_BTN,
        BULLETINS_SECTION: localizedKeys.SYSTEM_ADMIN_BULLETINS_SECTION,
        BULLETINS_UPLOAD_LINK: localizedKeys.SYSTEM_ADMIN_BULLETINS_UPLOAD_LINK,
        BULLETINS_VIEW_LINK: localizedKeys.SYSTEM_ADMIN_BULLETINS_VIEW_LINK,
        COU_SECTION: localizedKeys.SYSTEM_ADMIN_COU_SECTION,
        COU_CREATE_LINK: localizedKeys.SYSTEM_ADMIN_COU_CREATE_LINK,
        COU_VIEW_LINK: localizedKeys.SYSTEM_ADMIN_COU_VIEW_LINK,
        VEHICLE_MANAGER_SECTION:
            localizedKeys.SYSTEM_ADMIN_VEHICLE_MANAGER_SECTION,
        VEHICLE_UPLOAD_LINK: localizedKeys.SYSTEM_ADMIN_VEHICLE_UPLOAD_LINK,
        VEHICLE_SEARCH_LINK: localizedKeys.SYSTEM_ADMIN_VEHICLE_SEARCH_LINK,
    };

    const appUpdateNotification = {
        LABEL_FIELD_APPNOTIFICATION_TITLE:
            localizedKeys.LABEL_FIELD_APPNOTIFICATION_TITLE,
        LABEL_FIELD_APPNOTIFICATION_TEXT:
            localizedKeys.LABEL_FIELD_APPNOTIFICATION_TEXT,
        LABEL_FIELD_APPNOTIFICATION_RELOAD:
            localizedKeys.LABEL_FIELD_APPNOTIFICATION_RELOAD,
        LABEL_FIELD_APPNOTIFICATION_AVOID:
            localizedKeys.LABEL_FIELD_APPNOTIFICATION_AVOID,
    };

    const quickFund = {
        TITLE: localizedKeys.QUICKFUND_TITLE,
        DETAILS_TITLE: localizedKeys.QUICKFUND_DETAILS_TITLE,
        NOTE1: localizedKeys.QUICKFUND_NOTE1,
        NOTE2: localizedKeys.QUICKFUND_NOTE2,
        NOTE3: localizedKeys.QUICKFUND_NOTE3,
        APPLICATION_INFORMATION: {
            TITLE: localizedKeys.QUICKFUND_APP_INFO_TITLE,
            PRIMARY_APPLICANT:
                localizedKeys.QUICKFUND_APP_INFO_PRIMARY_APPLICANT,
            VEHICLE: localizedKeys.QUICKFUND_APP_INFO_VEHICLE,
            AMOUNT_TO_FINANCE:
                localizedKeys.QUICKFUND_APP_INFO_AMOUNT_TO_FINANCE,
            CONTRACT_TYPE: localizedKeys.QUICKFUND_APP_INFO_CONTRACT_TYPE,
            ADJUSTED_CAP_COST:
                localizedKeys.QUICKFUND_APP_INFO_ADJUSTED_CAP_COST,
            BALLOON_CONTRACT: localizedKeys.QUICKFUND_APP_INFO_BALLOON_CONTRACT,
        },
        MESSAGES: {
            LEARN: localizedKeys.QUICKFUND_MESSAGES_LEARN,
            DUE: localizedKeys.QUICKFUND_MESSAGES_DUE,
            ELIGIBLE_MESSAGE: localizedKeys.QUICKFUND_MESSAGES_ELIGIBLE_MESSAGE,
            OPTION: localizedKeys.QUICKFUND_MESSAGES_OPTION,
            TRY_AGAIN_LATER: localizedKeys.QUICKFUND_MESSAGES_TRY_AGAIN_LATER,
            NOT_ELIGIBLE_SEND_TO_VCI:
                localizedKeys.QUICKFUND_MESSAGES_NOT_ELIGIBLE_SEND_TO_VCI,
            DOES_NOT_QUALIFY: localizedKeys.QUICKFUND_MESSAGES_DOES_NOT_QUALIFY,
            ALREADY_QUICK_FUNDED:
                localizedKeys.QUICKFUND_MESSAGES_ALREADY_QUICK_FUNDED,
            ELIGIBLE_WITH_CONTRACT:
                localizedKeys.QUICKFUND_MESSAGES_ELIGIBLE_WITH_CONTRACT,
            NOT_ELIGIBLE_DUE_TO_MISSING_OR_FAILED_CONTRACT:
                localizedKeys.QUICKFUND_MESSAGES_NOT_ELIGIBLE_DUE_TO_MISSING_OR_FAILED_CONTRACT,
            VIEW_ONLY_PERMISSION:
                localizedKeys.QUICKFUND_MESSAGES_VIEW_ONLY_PERMISSION,
        },
        INFORMATION: {
            TITLE: localizedKeys.QUICKFUND_INFO_TITLE,
            VIN: localizedKeys.QUICKFUND_INFO_VIN,
            VIN_ERROR: localizedKeys.QUICKFUND_INFO_VIN_ERROR,
            CONTRACT: localizedKeys.QUICKFUND_INFO_CONTRACT,
            ADJUSTED_CAP_COST_ERROR:
                localizedKeys.QUICKFUND_INFO_ADJUSTED_CAP_COST_ERROR,
            SECURITY_DEPOSIT: localizedKeys.QUICKFUND_INFO_SECURITY_DEPOSIT,
            MONTHLY_PAYMENT: localizedKeys.QUICKFUND_INFO_MONTHLY_PAYMENT,
            ACQUISITION_FEE: localizedKeys.QUICKFUND_INFO_ACQUISITION_FEE,
            QUICKFUND_ADVANCE_AMOUNT:
                localizedKeys.QUICKFUND_INFO_QUICKFUND_ADVANCE_AMOUNT,
            QUICKFUND_ADVANCE_AMOUNT_TOOLTIP:
                localizedKeys.QUICKFUND_INFO_QUICKFUND_ADVANCE_AMOUNT_TOOLTIP,
            QUICKFUND_ADVANCE_AMOUNT_BALLOON_TOOLTIP:
                localizedKeys.QUICKFUND_INFO_QUICKFUND_ADVANCE_AMOUNT_BALLOON_TOOLTIP,
            REQUIRED: localizedKeys.QUICKFUND_INFO_REQUIRED,
            NOTE: localizedKeys.QUICKFUND_INFO_NOTE,
            SUBMIT_BTN: localizedKeys.QUICKFUND_INFO_SUBMIT_BTN,
            CANCEL_BTN: localizedKeys.QUICKFUND_INFO_CANCEL_BTN,
            AGREEMENT: localizedKeys.QUICKFUND_INFO_AGREEMENT,
            OTHER_AMOUNT: localizedKeys.QUICKFUND_INFO_OTHER_AMOUNT,
            OTHER_AMOUNT_ERROR: localizedKeys.QUICKFUND_INFO_OTHER_AMOUNT_ERROR,
            SUCCESS_NOTIFICATION:
                localizedKeys.QUICKFUND_INFO_SUCCESS_NOTIFICATION,
            ERROR_NOTIFICATION: localizedKeys.QUICKFUND_INFO_ERROR_NOTIFICATION,
            SECURITY_DEPOSIT_ERROR:
                localizedKeys.QUICKFUND_INFO_SECURITY_DEPOSIT_ERROR,
            MONTHLY_PAYMENT_ERROR:
                localizedKeys.QUICKFUND_INFO_MONTHLY_PAYMENT_ERROR,
            ACQUISITION_FEE_ERROR:
                localizedKeys.QUICKFUND_INFO_ACQUISITION_FEE_ERROR,
            AMOUNT_FINANCED: localizedKeys.QUICKFUND_INFO_AMOUNT_FINANCED,
            AMOUNT_FINANCED_TOOLTIP:
                localizedKeys.QUICKFUND_INFO_AMOUNT_FINANCED_TOOLTIP,
            ADVANCE_AMOUNT_ERROR:
                localizedKeys.QUICKFUND_INFO_ADVANCE_AMOUNT_ERROR,
        },
        STATUS: {
            TITLE: localizedKeys.QUICKFUND_STATUS_TITLE,
            SUBMITTED: localizedKeys.QUICKFUND_STATUS_SUBMITTED,
            SUBMITTED_BY: localizedKeys.QUICKFUND_STATUS_SUBMITTED_BY,
            STATUS_SUBMITTED_ON: localizedKeys.QUICKFUND_STATUS_SUBMITTED_ON,
            BULLETIN_NUMBER: localizedKeys.QUICKFUND_STATUS_BULLETIN_NUMBER,
            CONTRACT_VALIDATION_NUMBER:
                localizedKeys.QUICKFUND_STATUS_CONTRACT_VALIDATION_NUMBER,
        },
    };

    const criticalOrganizationUpdates = {
        CLOSED: localizedKeys.CRITICAL_ORGANIZATION_UPDATE_CLOSE,
        READMORE: localizedKeys.CRITICAL_ORGANIZATION_UPDATE_READ_MORE,
    };

    return {
        localizedKeys,
        fundingStatus,
        fundingStatusDetails,
        statusFilterOptionToResponseMap,
        statusFilterResponseToOptionMap,
        statusFilterOptionMap,
        contactInfo,
        departmentInfo,
        labelText,
        labelTitle,
        labelField,
        statusSummary,
        bulletins,
        handbook,
        modal,
        data,
        eft,
        errorMessages,
        labelsCommon,
        serviceLoanerInventory,
        dealerRentalInventory,
        inventoryTable,
        dealerMgmt,
        registrationSuccess,
        accountRegistration,
        detailsView,
        commonLabels,
        addNewUser,
        ismAddNewUser,
        ismUpdateUser,
        editUser,
        deleteUser,
        vehicleInformation,
        applicantInfo,
        creditApp,
        creditAppOtherInformation,
        creditAppPrompt,
        creditAppPrintInstructions,
        occupationIndividualInformation,
        passwordReset,
        forgotPassword,
        financeSection,
        businessComercial,
        creditSearch,
        creditStatusFilterOptionMap,
        creditStatusFilterOptionToResponseMap,
        creditStatusFilterResponseToOptionMap,
        creditStatusDetails,
        portfolioManagerDetails,
        vehicleTypeFilterResponseToOptionMap,
        payments,
        paymentHistory,
        paymentTypeFilterEnumToUi,
        paymentTypeFilterUiToEnum,
        paymentDateFilterEnumToUi,
        paymentDateFilterUiToEnum,
        paymentAccountEnumToUi,
        paymentStatusEnumToUi,
        paymentStatusUiToEnum,
        paymentReasonEnumToUi,
        paymentBankAccountTypeToUi,
        payoffQuotes,
        payoffQuotesDetail,
        payoffQuotesDetailInfo,
        payoffQuotesQuoteDetails,
        payoffQuotesNote,
        payoffQuotesAccount,
        payoffQuotesDetailUS,
        payoffQuoteInformation,
        payoffPaymentInformation,
        assignSecurityDepositToUi,
        purchaseTypeToUi,
        warningMessages,
        portfolioManager,
        reserves,
        documents,
        systemAdmin,
        appUpdateNotification,
        quickFund,
        criticalOrganizationUpdates,
    };
};
